import React from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import "../styles/process-optimization.css";
import "../styles/raci-matrix.css";
import FormModal from "../components/ui/FormModal";
import useOptimizationData from "../hooks/useOptimizationData";
import useRlOptimizationData from "../hooks/useRlOptimizationData";
import useBprRedesignData from "../hooks/useBprRedesignData";
import useOptimizedVersionSave from "../hooks/useOptimizedVersionSave";
import useSaveEngineSelection from "../hooks/useSaveEngineSelection";
import { EngineSelector, ProvenanceNotices, ConfirmSaveBody, AdoptList, EnginePotentialBand, KpiExclusionNote } from "../components/ui/SaveEnginePanel";
import SaveHeuristicsChecklist from "../components/ui/SaveHeuristicsChecklist";
import { readEngineNativeKpis, hasNativeMovement } from "../utils/engineNativeKpis";
import {
  getScenarioSectionHeading,
  getScenarioTypeLabel,
} from "../utils/processOptimizationHelpers";
import { getValueAnalysis } from "../services/processOptimizationService";
import {
  applyAiClassifications,
  saveValueClassifications,
  getValueAnalysis as getStoredValueAnalysis,
} from "../services/valueAnalysisService";
import { getSkillCheck } from "../services/skillCheckService";
import CurrencySelector from "../components/ui/CurrencySelector";
import useCurrencyRates from "../hooks/useCurrencyRates";
import { convertCurrency, formatMoney } from "../services/currencyService";
import { analyzeProcess, restructureProcess, getProcessBpmnXml, getRestructuredBpmnXml } from "../services/bpmnService";
import { whatIfFlowAnalysis } from "../services/flowAnalysisService";
import BpmnInteractiveViewer from "../components/ui/BpmnInteractiveViewer";
import BpmnPathAnimator from "../components/ui/BpmnPathAnimator";
import RlRedesignPanel, { GOAL_OPTIONS as RL_GOAL_OPTIONS } from "../components/ui/RlRedesignPanel";
import BprRedesignPanel from "../components/ui/BprRedesignPanel";
import { extractBprBpmnXml, getBprBpmnSource } from "../services/bprRedesignService";
import ComparisonReportPanel from "../components/ui/ComparisonReportPanel";
import useComparisonReportData from "../hooks/useComparisonReportData";
import DevilsQuadrangleChart from "../components/ui/DevilsQuadrangleChart";
import BeforeAfterEnginePanel from "../components/ui/BeforeAfterEnginePanel";
import SubprocessChildResults from "../components/ui/SubprocessChildResults";
import { ChildChip } from "../components/ui/SubprocessProjectionBits";
import { DEFAULT_INCLUDE_SUBPROCESS } from "../utils/subprocessRedesign";
import ProcessAdvisoryModal from "../components/ui/ProcessAdvisoryModal";
import RaciJobEditModal from "../components/ui/RaciJobEditModal";
import {
  ROLE_LABELS,
  overrideKey,
  buildCandidateList,
  agentOverrides,
  mergeOverrides,
  manualResourceChanges,
  improvementsFromDimensions,
  toResourceOverrides,
  toJobReassignments,
  applyOverridesToTaskGroups,
  applyOverridesToWorkloadJobs,
} from "../utils/optimizationRaci";
import { getJobsByCompanyId, getJobs } from "../services/jobService";

const CATEGORY_COLOR = {
  VA:  { bg: "#dcfce7", color: "#15803d", border: "#86efac" },
  BVA: { bg: "#dbeafe", color: "#1d4ed8", border: "#93c5fd" },
  NVA: { bg: "#fee2e2", color: "#b91c1c", border: "#fca5a5" },
};
const getCategoryColor = (cat) => CATEGORY_COLOR[cat] ?? CATEGORY_COLOR.NVA;

// Staged progress for the Value-Added analysis wait. The AI classification takes
// tens of seconds; a bare spinner reads as "hung". The stages describe what the
// pipeline actually does, and the bar advances on a schedule (capped at 92% —
// only the real response completes it, so it never lies about being done).
const VA_STAGES = [
  { at: 0, pct: 8, label: "Fetching the process's tasks and descriptions…" },
  { at: 2500, pct: 30, label: "AI is decomposing each task into steps…" },
  { at: 9000, pct: 55, label: "Classifying every step against the FBPM Ch.6 tests (VA / BVA / NVA)…" },
  { at: 22000, pct: 74, label: "Validating rule citations and recomputing percentages…" },
  { at: 38000, pct: 86, label: "Joining assigned jobs and preparing the table…" },
  { at: 60000, pct: 92, label: "Still working — large processes can take up to ~2 minutes…" },
];
function VaProgress() {
  const [stageIdx, setStageIdx] = React.useState(0);
  React.useEffect(() => {
    const timers = VA_STAGES.map((s, i) =>
      i === 0 ? null : setTimeout(() => setStageIdx(i), s.at),
    ).filter(Boolean);
    return () => timers.forEach(clearTimeout);
  }, []);
  const stage = VA_STAGES[stageIdx];
  return (
    <div className="role-card">
      <div
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={stage.pct}
        aria-valuetext={stage.label}
        style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "48px 24px", gap: 14 }}
      >
        <div style={{ fontSize: 15, fontWeight: 700, color: "#374151" }}>Running Value-Added Analysis</div>
        <div style={{ width: "100%", maxWidth: 460, height: 10, background: "#e5e7eb", borderRadius: 999, overflow: "hidden" }}>
          <div style={{ width: `${stage.pct}%`, height: "100%", background: "linear-gradient(90deg, #0e6b68, #0d9488)", borderRadius: 999, transition: "width 1.2s ease" }} />
        </div>
        <div style={{ fontSize: 13, color: "#4b5563", textAlign: "center" }} aria-live="polite">{stage.label}</div>
        <div style={{ fontSize: 11.5, color: "#9ca3af", textAlign: "center" }}>
          Classification is AI-assisted and advisory — nothing is saved until you press Save, and your own edits always win.
        </div>
      </div>
    </div>
  );
}

// Stages drawn from the restructure endpoint's own documented pipeline (backend
// bpmn-native.controller.ts `analyze()`: build TO-BE -> LLM Ch.3-5 advisory review
// -> auto-apply fixable findings -> validate soundness) — not invented labels.
// The backend allows up to 220s before timing out (process-optimization.service.ts),
// so the schedule spreads across that window and caps at 92% — only the real
// response completes it.
const ANALYZE_STAGES = [
  { at: 0, pct: 10, label: "Fetching the process's current structure…" },
  { at: 4000, pct: 28, label: "Rebuilding the TO-BE candidate — including any sub-processes…" },
  { at: 20000, pct: 50, label: "Running the FBPM Ch.3–5 structural advisory review (AI-assisted)…" },
  { at: 60000, pct: 68, label: "Applying auto-fixable structural corrections…" },
  { at: 100000, pct: 80, label: "Validating BPMN soundness…" },
  { at: 150000, pct: 92, label: "Still working — processes with several sub-processes can take a few minutes…" },
];
function AnalyzeProgress() {
  const [stageIdx, setStageIdx] = React.useState(0);
  React.useEffect(() => {
    const timers = ANALYZE_STAGES.map((s, i) =>
      i === 0 ? null : setTimeout(() => setStageIdx(i), s.at),
    ).filter(Boolean);
    return () => timers.forEach(clearTimeout);
  }, []);
  const stage = ANALYZE_STAGES[stageIdx];
  return (
    <div
      className="pa-advisory-placeholder"
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={stage.pct}
      aria-valuetext={stage.label}
      style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "48px 24px", gap: 14 }}
    >
      <div style={{ fontSize: 15, fontWeight: 700, color: "#374151" }}>Analyzing BPMN Process</div>
      <div style={{ width: "100%", maxWidth: 460, height: 10, background: "#e5e7eb", borderRadius: 999, overflow: "hidden" }}>
        <div style={{ width: `${stage.pct}%`, height: "100%", background: "linear-gradient(90deg, #0e6b68, #0d9488)", borderRadius: 999, transition: "width 1.2s ease" }} />
      </div>
      <div className="pa-advisory-desc" style={{ textAlign: "center" }} aria-live="polite">{stage.label}</div>
      <div style={{ fontSize: 11.5, color: "#9ca3af", textAlign: "center" }}>
        The structural review is AI-assisted and advisory — nothing changes until you apply it.
      </div>
    </div>
  );
}

// Stacked time composition (validated triple): processing / waiting / rework.
const TIME_STACK = { processing: "#0d9488", waiting: "#d97706", rework: "#9a3412" };

// Optimization-goal presets (pipeline step 6): Devil's-Quadrangle weights for
// scenario ranking. The agent receives these with the optimize request.
const GOAL_PRESETS = {
  balanced: { label: "Balanced (time + cost)", weights: { time: 1, cost: 1, quality: 0.5, flexibility: 0.5 } },
  time: { label: "Time-first", weights: { time: 2, cost: 1, quality: 0.5, flexibility: 0.5 } },
  cost: { label: "Cost-first", weights: { time: 1, cost: 2, quality: 0.5, flexibility: 0.5 } },
  quality: { label: "Quality-first", weights: { time: 1, cost: 1, quality: 2, flexibility: 1 } },
  flexibility: { label: "Flexibility-first", weights: { time: 1, cost: 1, quality: 1, flexibility: 2 } },
};

/** Longest common leading run of steps shared by every route — the identical
 *  prefix each path repeats. Compared on stringified steps so parallel blocks match. */
function sharedPrefixLength(routes) {
  const lists = (routes || []).map((r) => (Array.isArray(r.route) ? r.route : (Array.isArray(r) ? r : [])));
  if (lists.length < 2) return 0;
  const minLen = Math.min(...lists.map((l) => l.length));
  let n = 0;
  for (; n < minLen; n++) {
    const idx = n;
    const first = String(lists[0][idx]);
    if (!lists.every((l) => String(l[idx]) === first)) break;
  }
  return n;
}

/** One execution route in the Path View's visual language: start/end event circles,
 *  task chips, parallel blocks bracketed with a ∥ badge, arrows between. Renders on a
 *  single horizontally-scrollable line (no wrapping). Steps before `dimCount` are the
 *  prefix shared by every route, drawn muted so the divergent tail stands out. */
function PathFlowStrip({ route, dimCount = 0 }) {
  const arrow = (muted) => <span style={{ color: muted ? "#cbd5e1" : "#94a3b8", fontWeight: 800, fontSize: 12, flexShrink: 0 }}>→</span>;
  const circle = (kind) => (
    <span style={{
      width: 22, height: 22, borderRadius: "50%", flexShrink: 0, background: "#fff",
      border: `2.5px solid ${kind === "start" ? "#16a34a" : "#475569"}`,
      color: kind === "start" ? "#16a34a" : "#475569",
      display: "inline-flex", alignItems: "center", justifyContent: "center",
      fontSize: 9, fontWeight: 900,
    }}>{kind === "start" ? "▶" : "■"}</span>
  );
  const chip = (name, key, muted) => (
    <span key={key} style={{
      background: muted ? "#f8fafc" : "#fff",
      border: `1.5px solid ${muted ? "#e5e7eb" : "#cbd5e1"}`,
      borderRadius: 8, padding: "3px 9px", flexShrink: 0,
      fontSize: 11, fontWeight: 600, color: muted ? "#94a3b8" : "#0f172a", whiteSpace: "nowrap",
      boxShadow: muted ? "none" : "0 1px 3px rgba(15,23,42,0.08)",
    }}>{name}</span>
  );
  const steps = Array.isArray(route) ? route : [];
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "nowrap", overflowX: "auto", padding: "4px 0" }}>
      {circle("start")}
      {steps.length === 0 && (
        <>
          {arrow(false)}
          <span style={{ fontSize: 11, color: "#9ca3af", fontStyle: "italic", flexShrink: 0 }}>no task on this branch</span>
        </>
      )}
      {steps.map((s, i) => {
        const muted = i < dimCount;
        const str = String(s);
        if (str.startsWith("[") && str.endsWith("]")) {
          const inner = str.slice(1, -1).trim().split(" ∥ ");
          return (
            <React.Fragment key={i}>
              {arrow(muted)}
              <span style={{ display: "inline-flex", alignItems: "center", gap: 5, flexShrink: 0, border: `1.5px dashed ${muted ? "#ddd6fe" : "#7c3aed"}`, borderRadius: 10, padding: "3px 7px", background: muted ? "#faf9ff" : "#f5f3ff" }}>
                <span title="Parallel block — branches run concurrently; wall-clock follows the longer one" style={{ color: muted ? "#c4b5fd" : "#7c3aed", fontWeight: 900, fontSize: 11 }}>∥</span>
                <span style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                  {inner.map((b, k) => (
                    <span key={k} style={{ display: "flex", alignItems: "center", gap: 4 }}>
                      {b.split(" → ").map((t, j, arr2) => (
                        <React.Fragment key={j}>
                          {chip(t, `${k}-${j}`, muted)}
                          {j < arr2.length - 1 && arrow(muted)}
                        </React.Fragment>
                      ))}
                    </span>
                  ))}
                </span>
              </span>
            </React.Fragment>
          );
        }
        return (
          <React.Fragment key={i}>
            {arrow(muted)}
            {chip(str, i, muted)}
          </React.Fragment>
        );
      })}
      {arrow(false)}
      {circle("end")}
    </div>
  );
}

const ProcessOptimizationPage = () => {
  // The component is mounted via section-based routing (Dashboard switch/case),
  // not as a React Router <Route>, so useParams() returns {}. We read the
  // process ID from the URL pathname instead.
  const { processId: paramId } = useParams();
  const location = useLocation();
  const processId = React.useMemo(() => {
    if (paramId) return paramId;
    const parts = location.pathname.split("/");
    const last = parts[parts.length - 1];
    return last && last !== "results" ? last : null;
  }, [paramId, location.pathname]);
  const navigate = useNavigate();
  const {
    selectedId,
    optimizationData,
    processMeta,
    optimizing,
    showMinTasksModal,
    setShowMinTasksModal,
    bpmnImages,
    refreshOptimizedImage,
    bpmnLoading,
    bpmnZoom,
    setBpmnZoom,
    bpmnOffset,
    setBpmnOffset,
    bpmnDragRef,
    errorModal,
    setErrorModal,
    resetSelection,
    optimize,
    setOptimizationData,
  } = useOptimizationData();

  // RL redesign (separate pipeline, /cms/rl-optimize) — lifted to page level
  // rather than owned by the Redesign tab's RL panel, so the last run's result
  // (including its final_bpmn_xml) survives switching to the BPMN tab's "RL
  // BPMN" view rather than being lost when the panel unmounts.
  const rl = useRlOptimizationData();

  // BPR redesign (third pipeline, /cms/bpr-redesign — an external, already-
  // trained ML model) — lifted to page level for the same reason as `rl`
  // above: the last run's redesigned diagram must survive switching to the
  // BPMN tab's "BPR BPMN" view rather than being lost when the panel unmounts.
  const bpr = useBprRedesignData();

  // Comparison Report (/cms/comparison-report) — a read-only summary across the
  // three pipelines above against one shared baseline. Lifted to page level for
  // the same reason as rl/bpr: the last report survives switching tabs.
  const comparisonReport = useComparisonReportData();

  // Advisory task/job skill-match check (never blocks optimization).
  const [skillCheck, setSkillCheck] = React.useState(null);

  // Optimization goal (pipeline step 6) — Devil's-Quadrangle weights for scenario ranking.
  const [goalKey, setGoalKey] = React.useState("balanced");
  // Classifications changed after the last run: downstream numbers are stale until re-run.
  const [vaStale, setVaStale] = React.useState(false);
  // Distinguish a scenario switch (state-local swap) from a fresh optimize run.
  const scenarioSwitchRef = React.useRef(false);

  // Switch the ACTIVE scenario: swapping redesign_flow + redesign_report keeps every
  // tab consistent, because all views derive from these two objects. The catalog
  // review (process-level, scenario-independent) is carried over.
  const selectScenario = React.useCallback((sc) => {
    scenarioSwitchRef.current = true;
    setQfOverrides({});
    setOptimizationData((prev) => prev && ({
      ...prev,
      redesign_flow: sc.redesign_flow,
      redesign_report: { ...sc.redesign_report, catalog_review: prev.redesign_report?.catalog_review },
      scenarios: (prev.scenarios || []).map((s) => ({ ...s, is_active: s.id === sc.id })),
    }));
  }, [setOptimizationData]);

  // User overrides of the AI quality/flexibility scores in the Before/After tab.
  const [qfOverrides, setQfOverrides] = React.useState({});
  const setQfOverride = React.useCallback(
    (key, val) => setQfOverrides((prev) => ({ ...prev, [key]: val === "" ? undefined : val })),
    [],
  );

  // Recommended heuristics the analyst adopted (keyed by code). Adopted heuristics are
  // persisted into the TO-BE redesign scenario on Save, alongside the agent-applied ones.
  const [adoptedHeuristics, setAdoptedHeuristics] = React.useState({});
  const toggleAdoptHeuristic = React.useCallback((h) => {
    setAdoptedHeuristics((prev) => {
      const next = { ...prev };
      if (next[h.code]) delete next[h.code];
      else next[h.code] = h;
      return next;
    });
  }, []);

  // The ACCEPTED job reassignments (Analyze tab selection) at save time — a ref
  // because the selection state lives further down the hook chain; it is kept
  // current by an effect next to that state. Save persists exactly this set.
  const acceptedReplacementsRef = React.useRef([]);

  // Which engines compose the saved version, and the payload that selection
  // produces. Nothing is fetched until RL or BPR is actually selected, so an
  // agent-only save takes exactly the path it always has.
  const engineSelection = useSaveEngineSelection({ selectedId, rl, bpr });

  // Each selected engine's own end-to-end claim, for the reference band under the
  // Save tab's KPI tiles. Only engines actually selected appear: the band describes
  // what the chosen composition could be worth, not every engine that happens to
  // have been run. An engine reporting no movement is dropped rather than shown as
  // a flat row, which would read as a failed run instead of "nothing to add here".
  const engineNativeKpis = React.useMemo(() => {
    const out = [];
    if (engineSelection.selected.rl) out.push(readEngineNativeKpis(rl.result, "rl"));
    if (engineSelection.selected.bpr) out.push(readEngineNativeKpis(bpr.result, "bpr"));
    return out.filter(hasNativeMovement);
  }, [engineSelection.selected.rl, engineSelection.selected.bpr, rl.result, bpr.result]);

  // What the save will actually record as adopted: the analyst's manual adoptions
  // (Redesign tab, and the Suggested rows further down the Save tab) plus every
  // ticked checklist row the workflow has no field for. The checklist ticks are
  // derived from the checkboxes rather than mirrored into `adoptedHeuristics`,
  // which is what keeps a row from reading "ticked" on screen while nothing
  // recorded it — the engines' defaults arrive pre-ticked with no click involved.
  // Manual adoptions win the merge: those were made against a specific row.
  const effectiveAdoptedHeuristics = React.useMemo(
    () => ({ ...engineSelection.adoptedFromChecklist, ...adoptedHeuristics }),
    [engineSelection.adoptedFromChecklist, adoptedHeuristics],
  );

  const {
    saving, saveModal, setSaveModal, savedVersion,
    // Both Save buttons go through requestSave, never saveOptimizedVersion directly —
    // that is what guarantees an engine-composed save cannot skip the confirm step.
    confirmOpen, setConfirmOpen, requestSave, confirmSave,
  } = useOptimizedVersionSave({
      selectedId,
      setErrorModal,
      redesignReport: optimizationData?.redesign_report,
      redesignFlow: optimizationData?.redesign_flow,
      qfOverrides,
      adoptedHeuristics: effectiveAdoptedHeuristics,
      acceptedReplacementsRef,
      enginePayload: engineSelection.payload,
    });

  const [activeBpmnTab, setActiveBpmnTab] = React.useState("optimized");
  // Interactive BPMN viewer (bpmn-js — the CMS editor's engine, read-only here):
  // per-view mode toggle + lazily fetched XML per view, so activities, gateways,
  // events and every arrow/link can be inspected up close instead of a static PNG.
  const [bpmnViewMode, setBpmnViewMode] = React.useState("interactive"); // "image" | "interactive"
  const [bpmnXml, setBpmnXml] = React.useState({ current: null, optimized: null, advisory: null });
  const [bpmnXmlLoading, setBpmnXmlLoading] = React.useState(false);
  const [bpmnXmlError, setBpmnXmlError] = React.useState("");
  // The mounted bpmn-js viewer instance for the Current/Optimized interactive
  // diagram, handed up once its xml is on screen — lets the token-walkthrough
  // animator mark elements directly without owning a second viewer.
  const [interactiveViewer, setInteractiveViewer] = React.useState(null);
  const bpmnXmlSeqRef = React.useRef(0);

  const loadBpmnXml = React.useCallback(async (view, id) => {
    if (!id) return;
    const seq = ++bpmnXmlSeqRef.current;
    setBpmnXmlLoading(true);
    setBpmnXmlError("");
    const fetchOnce = () => {
      if (view === "current") return getProcessBpmnXml(id);
      // Pin to the ACTIVE scenario (see activeBpmnSpecRef below) — the same spec
      // the PNG view and the Analyzed diagram use, so all three "Optimized"
      // surfaces (static image, interactive XML, Analyzed) always agree.
      if (view === "optimized") return getProcessBpmnXml(id, { optimize: true, scenarioSpec: activeBpmnSpecRef.current, jobOverrides: activeJobOverridesRef.current });
      return getRestructuredBpmnXml(id);
    };
    try {
      let xml = null;
      try {
        xml = await fetchOnce();
      } catch (first) {
        // The very first diagram generation on a cold server can fail once and
        // succeed immediately after (verified: the warm call returns identical
        // XML). Users were "fixing" it by toggling the view — which simply
        // refetched. Do that retry automatically instead.
        if (seq !== bpmnXmlSeqRef.current) return;
        await new Promise((r) => setTimeout(r, 1500));
        if (seq !== bpmnXmlSeqRef.current) return;
        xml = await fetchOnce();
      }
      if (seq !== bpmnXmlSeqRef.current) return;
      setBpmnXml((prev) => ({ ...prev, [view]: xml }));
    } catch (e) {
      if (seq !== bpmnXmlSeqRef.current) return;
      setBpmnXmlError(
        view === "advisory"
          ? "The analyzed diagram isn't ready yet — run “Analyze Process” first, then try again."
          : (e?.message || "The diagram XML could not be loaded."),
      );
    } finally {
      if (seq === bpmnXmlSeqRef.current) setBpmnXmlLoading(false);
    }
  }, []);

  const [activeTab, setActiveTab] = React.useState("overview");
  // Redesign tab: "agent" = existing deterministic /cms/optimize heuristics
  // matrix below; "rl" = the RL agent's own redesign (separate pipeline, see
  // RL_OPTIMIZATION_FRONTEND_API.md); "bpr" = the external, already-trained
  // BPR Redesign ML model (separate pipeline, see FRONTEND_GUIDE.md). Kept as
  // a view toggle rather than shown side-by-side, so the already-long
  // Redesign tab doesn't triple in length.
  const [redesignMode, setRedesignMode] = React.useState("agent");

  // Engine picker for the AS-IS vs TO-BE tab (separate from redesignMode above —
  // that one switches the Redesign tab's own panels; this one only affects which
  // engine's KPIs/Devil's Quadrangle render on the before/after tab). "agent" keeps
  // today's behaviour byte-identical; "rl"/"bpr" are new.
  // Greedy's own sub-process opt-in, mirroring the RL/BPR panels'.
  //
  // Read from the shared DEFAULT_INCLUDE_SUBPROCESS constant rather than a literal so
  // the automatic first run below uses the same value the checkbox renders. Those two
  // are the thing that must not drift: a box shown ticked above results produced
  // without the flag would be describing a run that never happened.
  const [agentIncludeSubprocess, setAgentIncludeSubprocess] = React.useState(DEFAULT_INCLUDE_SUBPROCESS);

  const [beforeAfterEngine, setBeforeAfterEngine] = React.useState("agent");
  const [beforeAfterGoal, setBeforeAfterGoal] = React.useState("both");
  const [beforeAfterIncludeSubprocess, setBeforeAfterIncludeSubprocess] = React.useState(DEFAULT_INCLUDE_SUBPROCESS);

  // Fetch the active view's XML when the interactive mode needs it.
  React.useEffect(() => {
    if (activeTab !== "bpmn" || bpmnViewMode !== "interactive" || !selectedId) return;
    if (bpmnXml[activeBpmnTab]) return; // already loaded for this process
    loadBpmnXml(activeBpmnTab, selectedId);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, activeBpmnTab, bpmnViewMode, selectedId]);

  // New process (or a scenario re-analysis) invalidates every cached diagram XML.
  React.useEffect(() => {
    bpmnXmlSeqRef.current += 1;
    setBpmnXml({ current: null, optimized: null, advisory: null });
    setBpmnXmlError("");
    // The mounted viewer instance belongs to the OLD process's diagram — drop it so
    // the token animator waits for the new one rather than marking stale elements.
    setInteractiveViewer(null);
  }, [selectedId]);

  // ── Analyze Process state ─────────────────────────────────────────────────
  const [analyzing, setAnalyzing] = React.useState(false);
  const [analyzeError, setAnalyzeError] = React.useState("");
  const [analysisResult, setAnalysisResult] = React.useState(null);
  const [showAnalysisModal, setShowAnalysisModal] = React.useState(false);
  const [advisoryImage, setAdvisoryImage] = React.useState(null);
  const [restructuring, setRestructuring] = React.useState(false);
  const [restructureError, setRestructureError] = React.useState("");
  const analyzeRunningRef = React.useRef(null);  // processId currently in flight
  const analyzedForRef    = React.useRef(null);  // processId that completed successfully
  const wantsModalRef     = React.useRef(false); // user opened the tab while it was still running
  const analyzeSeqRef     = React.useRef(0);     // discards responses from a superseded process
  // The ACTIVE scenario's bpmn_spec (elimination + parallel flags), sent to the BPMN
  // analyze/restructure so the diagram matches exactly what that scenario's tabs show.
  const activeBpmnSpecRef = React.useRef(undefined);
  const activeScenarioIdRef = React.useRef(null);
  // RESPONSIBLE-role job reassignments (RACI picker) currently in effect, read by
  // loadBpmnXml's "optimized" fetchOnce above so the Optimized diagram's swim
  // lanes follow a hand-picked or agent-accepted reassignment — a what-if change,
  // never persisted unless the analyst clicks Save.
  const activeJobOverridesRef = React.useRef([]);
  const prevJobOverridesKeyRef = React.useRef("[]");

  /**
   * Runs the analysis for `id`, or reuses the in-flight//completed one.
   * Deliberately ref-only so it stays referentially stable and can be called
   * straight from the prefetch effect without re-triggering it.
   *
   * openModal=false is the background prefetch: it must never pop the modal,
   * since the user is probably reading another tab while it runs.
   */
  const runAnalysis = React.useCallback(async (id, { openModal = false } = {}) => {
    if (!id) return;
    // Finished already — just surface it.
    if (analyzedForRef.current === id) {
      if (openModal) setShowAnalysisModal(true);
      return;
    }
    // Same process already in flight — don't fire a second 2-minute request;
    // remember that the user now wants the modal when it lands.
    if (analyzeRunningRef.current === id) {
      if (openModal) wantsModalRef.current = true;
      return;
    }
    const seq = ++analyzeSeqRef.current;
    analyzeRunningRef.current = id;
    setAnalyzing(true);
    setAnalyzeError("");
    setAnalysisResult(null);
    try {
      // Pin the diagram to the ACTIVE scenario (if the optimize run has produced
      // scenarios); undefined → the agent's default TO-BE (which equals the top scenario).
      const result = await analyzeProcess(id, activeBpmnSpecRef.current);
      if (seq !== analyzeSeqRef.current) return; // a different process was selected meanwhile
      analyzedForRef.current = id;
      setAnalysisResult(result);
      // A fresh analysis re-caches the restructured diagram server-side — drop the
      // stale advisory XML so the interactive viewer refetches it on next view.
      setBpmnXml((prev) => ({ ...prev, advisory: null }));
      if (openModal || wantsModalRef.current) setShowAnalysisModal(true);
    } catch (e) {
      if (seq !== analyzeSeqRef.current) return;
      setAnalyzeError(e?.message || "Analysis failed. Please try again.");
    } finally {
      if (seq === analyzeSeqRef.current) {
        analyzeRunningRef.current = null;
        wantsModalRef.current = false;
        setAnalyzing(false);
      }
    }
  }, []);

  const handleRestructureProcess = React.useCallback(async () => {
    if (!selectedId || restructuring) return;
    setRestructuring(true);
    setRestructureError("");
    try {
      if (advisoryImage) URL.revokeObjectURL(advisoryImage);
      const imgUrl = await restructureProcess(selectedId);
      setAdvisoryImage(imgUrl);
      setShowAnalysisModal(false);
      // Restructure regenerates the diagram server-side, so the previously cached
      // advisory XML is stale. Drop it and refetch immediately so the INTERACTIVE
      // Analyze tab shows the new diagram on its own — no manual "Refresh diagram"
      // click. loadBpmnXml flips bpmnXmlLoading, which the viewer surfaces as
      // "Loading the analyzed diagram…" while the fetch is in flight.
      setBpmnXml((prev) => ({ ...prev, advisory: null }));
      loadBpmnXml("advisory", selectedId);
    } catch (e) {
      setRestructureError(e?.message || "Restructure failed. Please try again.");
    } finally {
      setRestructuring(false);
    }
  }, [selectedId, advisoryImage, restructuring, loadBpmnXml]);

  // Reset analyze state for the new process. Does NOT start the analysis here —
  // see the scenario-sync effect below, which is now the ONLY place that starts
  // it. It used to fire eagerly right here, on selectedId alone: that runs before
  // optimizationData (and its scenarios) exist, so it sent NO scenario pin and
  // got the agent's OWN independent default TO-BE — which can DISAGREE with
  // whichever scenario the frontend ranks #1 (e.g. the agent's default finds
  // eliminating the NVA task worth it, while the frontend's Devil's-Quadrangle
  // scoring ranks "Resources only" — no elimination — as the top scenario). That
  // wrong, unpinned result then got "locked in" (analyzedForRef.current = id),
  // so Analyze kept showing the eliminated version while "Resources only" was
  // ACTIVE and Optimized correctly showed the unchanged one. Also clear the
  // scenario refs so a stale spec from a PREVIOUS process can't leak into the
  // next one's first analysis.
  React.useEffect(() => {
    analyzedForRef.current = null;
    analyzeRunningRef.current = null;
    wantsModalRef.current = false;
    setAnalysisResult(null);
    setAnalyzeError("");
    setShowAnalysisModal(false);
    setAdvisoryImage((prev) => { if (prev) URL.revokeObjectURL(prev); return null; });
    activeBpmnSpecRef.current = undefined;
    activeScenarioIdRef.current = null;
    activeJobOverridesRef.current = [];
    prevJobOverridesKeyRef.current = "[]";
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);

  // Keep the active-scenario spec in sync, and START (or RE-ALIGN) the BPMN
  // analysis whenever the ACTIVE scenario becomes known or CHANGES — including
  // the very FIRST time, once optimizationData/scenarios have loaded. This is
  // the single place analysis is kicked off: waiting the extra moment for the
  // real scenario spec (instead of firing blind on selectedId) is what fixes the
  // "Resources only selected but Analyze shows Eliminate NVA steps" mismatch.
  React.useEffect(() => {
    const active = (optimizationData?.scenarios || []).find((s) => s.is_active);
    const newSpec = active?.bpmn_spec;
    const newId = active?.id ?? null;
    activeBpmnSpecRef.current = newSpec;
    const prevId = activeScenarioIdRef.current;
    activeScenarioIdRef.current = newId;
    if (newId != null && newId !== prevId && selectedId) {
      analyzedForRef.current = null;
      analyzeRunningRef.current = null;
      setAnalysisResult(null);
      setAnalyzeError("");
      setAdvisoryImage((prev) => { if (prev) URL.revokeObjectURL(prev); return null; });
      runAnalysis(selectedId, { openModal: false });

      // Also refresh the OPTIMIZED diagram (both the static image and the
      // interactive XML) for the newly active scenario — skipped on the very
      // FIRST determination (prevId === null): optimize()'s own loadBpmnImages
      // call already fetched it correctly pinned, so refetching here would just
      // be a redundant request. "Current" is scenario-independent and is never
      // touched.
      if (prevId != null) {
        setBpmnXml((prev) => ({ ...prev, optimized: null }));
        loadBpmnXml("optimized", selectedId);
        refreshOptimizedImage(selectedId, newSpec, activeJobOverridesRef.current);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [optimizationData, selectedId]);
  // ─────────────────────────────────────────────────────────────────────────
  const [valueAnalysis, setValueAnalysis] = React.useState(null);
  const [valueAnalysisLoading, setValueAnalysisLoading] = React.useState(false);
  const [valueAnalysisError, setValueAnalysisError] = React.useState("");
  const [expandedTasks, setExpandedTasks] = React.useState({});
  const valueAnalysisFetchingRef = React.useRef(false);
  const valueAnalysisSeqRef = React.useRef(0); // discards responses from a superseded process

  // Currency display: results come from the backend in USD; the user can view
  // them converted into another currency without changing the stored data.
  const [displayCurrency, setDisplayCurrency] = React.useState("USD");
  const { rates, refreshing: refreshingRates, refresh: onRefreshRates } =
    useCurrencyRates();

  // Convert a USD amount to the selected currency and format it for display.
  const money = React.useCallback(
    (usdAmount) =>
      formatMoney(
        convertCurrency(usdAmount, "USD", displayCurrency, rates),
        displayCurrency,
      ),
    [displayCurrency, rates],
  );

  // Minutes formatter: past the hour mark a bare minutes figure stops being
  // readable, so every time display ≥60 min carries its hour equivalent too.
  const fmtMin = React.useCallback((min) => {
    const n = Number(min);
    if (!Number.isFinite(n)) return "—";
    const base = `${n.toLocaleString(undefined, { maximumFractionDigits: 2 })} min`;
    if (Math.abs(n) < 60) return base;
    const total = Math.round(Math.abs(n));
    const h = Math.floor(total / 60);
    const m = total % 60;
    return `${base} (≈ ${h} h${m ? ` ${m} m` : ""})`;
  }, []);

  // Bare numeric formatter: round to at most 2 decimals (drops float noise like
  // 2015.9999999999998 and trailing zeros). Returns "—" for non-finite input.
  const num2 = React.useCallback((v) => {
    const n = Number(v);
    if (!Number.isFinite(n)) return "—";
    return n.toLocaleString(undefined, { maximumFractionDigits: 2 });
  }, []);

  // Rounds every numeric substring in a backend-provided string to at most 2
  // decimals (e.g. "$12.582390979055507/hr" → "$12.58/hr").
  const fixDecimals = React.useCallback(
    (s) => (typeof s === "string" ? s.replace(/\d+\.\d{3,}/g, (m) => Number(m).toFixed(2)) : s),
    [],
  );

  const scenarioSectionHeading = React.useMemo(
    () => getScenarioSectionHeading(optimizationData),
    [optimizationData],
  );
  const scenarioTypeLabel = React.useMemo(
    () => getScenarioTypeLabel(optimizationData),
    [optimizationData],
  );

  const handleBackToList = React.useCallback(() => {
    resetSelection();
    navigate("/process-optimization");
  }, [navigate, resetSelection]);

  const handleErrorClose = React.useCallback(() => {
    setErrorModal({ open: false, title: "", message: "" });
    handleBackToList();
  }, [handleBackToList, setErrorModal]);

  // Value-Added tab: persist the AI classifications only when the user presses Save.
  // Goes through the backend populate-if-empty endpoint, so it fills unclassified steps
  // and never overwrites a USER override or an existing value.
  const [savingVa, setSavingVa] = React.useState(false);
  const [vaSaveMsg, setVaSaveMsg] = React.useState("");
  // task_ids the reviewer ticked to let AI overwrite their saved classification on Save.
  const [vaOverwrite, setVaOverwrite] = React.useState({});
  const toggleVaOverwrite = React.useCallback(
    (taskId) => setVaOverwrite((prev) => ({ ...prev, [taskId]: !prev[taskId] })),
    [],
  );
  // Analyst's own classification choice per task (overrides the AI suggestion). A
  // changed value saves as a USER classification — authoritative, never AI-overwritten.
  const [vaEdits, setVaEdits] = React.useState({});
  const setVaEdit = React.useCallback(
    (taskId, val) => setVaEdits((prev) => ({ ...prev, [taskId]: val })),
    [],
  );
  const handleSaveVaClassifications = React.useCallback(async () => {
    const tcs = Array.isArray(valueAnalysis?.task_classifications)
      ? valueAnalysis.task_classifications
      : [];
    const aiItems = [];
    const userItems = [];
    for (const t of tcs) {
      // `dominant_category` is the EFFECTIVE current classification (the agent
      // overlays a saved USER override onto the fresh AI result); the model's own
      // call is preserved separately under `ai_suggested_category`.
      const effective = String(t.dominant_category || "").toUpperCase();
      const aiSuggested = String(t.ai_suggested_category || t.dominant_category || "").toUpperCase();
      const tid = Number(t.task_id);
      if (!Number.isFinite(tid)) continue;
      const chosen = String(vaEdits[tid] || effective).toUpperCase();
      if (!["VA", "BVA", "NVA"].includes(chosen)) continue;
      if (vaEdits[tid] && chosen !== effective) {
        userItems.push({
          task_id: tid,
          value_classification: chosen,
          value_rationale: `Manually set by analyst (AI suggested ${aiSuggested}: ${t.va_percentage ?? 0}% VA / ${t.bva_percentage ?? 0}% BVA / ${t.nva_percentage ?? 0}% NVA).`,
          bva_business_goal: chosen === "BVA" ? t.action_recommendation || null : null,
        });
      } else if (t.user_override) {
        // Already a saved USER override the analyst didn't change this session —
        // leave it untouched (re-populating would be a no-op the backend skips,
        // but skipping here keeps it out of the "populated" count and messaging).
        continue;
      } else if (["VA", "BVA", "NVA"].includes(effective)) {
        const rationale =
          `AI: ${t.va_percentage ?? 0}% VA / ${t.bva_percentage ?? 0}% BVA / ` +
          `${t.nva_percentage ?? 0}% NVA. ${t.action_recommendation || ""}`.trim();
        const item = { task_id: tid, value_classification: effective, value_rationale: rationale };
        if (effective === "BVA" && t.action_recommendation) item.bva_business_goal = t.action_recommendation;
        aiItems.push(item);
      }
    }
    if (aiItems.length === 0 && userItems.length === 0) {
      setVaSaveMsg("No classifications to save.");
      return;
    }
    setSavingVa(true);
    setVaSaveMsg("");
    try {
      const parts = [];
      // Analyst-chosen values: authoritative USER classifications, matched by
      // process_task_id via the backend's stored value-analysis rows.
      if (userItems.length) {
        const stored = await getStoredValueAnalysis(selectedId).catch(() => null);
        const ptByTask = new Map(
          (stored?.tasks || []).map((r) => [Number(r.task_id), r.process_task_id]),
        );
        const payload = userItems
          .filter((u) => ptByTask.has(u.task_id))
          .map((u) => ({
            process_task_id: ptByTask.get(u.task_id),
            value_classification: u.value_classification,
            value_rationale: u.value_rationale,
            bva_business_goal: u.bva_business_goal,
          }));
        if (payload.length) {
          await saveValueClassifications(selectedId, payload);
          parts.push(`${payload.length} set by you (USER)`);
        }
      }
      if (aiItems.length) {
        const overwriteIds = aiItems.map((it) => it.task_id).filter((id) => vaOverwrite[id]);
        const res = await applyAiClassifications(selectedId, aiItems, overwriteIds);
        const populated = res?.populated ?? 0;
        const overwritten = res?.overwritten ?? 0;
        const skipped = res?.skipped_task_ids?.length ?? 0;
        if (populated) parts.push(`${populated} populated`);
        if (overwritten) parts.push(`${overwritten} AI-overwritten`);
        if (skipped) parts.push(`${skipped} left as-is`);
      }
      setVaSaveMsg(
        parts.length
          ? `Saved — ${parts.join(", ")}.`
          : "Saved — no changes (pick a classification or tick a step to let AI overwrite).",
      );
      // Data-consistency rule: a change at one stage must reflect everywhere. The flow/
      // CTE/DQ numbers were computed from the OLD classifications — flag them stale.
      if (parts.length) setVaStale(true);
    } catch (e) {
      setVaSaveMsg(e?.message || "Failed to save classifications.");
    } finally {
      setSavingVa(false);
    }
  }, [selectedId, valueAnalysis, vaOverwrite, vaEdits]);

  // Reset to overview whenever new optimization data arrives (prevents ghost tabs).
  // A scenario SWITCH is a state-local swap, not a new run — keep the user where they are.
  React.useEffect(() => {
    if (scenarioSwitchRef.current) {
      scenarioSwitchRef.current = false;
      return;
    }
    setActiveTab("overview");
    setQfOverrides({});
    setAdoptedHeuristics({});
    setVaEdits({});
    setVaStale(false);
    // Re-running optimize also invalidates the CACHED Value-Added analysis. The
    // agent overlays any USER override the analyst saved before the re-run, but the
    // tab caches its previous response and its fetch is guarded against re-fetching
    // while non-null — so without this it kept showing the pre-change AI label (the
    // "reverts on re-run" bug) while every OTHER tab recomputes from the saved
    // values, leaving the tabs inconsistent. Drop the cache so the Value-Added tab
    // re-fetches fresh and agrees with the rest. Guarded so the very first run (no
    // cache yet, possibly a VA fetch already in flight) is left undisturbed.
    setValueAnalysis((prev) => {
      if (!prev) return prev;
      valueAnalysisSeqRef.current += 1;      // discard any in-flight response
      valueAnalysisFetchingRef.current = false;
      return null;                            // force a fresh fetch
    });
    setValueAnalysisError("");
    setExpandedTasks({});
    // Fresh run → the agent's full reassignment set is in effect again, and any
    // hand-picked reassignment is void: it was scored against the previous run's
    // staffing and the new response already reflects whatever was saved.
    setDeselectedReplacements({});
    setAppliedSelection(null);
    setSelectionError("");
    setSelectionWarnings([]);
    setManualOverrides({});
    setEditTarget(null);
    setEditError("");
  }, [optimizationData]);

  // ── Selective reassignment apply (Analyze tab) ────────────────────────────
  // The analyst chooses WHICH of the agent's cheaper-job swaps to keep — all,
  // some, or none — and the chosen subset is re-scored by the BACKEND what-if
  // (the same canonical engine that scored the scenarios; no client-side math).
  // The result replaces the active TO-BE, so every tab (Overview, Analyze,
  // AS-IS vs TO-BE, Flow) reflects exactly the accepted swaps.
  const replacementKey = React.useCallback(
    (r) => `${r.task_id}-${r.original_job_id ?? r.original_job?.job_id ?? "j"}-${r.raci_role ?? ""}`,
    [],
  );
  const [deselectedReplacements, setDeselectedReplacements] = React.useState({});
  const [applyingSelection, setApplyingSelection] = React.useState(false);
  const [selectionError, setSelectionError] = React.useState("");
  const [selectionWarnings, setSelectionWarnings] = React.useState([]);
  // null = the agent's default (all swaps); {applied, total} after a custom apply.
  const [appliedSelection, setAppliedSelection] = React.useState(null);

  // ── Manual RACI reassignment (task-focused workload table) ────────────────
  // The analyst picks the job for a role directly, instead of only accepting or
  // rejecting what the agent proposed. Manual edits and agent swaps live in ONE
  // override set (see mergeOverrides), so both are re-scored by the same backend
  // engine and persisted by the same Save path — a hand-picked job is not a
  // second-class change.
  const [manualOverrides, setManualOverrides] = React.useState({});
  const [editTarget, setEditTarget] = React.useState(null); // {taskId, taskName, role, job}
  const [editError, setEditError] = React.useState("");
  // Workload Balancing RACI view. Task-focused is the default: "who does step N"
  // is the question an analyst reassigning work actually starts from, and it is
  // the only view where a role-holder can be edited (a job-focused row spans
  // several tasks, so an edit there would be ambiguous).
  const [raciView, setRaciView] = React.useState("task"); // "task" | "job"

  // Every job is offerable in the reassignment picker — a skill gap is advice,
  // not a bar (consistent with the advisory skill banner above). But "every job"
  // means every job OF THIS PROCESS'S COMPANY: a job belongs to one company, so
  // an unscoped list would offer staff that cannot legally do this work. The
  // company comes from the process record the optimize run already fetched.
  const jobsCompanyId =
    processMeta?.company_id ??
    processMeta?.company?.company_id ??
    processMeta?.companyId ??
    null;
  const [allJobs, setAllJobs] = React.useState([]);
  React.useEffect(() => {
    let cancelled = false;
    setAllJobs([]);
    // processMeta is still null while its own fetch (inside optimize()) is
    // in flight, which looks identical to "this process genuinely has no
    // company" — wait for it to settle instead of firing the unscoped /job
    // fallback below on every page load.
    if (jobsCompanyId == null && optimizing) return undefined;
    (async () => {
      if (jobsCompanyId != null) {
        // Already falls back internally from /job/company/{id} to
        // /job?companyId=, and resolves to [] rather than throwing.
        const list = await getJobsByCompanyId(jobsCompanyId).catch(() => []);
        if (!cancelled) setAllJobs(Array.isArray(list) ? list : []);
        return;
      }
      // No company on the process record: rather than silently offering nothing,
      // fall back to the unscoped list. Cross-company jobs are possible here, so
      // the picker labels each candidate with its company. `/job` is restricted
      // for non-superadmin roles (403) — suppressErrorPage keeps that failure
      // local to this optional picker instead of bouncing the whole page to
      // the global error screen.
      try {
        const list = await getJobs({ suppressErrorPage: true });
        if (!cancelled) setAllJobs(Array.isArray(list) ? list : []);
      } catch (_) {
        // Empty degrades the picker to the optimization's own candidates rather
        // than breaking it.
        if (!cancelled) setAllJobs([]);
      }
    })();
    return () => { cancelled = true; };
  }, [jobsCompanyId, optimizing]);

  // Whether the RACI staffing totals (matrix-based, unlike the flow metrics) have
  // been recomputed for the override set currently in effect. Shared by the
  // Overview tile and the Save tab so the two cannot disagree about it.
  // True once ANY re-score has happened without a refreshed matrix — including a
  // re-score that ended with zero overrides. Rejecting every suggested swap still
  // leaves the matrix describing the agent's optimized staffing, which is exactly
  // as wrong as it is when overrides were added.
  const staffingStale =
    appliedSelection != null && !appliedSelection.staffing_refreshed;

  const pendingOverrides = React.useMemo(
    () =>
      mergeOverrides(
        agentOverrides(optimizationData, deselectedReplacements, replacementKey),
        manualOverrides,
      ),
    [optimizationData, deselectedReplacements, manualOverrides, replacementKey],
  );

  // The subset of pendingOverrides that can move a BPMN swim lane: only
  // RESPONSIBLE (R) — the role a lane represents (who performs the task).
  // Accountable/Consulted/Informed reassignments change staffing, not the
  // diagram.
  const bpmnJobOverrides = React.useMemo(
    () =>
      pendingOverrides
        .filter((o) => String(o.role || "").toUpperCase() === "R" && o.new_job_id != null)
        .map((o) => ({ task_id: o.task_id, job_id: o.new_job_id })),
    [pendingOverrides],
  );

  // Refresh the Optimized diagram (both the static image and the interactive
  // XML) whenever which RESPONSIBLE reassignments are in effect actually
  // changes — including going back to none (an "undo"). Without this, applying
  // or undoing a reassignment re-scored the cost/staffing numbers but left the
  // swim lanes exactly as they were.
  React.useEffect(() => {
    const key = JSON.stringify(
      [...bpmnJobOverrides].sort((a, b) => a.task_id - b.task_id),
    );
    activeJobOverridesRef.current = bpmnJobOverrides;
    const prevKey = prevJobOverridesKeyRef.current;
    prevJobOverridesKeyRef.current = key;
    if (key === prevKey || !selectedId) return;
    setBpmnXml((prev) => ({ ...prev, optimized: null }));
    loadBpmnXml("optimized", selectedId);
    refreshOptimizedImage(selectedId, activeBpmnSpecRef.current, bpmnJobOverrides);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bpmnJobOverrides, selectedId]);

  // Keep the save-time ref in sync: every ACCEPTED change (ticked agent swaps +
  // manual reassignments) in the backend's job_reassignments wire shape. Save
  // persists exactly this set into the new version's staffing (clone-on-reassign;
  // the original is untouched).
  React.useEffect(() => {
    acceptedReplacementsRef.current = toJobReassignments(pendingOverrides);
  }, [pendingOverrides, acceptedReplacementsRef]);

  /**
   * Re-score the process with `overrideList` in effect. The single place any
   * staffing change reaches the backend: the checkbox list and the manual picker
   * both come through here, so they can never diverge in how they are scored.
   * Returns "" on success or the error message, letting each caller decide where
   * to surface it.
   */
  const rescoreWithOverrides = React.useCallback(async (overrideList, info) => {
    if (!selectedId) return "No process selected.";
    setApplyingSelection(true);
    setSelectionError("");
    setSelectionWarnings([]);
    try {
      const overrides = toResourceOverrides(overrideList);
      const active = (optimizationData?.scenarios || []).find((s) => s.is_active);
      // gateways is a REQUIRED array on the what-if DTO; with base_on_stored_gateways
      // an empty delta list means "keep the stored AS-IS branching unchanged".
      const body = { base_on_stored_gateways: true, gateways: [], resource_overrides: overrides };
      // Keep the active scenario's eliminations so the re-score stays comparable.
      const ex = active?.bpmn_spec?.exclude_task_ids;
      if (Array.isArray(ex)) body.exclude_task_ids = ex.map(Number);
      const nf = await whatIfFlowAnalysis(selectedId, body);
      // Overrides the backend could not apply are surfaced, never swallowed.
      const warn = (nf?.flags || []).filter((f) => /resource override/i.test(String(f)));
      const r2 = (x) => Math.round(Number(x) * 100) / 100;
      const patchDim = (dim, toBe, higherIsBetter) => {
        if (!dim || toBe == null) return dim;
        const asIsV = Number(dim.as_is);
        const delta = r2(toBe - asIsV);
        return {
          ...dim,
          to_be: toBe,
          delta,
          percent_change: asIsV ? r2((delta / asIsV) * 100) : null,
          improved: higherIsBetter ? delta > 0 : delta < 0,
        };
      };
      // The RACI staffing figures (the Overview's staffing tile and the Save
      // tab's totals) live on raci_assignments.matrix, NOT on the flow object —
      // so they only move if the what-if response carries a refreshed matrix.
      // When it does, adopt it; when it does not, say so rather than presenting
      // the pre-change total next to re-scored numbers as though it agreed.
      const freshRaci = nf?.raci_assignments?.matrix ? nf.raci_assignments : null;
      const selectionInfo = { ...info, staffing_refreshed: !!freshRaci };
      scenarioSwitchRef.current = true; // state-local swap — stay on this tab
      setOptimizationData((prev) => {
        if (!prev) return prev;
        const dims = prev.redesign_report?.dimensions || {};
        const newDims = {
          ...dims,
          time: patchDim(dims.time, nf.cycle_time_minutes, false),
          cost: patchDim(dims.cost, nf.labor_cost_per_case, false),
          quality: patchDim(dims.quality, nf.quality_score, true),
          flexibility: patchDim(dims.flexibility, nf.flexibility_score, true),
        };

        // The TO-BE moved, so every surface that EXPLAINS it has to move with the
        // numbers — not just the tabs that read the flow object.
        //
        // 1. evidence.cost.resource_changes feeds the AS-IS vs TO-BE evidence box
        //    AND the Flow tab's "why the TO-BE improves" list. Hand-picked swaps
        //    are appended so neither claims "no resource reassignments" while a
        //    reassignment is driving the cost figure above it.
        const taskNameById = new Map(
          [
            ...(prev.raci_assignments?.tasks || []),
            ...(prev.redesign_flow?.as_is?.tasks || []),
          ].map((t) => [String(t.task_id), t.task_name]),
        );
        const manualRows = manualResourceChanges(overrideList, taskNameById);
        const prevEvidence = prev.redesign_report?.evidence || {};
        const newEvidence = manualRows.length
          ? {
              ...prevEvidence,
              cost: {
                ...(prevEvidence.cost || {}),
                resource_changes: [
                  // The agent's own rows are left as it reported them; analyst rows
                  // are additive and marked, so the two are never conflated.
                  ...((prevEvidence.cost?.resource_changes || []).filter((r) => !r.analyst_set)),
                  ...manualRows,
                ],
              },
            }
          : prevEvidence;

        // 2. The ranked scenario cards advertise their own improvement
        //    percentages. Once the ACTIVE candidate has been re-scored they are
        //    the old candidate's numbers, so they are recomputed from the patched
        //    dimensions and the card is marked as analyst-modified. `score` is a
        //    backend ranking weight and is NOT recomputed — see the card.
        //
        //    The flag is ASSIGNED, never OR-ed with its previous value: undoing
        //    every manual edit (or re-ticking every suggested swap) puts the
        //    candidate back to what the engine ranked, and a sticky badge would
        //    keep withholding a score that is valid again.
        const newImprovements = improvementsFromDimensions(newDims);
        const userModified = !!info.user_modified;

        return {
          ...prev,
          redesign_flow: { ...prev.redesign_flow, to_be: nf },
          ...(freshRaci
            ? {
                raci_assignments: {
                  ...prev.raci_assignments,
                  ...freshRaci,
                  matrix: { ...prev.raci_assignments?.matrix, ...freshRaci.matrix },
                },
              }
            : {}),
          redesign_report: {
            ...prev.redesign_report,
            dimensions: newDims,
            evidence: newEvidence,
            user_selection: selectionInfo,
          },
          scenarios: (prev.scenarios || []).map((s) =>
            s.is_active
              ? {
                  ...s,
                  improvements: newImprovements,
                  user_modified: userModified,
                  redesign_flow: { ...s.redesign_flow, to_be: nf },
                  redesign_report: s.redesign_report
                    ? {
                        ...s.redesign_report,
                        dimensions: newDims,
                        evidence: newEvidence,
                        user_selection: selectionInfo,
                      }
                    : s.redesign_report,
                }
              : s,
          ),
        };
      });
      setAppliedSelection(selectionInfo);
      setSelectionWarnings(warn);
      return "";
    } catch (e) {
      const msg = e?.message || "Could not re-score the selected reassignments.";
      setSelectionError(msg);
      return msg;
    } finally {
      setApplyingSelection(false);
    }
  }, [selectedId, optimizationData, setOptimizationData]);

  const applySelectedReplacements = React.useCallback(async () => {
    const reps = optimizationData?.normalized?.costOptimization?.replacements || [];
    if (!selectedId || applyingSelection || reps.length === 0) return;
    const manualCount = Object.keys(manualOverrides).length;
    await rescoreWithOverrides(pendingOverrides, {
      applied: pendingOverrides.length,
      total: Math.max(reps.length, pendingOverrides.length),
      manual: manualCount,
      // Deviates from the ranked candidate if anything was hand-picked OR any
      // suggested swap was dropped. Re-ticking everything makes it false again.
      user_modified: manualCount > 0 || pendingOverrides.length !== reps.length,
      note: "Reassignment subset chosen by the analyst — TO-BE re-scored by the backend what-if engine.",
    });
  }, [
    selectedId,
    applyingSelection,
    optimizationData,
    manualOverrides,
    pendingOverrides,
    rescoreWithOverrides,
  ]);

  /**
   * Commit one manual pick from the modal. The merged set is computed from the
   * NEW edit rather than from state, because the re-score has to send the change
   * the analyst just made — not the set as it was one render ago.
   */
  const applyManualOverride = React.useCallback(
    async (job) => {
      if (!editTarget || applyingSelection) return;
      const { taskId, role, job: fromJob } = editTarget;
      const next = {
        ...manualOverrides,
        [overrideKey(taskId, role, fromJob?.job_id)]: {
          task_id: Number(taskId),
          role,
          replace_job_id: fromJob?.job_id != null ? Number(fromJob.job_id) : null,
          new_job_id: Number(job.job_id),
          new_job_name: job.job_name,
          new_job_rate: job.hourly_rate,
          from_job_name: fromJob?.name || fromJob?.job_name || null,
          // Carried so the evidence line can price BOTH sides of the swap; without
          // it the "from" rate renders blank next to a populated "to" rate.
          from_job_rate: fromJob?.hourly_rate ?? null,
        },
      };
      const merged = mergeOverrides(
        agentOverrides(optimizationData, deselectedReplacements, replacementKey),
        next,
      );
      const reps = optimizationData?.normalized?.costOptimization?.replacements || [];
      setEditError("");
      const err = await rescoreWithOverrides(merged, {
        applied: merged.length,
        total: Math.max(reps.length, merged.length),
        manual: Object.keys(next).length,
        user_modified: true,
        note: "Includes reassignment(s) you set by hand — TO-BE re-scored by the backend what-if engine.",
      });
      if (err) {
        // The staffing change is only recorded once the backend has scored it,
        // so a failed re-score must not leave the table showing a job the
        // numbers below it never accounted for.
        setEditError(err);
        return;
      }
      setManualOverrides(next);
      setEditTarget(null);
    },
    [
      editTarget,
      applyingSelection,
      manualOverrides,
      optimizationData,
      deselectedReplacements,
      replacementKey,
      rescoreWithOverrides,
    ],
  );

  const revertManualOverride = React.useCallback(
    async (taskId, role, fromJobId) => {
      if (applyingSelection) return;
      const next = { ...manualOverrides };
      delete next[overrideKey(taskId, role, fromJobId)];
      const merged = mergeOverrides(
        agentOverrides(optimizationData, deselectedReplacements, replacementKey),
        next,
      );
      const reps = optimizationData?.normalized?.costOptimization?.replacements || [];
      const err = await rescoreWithOverrides(merged, {
        applied: merged.length,
        total: Math.max(reps.length, merged.length),
        manual: Object.keys(next).length,
        // Undoing the LAST manual edit while every suggested swap is still ticked
        // restores the ranked candidate exactly — so this can legitimately be false.
        user_modified:
          Object.keys(next).length > 0 || merged.length !== reps.length,
        note: "Manual reassignment reverted — TO-BE re-scored by the backend what-if engine.",
      });
      if (!err) setManualOverrides(next);
    },
    [
      applyingSelection,
      manualOverrides,
      optimizationData,
      deselectedReplacements,
      replacementKey,
      rescoreWithOverrides,
    ],
  );
  // ─────────────────────────────────────────────────────────────────────────

  // VAA is slow, so it starts on load rather than on tab open — the user can work
  // in other tabs while it runs and the Value tab shows its own loader. When the AI
  // analysis fails or silently degrades (nothing may fail without the user knowing),
  // fall back to the PREVIOUSLY SAVED classifications from the CMS, say exactly why,
  // and offer Retry.
  React.useEffect(() => {
    if (!selectedId || valueAnalysis || valueAnalysisFetchingRef.current) return;
    // Discards a response once a different process has superseded this run. Not a
    // cleanup flag: this effect re-runs on its own setValueAnalysis, so a cleanup
    // flag would also fire on the success path and strand the loader.
    const seq = ++valueAnalysisSeqRef.current;
    const isStale = () => seq !== valueAnalysisSeqRef.current;
    valueAnalysisFetchingRef.current = true;
    setValueAnalysisLoading(true);
    setValueAnalysisError("");
    const storedFallback = async (reason) => {
      try {
        const stored = await getStoredValueAnalysis(selectedId);
        if (isStale()) return;
        // Keep sub-process rows: they are NOT classifiable (no description to decompose —
        // their value content lives in the child process), but hiding them made the tab
        // silently disagree with the 12-step process. They render as read-only rows that
        // say why. Only real tasks count toward the VA/BVA/NVA percentages.
        const allRows = stored?.tasks || [];
        const tasks = allRows.filter((t) => !t.is_child_process);
        const classified = tasks.filter((t) => t.value_classification);
        const pct = (cat) =>
          classified.length ? Math.round((classified.filter((t) => t.value_classification === cat).length / classified.length) * 100) : 0;
        setValueAnalysis({
          _fallback: { reason },
          process_summary: {
            total_tasks: tasks.length,
            total_steps_analyzed: classified.length,
            overall_va_percentage: pct("VA"),
            overall_bva_percentage: pct("BVA"),
            overall_nva_percentage: pct("NVA"),
          },
          task_classifications: allRows.map((t) => ({
            task_id: t.task_id ?? `sub-${t.process_task_id}`,
            task_name: t.task_name,
            dominant_category: t.value_classification || "",
            va_percentage: null,
            bva_percentage: null,
            nva_percentage: null,
            action_recommendation: t.value_rationale || "",
            jobs: [],
            steps: [],
            _saved_source: t.value_source || null,
            saved_source: t.value_source || null,
            // Stored USER classifications win here too (the AI was unavailable, so
            // there is no fresh suggestion to show alongside).
            user_override: t.value_source === "USER",
            _is_subprocess: !!t.is_child_process,
          })),
        });
      } catch (e2) {
        if (isStale()) return;
        setValueAnalysisError(
          `AI value analysis failed (${reason}) and the saved classifications could not be loaded either (${e2?.message || "unknown"}).`,
        );
      }
    };
    getValueAnalysis(selectedId)
      .then(async (data) => {
        if (isStale()) return;
        const degraded = data && data.status && data.status !== "ok";
        const empty = !data || !(data.task_classifications || []).length;
        if (degraded || empty) {
          await storedFallback(
            degraded
              ? `the AI model was unavailable or returned an unusable response (status: ${data.status})`
              : "the AI returned no classifications",
          );
        } else {
          setValueAnalysis(data);
        }
      })
      .catch(async (e) => {
        if (isStale()) return;
        await storedFallback(e?.message || "the analysis request failed");
      })
      .finally(() => {
        if (isStale()) return;
        valueAnalysisFetchingRef.current = false;
        setValueAnalysisLoading(false);
      });
  }, [selectedId, valueAnalysis]);

  const retryValueAnalysis = React.useCallback(() => {
    // Clearing the state re-arms the lazy-load effect above for a fresh AI attempt.
    setValueAnalysis(null);
    setValueAnalysisError("");
  }, []);

  // Reset VAA state when the selected process changes
  React.useEffect(() => {
    setValueAnalysis(null);
    setValueAnalysisError("");
    setExpandedTasks({});
    valueAnalysisFetchingRef.current = false;
  }, [selectedId]);

  // Reset RL redesign state when the selected process changes (otherwise a
  // previous process's last run/final_bpmn_xml could linger under "RL BPMN"),
  // and eagerly check RL readiness — it's a fast, no-training call, so there's
  // no reason to wait for the analyst to open the RL tab to find out whether
  // the button should even be enabled. The actual training run stays on-demand.
  React.useEffect(() => {
    rl.reset();
    if (selectedId) rl.loadPreview(selectedId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);

  // Reset BPR redesign state when the selected process changes — same
  // reasoning as the RL reset above. Unlike RL there's no cheap "preview" to
  // eagerly call (the BPR API has no such endpoint): the model only runs
  // on-demand via the Run button in the Redesign tab.
  React.useEffect(() => {
    bpr.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);

  React.useEffect(() => {
    if (!processId) return;
    const parsed = Number(processId);
    const pid = Number.isNaN(parsed) ? processId : parsed;
    resetSelection();
    // The constant, not the state, on purpose: this effect must not re-fire when the
    // analyst ticks the box (that is what "Re-run with this goal" is for), and at
    // this point the state IS the constant anyway.
    optimize(pid, undefined, { includeSubprocessRedesigns: DEFAULT_INCLUDE_SUBPROCESS });
  }, [optimize, resetSelection, processId]);

  // Advisory skill-match check for the selected process (mismatch is a signal, not a block).
  React.useEffect(() => {
    if (!selectedId) {
      setSkillCheck(null);
      return;
    }
    let cancelled = false;
    getSkillCheck(selectedId)
      .then((d) => { if (!cancelled) setSkillCheck(d); })
      .catch(() => { if (!cancelled) setSkillCheck(null); });
    return () => { cancelled = true; };
  }, [selectedId]);

  const raciTaskGroups = React.useMemo(() => {
    const tasks = optimizationData?.raci_assignments?.tasks;
    if (!Array.isArray(tasks)) return [];
    const frozenJobs = optimizationData?.normalized?.workloadBalancing?.frozen_jobs || [];
    // Carried alongside the name so the reassignment picker can show what the
    // incumbent costs and price each candidate against it.
    const rateOf = (j) => j?.hourly_rate ?? j?.hourlyRate ?? null;
    return tasks.map((task, idx) => {
      const jobs = [];
      (Array.isArray(task.responsible) ? task.responsible : []).forEach((j) =>
        jobs.push({ job_id: j.job_id, name: j.job_name, role: "R", hourly_rate: rateOf(j) }),
      );
      if (task.accountable) {
        jobs.push({ job_id: task.accountable.job_id, name: task.accountable.job_name, role: "A", hourly_rate: rateOf(task.accountable) });
      }
      (Array.isArray(task.consulted) ? task.consulted : [])
        // The RACI matrix records REAL staffing only. Pool candidates are
        // possible REPLACEMENTS, not extra consultants — they surface in the
        // Analyze tab's reassignment cards, never as additional RACI rows.
        // (Newer agents already keep them out of `consulted`; this filter
        // covers older payloads that still folded them in.)
        .filter((j) => !j.is_pool_suggestion)
        .forEach((j) => jobs.push({ job_id: j.job_id, name: j.job_name, role: "C", hourly_rate: rateOf(j) }));
      frozenJobs
        .filter((f) => String(f.task_id) === String(task.task_id))
        .forEach((f) => jobs.push({ job_id: f.job_id, name: f.job_name, role: "I", hourly_rate: rateOf(f) }));
      return {
        task_id: task.task_id,
        task_name: task.task_name,
        order: task.order ?? idx + 1,
        jobs,
        // Sub-process composite: no R/A/C/I at this level — it carries the child's own
        // RACI so the row can be expanded to show who really does that work.
        is_subprocess: !!task.is_subprocess,
        child_process_id: task.child_process_id,
        subprocess_note: task.subprocess_note,
        child_raci: task.child_raci,
        composite_cost_usd: task.composite_cost_usd,
        composite_minutes: task.composite_minutes,
      };
    });
  }, [optimizationData]);

  // The same rows with any hand-picked reassignment applied — what the staffing
  // WILL be once saved. Drives the task-focused workload table (where the edit is
  // made); the Overview table deliberately keeps showing the measured AS-IS and
  // only flags the pending change (see raciOverrideFor).
  const effectiveRaciTaskGroups = React.useMemo(
    () => applyOverridesToTaskGroups(raciTaskGroups, manualOverrides),
    [raciTaskGroups, manualOverrides],
  );

  const raciTaskNameById = React.useMemo(
    () => new Map(raciTaskGroups.map((tg) => [String(tg.task_id), tg.task_name])),
    [raciTaskGroups],
  );

  /** The pending manual reassignment of one role-holder, if any. */
  const raciOverrideFor = React.useCallback(
    (taskId, role, jobId) => manualOverrides[overrideKey(taskId, role, jobId)] || null,
    [manualOverrides],
  );

  // Which sub-process rows are expanded (shared by the Overview / Analyze / Save tables).
  const [expandedSubprocess, setExpandedSubprocess] = React.useState({});
  const toggleSubprocess = React.useCallback(
    (id) => setExpandedSubprocess((p) => ({ ...p, [id]: !p[id] })),
    [],
  );

  /**
   * The child process's own RACI, shown when a sub-process row is expanded — the parent
   * has no R/A/C/I for a composite step, so this is where "who does this work" lives.
   */
  const SubprocessRaciDetail = React.useCallback(
    ({ row, colSpan }) => {
      const child = row?.child_raci;
      return (
        <tr>
          <td colSpan={colSpan} style={{ padding: 0, background: "#faf5ff", borderTop: "1px solid #e9d5ff" }}>
            <div style={{ padding: "12px 18px" }}>
              <div style={{ fontSize: 12, color: "#6b21a8", marginBottom: 10, lineHeight: 1.5 }}>
                {row?.subprocess_note ||
                  "Sub-process — its people, time and cost are defined inside its own process."}
              </div>
              {!child || !(child.rows || []).length ? (
                <div style={{ fontSize: 12, color: "#9ca3af" }}>
                  The child process&apos;s RACI is unavailable — re-run Optimize to load it.
                </div>
              ) : (
                <>
                  <div style={{ fontSize: 11.5, fontWeight: 700, color: "#5b21b6", marginBottom: 6 }}>
                    {child.process_name} — {child.rows.length} step(s) · staffing total {money(child.total_process_cost_usd ?? 0)}
                  </div>
                  <div style={{ overflowX: "auto" }}>
                    <table style={{ borderCollapse: "collapse", width: "100%", minWidth: 460, fontSize: 12, background: "#fff", borderRadius: 8 }}>
                      <thead>
                        <tr>
                          {["Step", "Job", "Role", "Time (min)", "Cost"].map((h, i) => (
                            <th key={h} style={{ padding: "6px 10px", fontSize: 10.5, color: "#6b7280", textAlign: i >= 3 ? "right" : "left", borderBottom: "1px solid #ede9fe" }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {child.rows.flatMap((cr) => {
                          const cells = [
                            ...(Array.isArray(cr.R) ? cr.R : []).map((e) => ({ ...e, _role: "R" })),
                            ...(cr.A ? [{ ...cr.A, _role: "A" }] : []),
                            ...(Array.isArray(cr.C) ? cr.C : []).map((e) => ({ ...e, _role: "C" })),
                            ...(Array.isArray(cr.I) ? cr.I : []).map((e) => ({ ...e, _role: "I" })),
                          ].filter((e) => e && e.job_name);
                          if (!cells.length) {
                            return [(
                              <tr key={`${cr.task_id}-none`}>
                                <td style={{ padding: "5px 10px" }}>{cr.task_name}</td>
                                <td colSpan={4} style={{ padding: "5px 10px", color: "#9ca3af" }}>No jobs assigned</td>
                              </tr>
                            )];
                          }
                          return cells.map((e, i) => (
                            <tr key={`${cr.task_id}-${e.job_id ?? e.job_name}-${e._role}`}>
                              <td style={{ padding: "5px 10px", color: i === 0 ? "#1e293b" : "transparent" }}>{i === 0 ? cr.task_name : "·"}</td>
                              <td style={{ padding: "5px 10px" }}>{e.job_name}</td>
                              <td style={{ padding: "5px 10px" }}>
                                <span className={`raci-badge raci-${String(e._role).toLowerCase()}`}>{e._role}</span>
                              </td>
                              <td style={{ padding: "5px 10px", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                                {e.attributed_time_minutes != null ? Number(e.attributed_time_minutes).toFixed(0) : "—"}
                              </td>
                              <td style={{ padding: "5px 10px", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{money(e.cost_usd ?? 0)}</td>
                            </tr>
                          ));
                        })}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          </td>
        </tr>
      );
    },
    [money],
  );

  const SubprocessToggle = React.useCallback(
    ({ id, open }) => (
      <button
        type="button"
        onClick={() => toggleSubprocess(id)}
        title={open ? "Hide the sub-process's own RACI" : "Show the sub-process's own RACI (who does this work)"}
        style={{ display: "inline-flex", alignItems: "center", gap: 4, verticalAlign: "middle", marginLeft: 6, border: "1px solid #c4b5fd", background: open ? "#ede9fe" : "#fff", color: "#5b21b6", borderRadius: 999, cursor: "pointer", fontSize: 10, fontWeight: 800, lineHeight: 1.5, padding: "2px 9px", whiteSpace: "nowrap" }}
      >
        {open ? "▾ Hide RACI" : "▸ View RACI"}
      </button>
    ),
    [toggleSubprocess],
  );

  if (!processId) {
    return (
      <div className="page-container po-redesign">
        <div className="page-header">
          <div className="header-content">
            <div>
              <h2 className="page-title">Optimization Results</h2>
              <p className="page-subtitle">No process selected</p>
            </div>
            <div className="roles-toolbar">
              <button className="secondary-btn" onClick={handleBackToList}>
                Back
              </button>
            </div>
          </div>
        </div>
        <div className="page-content">
          <div className="role-card">
            <div className="no-results">Please choose a process to optimize.</div>
          </div>
        </div>
      </div>
    );
  }

  // Pre-compute derived values to avoid IIFEs in JSX
  const co = optimizationData?.normalized?.costOptimization;
  const wb = optimizationData?.normalized?.workloadBalancing;
  // Job-focused workload table with pending reassignments applied to each
  // job's `assignments` — see applyOverridesToWorkloadJobs for why the
  // utilization figures themselves are left alone.
  const effectiveWorkloadJobs = applyOverridesToWorkloadJobs(
    wb?.jobs,
    manualOverrides,
    raciTaskNameById,
  );
  const redesignReport = optimizationData?.redesign_report;
  const redesignFlow = optimizationData?.redesign_flow;

  // Tasks eliminated by the ACTIVE scenario. Derived from the active report's
  // evidence, so it swaps with the scenario and every tab marks the same set.
  const eliminatedTasks = redesignReport?.evidence?.time?.eliminated_tasks || [];
  const eliminatedIds = new Set(eliminatedTasks.map((t) => Number(t.task_id)));
  const isEliminated = (taskId) => eliminatedIds.has(Number(taskId));
  const eliminatedBadge = (
    <span
      title="Removed by the active redesign scenario (activity elimination) — not part of the TO-BE"
      style={{ marginLeft: 6, fontSize: 10, fontWeight: 800, color: "#b91c1c", background: "#fee2e2", border: "1px solid #fca5a5", borderRadius: 999, padding: "1px 7px", verticalAlign: "middle" }}
    >
      eliminated in TO-BE
    </span>
  );
  // A step that is itself a sub-process (a child process embedded as a composite
  // activity). Its time & cost come from the child's OWN flow analysis.
  const subprocessBadge = (
    <span
      title="Sub-process — a child process embedded as a composite step. Its duration and cost come from the child process's own flow analysis."
      style={{ display: "inline-flex", alignItems: "center", verticalAlign: "middle", marginLeft: 6, fontSize: 10, fontWeight: 800, lineHeight: 1.5, color: "#5b21b6", background: "#ede9fe", border: "1px solid #c4b5fd", borderRadius: 999, padding: "2px 9px", whiteSpace: "nowrap" }}
    >
      Sub-Process
    </span>
  );
  // Job-focused table: which of a job's assignments arrived because of a
  // reassignment made on the task-focused table, and who it came from.
  const reassignedBadge = (fromJobName) => (
    <span
      title={fromJobName ? `Reassigned from ${fromJobName}` : "Reassigned by you"}
      style={{ display: "inline-flex", alignItems: "center", verticalAlign: "middle", marginLeft: 6, fontSize: 10, fontWeight: 800, lineHeight: 1.5, color: "#0e6b68", background: "#f0fdfa", border: "1px solid #99f6e4", borderRadius: 999, padding: "2px 9px", whiteSpace: "nowrap" }}
    >
      reassigned{fromJobName ? ` · was ${fromJobName}` : ""}
    </span>
  );
  const isSubprocessRow = (t) => !!(t && (t.is_subprocess || t.child_process_id));


  // Single source of "analysis readiness" — drives the banner AND the provisional badges,
  // so no tab can show a dependent metric (CTE, quality, VA-time) as final while its input
  // is missing. Derived from the backend flow analysis' data_quality (one source of truth).
  const readiness = (() => {
    const aq = redesignFlow?.as_is?.data_quality;
    const issues = [];
    if (aq) {
      if ((aq.tasks_missing_classification ?? 0) > 0) {
        // Sub-process steps are NOT classifiable here (they have no description to
        // decompose — their value content is classified inside their own child process),
        // and the backend already excludes them from tasks_missing_classification. So the
        // denominator must be the CLASSIFIABLE steps, not every workflow row — otherwise
        // "9 of 12" reads as 3 steps silently left behind.
        const subSteps = aq.subprocess_rows_included ?? 0;
        const classifiable = Math.max(0, (aq.task_count ?? 0) - subSteps);
        issues.push({
          key: "va",
          text:
            `${aq.tasks_missing_classification} of ${classifiable} classifiable step(s) have no value-added (VA/BVA/NVA) classification` +
            (subSteps > 0
              ? ` — the other ${subSteps} step(s) are sub-processes, classified inside their own process, so they are not counted here`
              : ""),
          impact: "Cycle-time efficiency, value-adding time and quality are provisional until classified.",
          fix: "Open the Value-Added tab and Save, or re-run Optimize to auto-classify empty steps.",
        });
      }
      if ((aq.tasks_missing_time ?? 0) > 0)
        issues.push({ key: "time", text: `${aq.tasks_missing_time} step(s) have no processing time`, impact: "Cycle time and cost are understated." });
      if ((aq.tasks_missing_responsible_job ?? 0) > 0)
        issues.push({ key: "cost", text: `${aq.tasks_missing_responsible_job} step(s) have no responsible job / rate`, impact: "Labor cost is understated." });
      if (aq.assumed_equal_split)
        issues.push({ key: "prob", text: "branch probabilities are missing (assumed an equal split)", impact: "Time, cost and CTE are estimates." });
    }
    return {
      issues,
      vaIncomplete: !!(aq && (aq.tasks_missing_classification ?? 0) > 0),
      ok: !!aq && issues.length === 0,
      hasData: !!aq,
    };
  })();
  const currentBpmnImage = activeBpmnTab === "current" ? bpmnImages.current : bpmnImages.optimized;
  const vaaSummary = valueAnalysis?.process_summary || {};
  // The value analysis only ever covers REAL tasks — a sub-process has no description to
  // decompose into VA/BVA/NVA steps. Merge the sub-process steps back in (from the flow
  // analysis, which knows their workflow order) as read-only rows, so this tab shows the
  // whole process and states WHY those steps are not classified here.
  const vaaTasks = (() => {
    const rows = Array.isArray(valueAnalysis?.task_classifications) ? valueAnalysis.task_classifications : [];
    if (!rows.length) return rows;
    if (rows.some((r) => r._is_subprocess)) return rows; // fallback path already merged them
    const flowTasks = redesignFlow?.as_is?.tasks || [];
    const subs = flowTasks
      .filter((t) => t.is_subprocess)
      .map((t) => ({
        task_id: t.task_id,
        task_name: t.task_name,
        dominant_category: t.value_classification || "",
        action_recommendation: "",
        jobs: [],
        steps: [],
        _is_subprocess: true,
        _order: t.order,
        _child_process_id: t.child_process_id,
      }));
    if (!subs.length) return rows;
    const orderOf = new Map(flowTasks.map((t) => [String(t.task_id), t.order]));
    return [...rows.map((r) => ({ ...r, _order: r._order ?? orderOf.get(String(r.task_id)) })), ...subs]
      .sort((a, b) => (a._order ?? 1e9) - (b._order ?? 1e9));
  })();

  return (
    <div className="page-container">
      <div className="po-sticky-header">
        <div className="po-header-meta-card">
          <div className="po-header-top-row">
            <div>
              <h2 className="po-process-title">
                {optimizationData?.process_name || "Process Optimization"}
              </h2>
              {optimizationData && (
                <div className="po-scenario-badge" style={{ marginTop: 6 }}>
                  {scenarioSectionHeading}: {scenarioTypeLabel}
                </div>
              )}
            </div>
            <div className="po-header-actions">
              {optimizationData && (
                <div className="po-currency-control">
                  <span className="po-currency-label">Currency</span>
                  <CurrencySelector
                    value={displayCurrency}
                    onChange={setDisplayCurrency}
                    onRefresh={onRefreshRates}
                    refreshing={refreshingRates}
                  />
                </div>
              )}
              <button className="secondary-btn sm" onClick={handleBackToList}>
                Back
              </button>
              {optimizationData && (
                <button
                  className="primary-btn sm"
                  onClick={requestSave}
                  // Same block as the Save tab's own button: this header shortcut
                  // POSTs the same payload, so it cannot stay live while that
                  // payload disagrees with the heuristic selection.
                  disabled={!selectedId || saving || engineSelection.saveBlocked}
                  title={engineSelection.saveBlockedReason || undefined}
                  style={{
                    background: "linear-gradient(135deg, #0e6b68 0%, #0a4f4d 100%)",
                    color: "white",
                    border: "none",
                    boxShadow: "0 4px 12px rgba(10, 79, 77, 0.22)",
                  }}
                >
                  {saving ? "Saving…" : "Save"}
                </button>
              )}
            </div>
          </div>
        </div>

        {optimizationData && (
          <div className="po-tabs-navbar">
            {[
              { key: "overview", label: "Overview", show: true },
              { key: "analyze", label: "Analyze", show: true },
              { key: "value", label: "Value-Added", show: true },
              { key: "flow", label: "Flow & Bottlenecks", show: !!redesignReport },
              { key: "redesign", label: "Redesign", show: !!redesignReport },
              { key: "before-after", label: "AS-IS vs TO-BE", show: !!redesignReport },
              { key: "comparison", label: "Comparison Report", show: !!optimizationData },
              { key: "bpmn", label: "BPMN", show: true },
              { key: "save", label: "Save", show: !!optimizationData },
            ]
              .filter((t) => t.show)
              .map((t, i) => (
                <button
                  key={t.key}
                  type="button"
                  className={`po-tab-trigger ${activeTab === t.key ? "active" : ""}`}
                  onClick={() => setActiveTab(t.key)}
                >
                  <span className="po-tab-step" aria-hidden="true">{i + 1}</span>
                  {t.label}
                </button>
              ))}
          </div>
        )}
      </div>

      <div className="page-content">
        {skillCheck?.has_mismatches && (
          <div style={{ margin: "0 0 14px", padding: "10px 14px", background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 10, color: "#92400e", fontSize: 13 }}>
            <strong>⚠ Skill mismatch on {skillCheck.mismatch_count} task{skillCheck.mismatch_count === 1 ? "" : "s"}:</strong>{" "}
            {skillCheck.tasks
              .filter((t) => t.missing_skills.length > 0)
              .slice(0, 4)
              .map((t) => `${t.task_name} → ${t.job_name || "unassigned"} (missing: ${t.missing_skills.join(", ")})`)
              .join("; ")}
            {skillCheck.mismatch_count > 4 ? ` …and ${skillCheck.mismatch_count - 4} more` : ""}
            <div style={{ fontSize: 11, color: "#b45309", marginTop: 4 }}>
              Optimization is not blocked — this is an advisory signal.
            </div>
          </div>
        )}
        {readiness.issues.length > 0 && (
          <div style={{ margin: "0 0 14px", padding: "12px 16px", background: "#fef6e7", border: "1px solid #f6d98a", borderRadius: 12, color: "#7a4d09" }}>
            <div style={{ fontSize: 13.5, fontWeight: 700, marginBottom: 6 }}>
              ⚠ Analysis incomplete — the results below are provisional
            </div>
            <ul style={{ margin: "0 0 0 18px", padding: 0, fontSize: 12.5, lineHeight: 1.5 }}>
              {readiness.issues.map((it) => (
                <li key={it.key} style={{ marginBottom: 3 }}>
                  <strong>{it.text}.</strong> {it.impact}
                  {it.fix ? <span style={{ color: "#a16207" }}> {it.fix}</span> : null}
                </li>
              ))}
            </ul>
          </div>
        )}
        {optimizationData && (() => {
          // LLM-step outcomes (nothing may degrade silently) + data-staleness + goal control.
          const llm = optimizationData.llm_status || {};
          const llmIssues = Object.entries(llm).filter(
            ([, v]) => v && ["failed", "fallback"].includes(String(v.status)),
          );
          const activeGoal = optimizationData.goal;
          return (
            <>
              {llmIssues.length > 0 && (
                <div style={{ margin: "0 0 14px", padding: "10px 14px", background: "#fef2f2", border: "1px solid #fca5a5", borderRadius: 10, color: "#991b1b", fontSize: 12.5 }}>
                  <strong>⚠ AI step {llmIssues.some(([, v]) => v.status === "failed") ? "failed" : "degraded"}:</strong>{" "}
                  {llmIssues.map(([k, v]) => `${k.replace(/_/g, " ")} — ${v.detail || v.status}`).join("; ")}
                  <div style={{ fontSize: 11, color: "#b91c1c", marginTop: 3 }}>
                    Deterministic (measured) results are unaffected; only the AI-assisted parts above are impacted.
                  </div>
                </div>
              )}
              {vaStale && (
                <div style={{ margin: "0 0 14px", padding: "10px 14px", background: "#fffbeb", border: "1px solid #f59e0b", borderRadius: 10, color: "#92400e", fontSize: 12.5, display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                  <span><strong>⟳ Classifications changed.</strong> Flow, CTE, quality and the Devil's Quadrangle were computed from the previous values — re-run the analysis to propagate your change through every tab.</span>
                  <button
                    type="button"
                    className="primary-btn sm"
                    disabled={optimizing}
                    onClick={() => { setVaStale(false); optimize(selectedId, GOAL_PRESETS[goalKey].weights, { includeSubprocessRedesigns: agentIncludeSubprocess }); }}
                  >
                    {optimizing ? "Re-running…" : "Re-run analysis"}
                  </button>
                </div>
              )}
              <div style={{ margin: "0 0 14px", padding: "8px 14px", background: "#fcfcfd", border: "1px solid #eef2f7", borderRadius: 10, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", fontSize: 12.5 }}>
                <span style={{ fontWeight: 700, color: "#374151" }}>Optimization goal</span>
                <select value={goalKey} onChange={(e) => setGoalKey(e.target.value)} style={{ fontSize: 12.5, padding: "3px 8px", border: "1px solid #d1d5db", borderRadius: 8 }}>
                  {Object.entries(GOAL_PRESETS).map(([k, g]) => (<option key={k} value={k}>{g.label}</option>))}
                </select>
                <button
                  type="button"
                  className="secondary-btn sm"
                  disabled={optimizing}
                  onClick={() => optimize(selectedId, GOAL_PRESETS[goalKey].weights, { includeSubprocessRedesigns: agentIncludeSubprocess })}
                  title="Re-run the optimization ranking scenarios against this goal's Devil's-Quadrangle weights"
                >
                  {optimizing ? "Running…" : "Re-run with this goal"}
                </button>
                {/* Same opt-in the RL and BPR panels carry. It only takes effect on
                    the next run, so it sits next to the button that starts one. */}
                <label
                  style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", color: "#374151" }}
                  title="Also redesign each sub-process and report what was applied inside it. This process's own KPIs are unchanged — the sub-process figures are reported separately, on the Redesign tab."
                >
                  <input
                    type="checkbox"
                    checked={agentIncludeSubprocess}
                    disabled={optimizing}
                    onChange={(e) => setAgentIncludeSubprocess(e.target.checked)}
                  />
                  <span>Include sub-processes</span>
                  <span style={{ fontSize: 11, color: "#9ca3af" }}>one extra run each · slower</span>
                </label>
                {activeGoal?.weights && (
                  <span style={{ color: "#6b7280", fontSize: 11.5 }}>
                    Current run: {activeGoal.source === "user" ? "your goal" : "default"} — weights t{activeGoal.weights.time} · c{activeGoal.weights.cost} · q{activeGoal.weights.quality} · f{activeGoal.weights.flexibility}
                  </span>
                )}
              </div>
            </>
          );
        })()}
        {readiness.ok && (
          <div style={{ margin: "0 0 14px", padding: "8px 14px", background: "#e8f5ee", border: "1px solid #b6e0c6", borderRadius: 12, color: "#1c6b43", fontSize: 12.5, fontWeight: 600 }}>
            ✓ Analysis complete — every step is classified and priced, so all metrics below are final.
          </div>
        )}
        {optimizing && !optimizationData && (
          <div className="role-card">
            <div className="no-results">Optimizing process...</div>
          </div>
        )}

        {optimizationData && (
          <div className="po-tab-content-container">
            {/* 1) Overview Tab */}
            {activeTab === "overview" && (
              <div className="po-tab-pane">
                <div className="role-card">
                  <div className="section-title">AS-IS Baseline — measured current state</div>
                  <p style={{ fontSize: 12.5, color: "#6b7280", margin: "6px 0 14px" }}>
                    The redesign works in steps. This page measures where the process stands today
                    (FBPM Ch.2/6/7); the redesign verdict — what changes and what it achieves — is in
                    <b> AS-IS vs TO-BE</b> and finalized in <b>Save</b>.
                  </p>
                  {(() => {
                    const asF = optimizationData?.redesign_flow?.as_is || {};
                    const baseTiles = [
                      { label: "Cycle time", value: asF.cycle_time_minutes != null ? fmtMin(asF.cycle_time_minutes) : `${Number(optimizationData.current_state?.total_time_hours ?? 0).toFixed(2)} hrs`, c: "#0e6b68" },
                      { label: "Labor cost / case", value: asF.labor_cost_per_case != null ? money(asF.labor_cost_per_case) : money(optimizationData.current_state?.total_cost ?? 0), c: "#1d4ed8" },
                      { label: "Cycle-time efficiency", value: asF.cycle_time_efficiency_percent != null ? `${num2(asF.cycle_time_efficiency_percent)}%` : "—", c: "#b45309" },
                      { label: "Processing / waiting / rework", value: asF.processing_time_minutes != null ? `${num2(asF.processing_time_minutes)} / ${num2(asF.waiting_time_minutes ?? 0)} / ${num2(asF.rework_time_minutes ?? 0)} min` : "—", c: "#475569" },
                      { label: "Tasks", value: optimizationData.current_state?.task_count ?? ((asF.tasks || []).length || "—"), c: "#7c3aed" },
                    ];
                    return (
                      <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                        {baseTiles.map((m) => (
                          <div key={m.label} style={{ flex: "1 1 150px", border: `1px solid ${m.c}22`, borderLeft: `4px solid ${m.c}`, borderRadius: 10, padding: "12px 16px", background: "#fff" }}>
                            <div style={{ fontSize: 11, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.05em" }}>{m.label}</div>
                            <div style={{ fontSize: 19, fontWeight: 800, color: m.c, marginTop: 4, fontVariantNumeric: "tabular-nums" }}>{m.value}</div>
                          </div>
                        ))}
                      </div>
                    );
                  })()}

                  {(() => {
                    // Overall process cost + the ACTIVE scenario's outcome — kept in sync
                    // with every other tab because it reads the same redesign_flow/report.
                    const asF2 = redesignFlow?.as_is || {};
                    const tbF2 = redesignFlow?.to_be || {};
                    const raciTotal = optimizationData?.raci_assignments?.matrix?.total_process_cost_usd;
                    const raciTotalToBe = optimizationData?.raci_assignments?.matrix?.to_be_total_process_cost_usd;
                    const staffingChanged = raciTotal != null && raciTotalToBe != null && raciTotalToBe !== raciTotal;
                    if (asF2.labor_cost_per_case == null && raciTotal == null) return null;
                    const changed = tbF2.cycle_time_minutes != null && asF2.cycle_time_minutes != null && tbF2.cycle_time_minutes !== asF2.cycle_time_minutes;
                    const costChanged = tbF2.labor_cost_per_case != null && asF2.labor_cost_per_case != null && tbF2.labor_cost_per_case !== asF2.labor_cost_per_case;
                    return (
                      <div style={{ marginTop: 16, borderTop: "1px dashed #e5e7eb", paddingTop: 12 }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: "#6b7280", marginBottom: 8 }}>
                          OVERALL PROCESS COST &amp; ACTIVE REDESIGN OUTCOME
                          {eliminatedTasks.length > 0 && <span style={{ fontWeight: 400 }}> · the active scenario eliminates {eliminatedTasks.length} step(s)</span>}
                        </div>
                        <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                          <div style={{ flex: "1 1 200px", border: "1px solid #1d4ed822", borderLeft: "4px solid #1d4ed8", borderRadius: 10, padding: "12px 16px", background: "#fff" }}>
                            <div style={{ fontSize: 11, fontWeight: 700, color: "#6b7280", textTransform: "uppercase" }}>Labor cost / case</div>
                            <div style={{ fontSize: 18, fontWeight: 800, color: "#1d4ed8", marginTop: 4, fontVariantNumeric: "tabular-nums" }}>
                              {money(asF2.labor_cost_per_case ?? 0)}
                              {costChanged && <span style={{ color: "#0d9488" }}> → {money(tbF2.labor_cost_per_case)}</span>}
                            </div>
                            {(() => {
                              // WHY the headline is what it is: split the Ch.7 expected cost into
                              // this process's own tasks vs the work embedded in sub-processes, so
                              // the figure reconciles at a glance with the task tables (which list
                              // own tasks) and the staffing tile.
                              const rows = Array.isArray(asF2.tasks) ? asF2.tasks : [];
                              if (!rows.length) return null;
                              let own = 0;
                              let sub = 0;
                              let subCount = 0;
                              for (const r of rows) {
                                const c = Number(r.expected_cost_usd || 0);
                                if (r.is_subprocess) { sub += c; subCount += 1; } else { own += c; }
                              }
                              if (subCount === 0) return null;
                              return (
                                <div style={{ fontSize: 11.5, color: "#1e40af", marginTop: 4, fontVariantNumeric: "tabular-nums" }}>
                                  = {money(own)} own tasks + {money(sub)} inside {subCount} sub-process{subCount === 1 ? "" : "es"}
                                </div>
                              );
                            })()}
                            <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 2 }}>{costChanged ? "AS-IS → TO-BE (active scenario)" : "AS-IS — the active scenario does not change it"}</div>
                          </div>
                          <div style={{ flex: "1 1 200px", border: "1px solid #0e6b6822", borderLeft: "4px solid #0e6b68", borderRadius: 10, padding: "12px 16px", background: "#fff" }}>
                            <div style={{ fontSize: 11, fontWeight: 700, color: "#6b7280", textTransform: "uppercase" }}>Cycle time</div>
                            <div style={{ fontSize: 18, fontWeight: 800, color: "#0e6b68", marginTop: 4, fontVariantNumeric: "tabular-nums" }}>
                              {asF2.cycle_time_minutes != null ? fmtMin(asF2.cycle_time_minutes) : "—"}
                              {changed && <span style={{ color: "#0d9488" }}> → {fmtMin(tbF2.cycle_time_minutes)}</span>}
                              {asF2.simulation?.cycle_time_minutes?.p90 != null && (
                                <div style={{ fontSize: 11.5, fontWeight: 600, color: "#0e6b68", marginTop: 3 }}
                                  title="Monte-Carlo over the enumerated paths (10,000 draws, ±20% triangular around each path's expected values — stated assumption). 90% of cases finish within this time.">
                                  P90 (simulated): {fmtMin(asF2.simulation.cycle_time_minutes.p90)}
                                  {tbF2.simulation?.cycle_time_minutes?.p90 != null && tbF2.simulation.cycle_time_minutes.p90 !== asF2.simulation.cycle_time_minutes.p90 && (
                                    <span style={{ color: "#0d9488" }}> → {fmtMin(tbF2.simulation.cycle_time_minutes.p90)}</span>
                                  )}
                                </div>
                              )}
                            </div>
                            <div style={{ fontSize: 11, color: "#9ca3af" }}>{changed ? "AS-IS → TO-BE (active scenario)" : "AS-IS — the active scenario does not change it"}</div>
                          </div>
                          {raciTotal != null && (() => {
                            const matrix2 = optimizationData?.raci_assignments?.matrix || {};
                            const compositeCost = Number(matrix2.subprocess_composite_cost_usd || 0);
                            const spCount = Number(matrix2.subprocess_count || 0);
                            const allStepsAsIs = raciTotal + compositeCost;
                            // Sub-process staffing is part of what the process truly costs —
                            // the HEADLINE is the all-steps figure; the line under it shows
                            // the composition. The TO-BE arrow stays on the same basis
                            // (reassigned/eliminated roles + the same composites).
                            const allStepsToBe = raciTotalToBe != null ? raciTotalToBe + compositeCost : null;
                            // `staffingStale` (hoisted): reassignments are in effect but
                            // this matrix was not re-scored with them, because the
                            // what-if returned no staffing block. The cost/case and
                            // cycle-time tiles beside this one DID move, so an arrow
                            // here would read as agreeing with them when it is really
                            // the pre-change figure.
                            return (
                              <div style={{ flex: "1 1 240px", border: "1px solid #7c3aed22", borderLeft: "4px solid #7c3aed", borderRadius: 10, padding: "12px 16px", background: "#fff" }}>
                                <div style={{ fontSize: 11, fontWeight: 700, color: "#6b7280", textTransform: "uppercase" }}>Total staffing cost (all RACI roles, all steps)</div>
                                <div style={{ fontSize: 18, fontWeight: 800, color: "#7c3aed", marginTop: 4, fontVariantNumeric: "tabular-nums" }}>
                                  {money(allStepsAsIs)}
                                  {!staffingStale && staffingChanged && allStepsToBe != null && <span style={{ color: "#0d9488" }}> → {money(allStepsToBe)}</span>}
                                  {staffingStale && (
                                    <span
                                      title="This total comes from the optimization's RACI matrix, which the what-if re-score did not return. Cost per case (left) does reflect your reassignments; this accounting total does not until the next full Optimize run."
                                      style={{ marginLeft: 8, fontSize: 10, fontWeight: 800, verticalAlign: "middle", color: "#b45309", background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 999, padding: "2px 8px", whiteSpace: "nowrap" }}
                                    >
                                      ⚠ not re-scored
                                    </span>
                                  )}
                                </div>
                                {/* Same composition treatment as the Labor cost / case tile. */}
                                {compositeCost > 0 && (
                                  <div style={{ fontSize: 11.5, color: "#6d28d9", marginTop: 4, fontVariantNumeric: "tabular-nums" }}>
                                    = {money(raciTotal)} assigned R/A/C/I roles + {money(compositeCost)} inside {spCount} sub-process{spCount === 1 ? "" : "es"}
                                  </div>
                                )}
                                <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 2 }}>
                                  {staffingStale
                                    ? "R+A+C+I attributed effort per case — shown AS-IS. Your current reassignment selection is reflected in cost per case (left), but this accounting total still describes the optimization's original staffing plan; only a full Optimize run recomputes it."
                                    : staffingChanged
                                    ? `R+A+C+I attributed effort per case (CMS-stored AS-IS staffing) — the TO-BE reflects the accepted reassignment(s)${eliminatedTasks.length > 0 ? ` and excludes the ${eliminatedTasks.length} eliminated step(s)` : ""}`
                                    : raciTotalToBe == null && eliminatedTasks.length > 0
                                      ? "R+A+C+I attributed effort per case — accounting view of the AS-IS staffing (eliminated steps still included; re-run the optimization to refresh this figure)"
                                      : "R+A+C+I attributed effort per case — unchanged: the active scenario does not remove or reassign attributed effort"}
                                </div>
                              </div>
                            );
                          })()}
                        </div>

                        {/* ── WHAT CHANGES THE AS-IS & WHERE TO REVIEW IT ──
                            Every driver of the active TO-BE names the tab that owns its
                            details, with a real button to go there — the analyst never has
                            to guess which tab explains a moved number. */}
                        {(() => {
                          const drivers = [];
                          const swaps = Array.isArray(co?.replacements) ? co.replacements.length : 0;
                          const sel = redesignReport?.user_selection;
                          // A hand-picked reassignment drives the cost change just as
                          // much as an agent-proposed one, so this row must appear for
                          // it too — including on a process where the agent proposed
                          // nothing at all (swaps === 0).
                          const manualCount = Object.keys(manualOverrides).length;
                          if (swaps > 0 || manualCount > 0) {
                            drivers.push({
                              key: "reassign",
                              icon: "⇄",
                              color: { fg: "#1d4ed8", bg: "#eff6ff", border: "#bfdbfe" },
                              text:
                                manualCount > 0
                                  ? `${pendingOverrides.length} job reassignment(s) in effect (${manualCount} set by you) — they drive the cost change`
                                  : sel
                                    ? `${sel.applied} of ${sel.total} job reassignment(s) accepted — they drive the cost change`
                                    : `${swaps} cheaper-job reassignment(s) drive the cost change`,
                              go: [{ label: "Review in Analyze", tab: "analyze" }],
                            });
                          }
                          if (eliminatedTasks.length > 0) {
                            drivers.push({
                              key: "eliminate",
                              icon: "✂",
                              color: { fg: "#b91c1c", bg: "#fef2f2", border: "#fecaca" },
                              text: `${eliminatedTasks.length} step(s) eliminated (classified NVA) — they drive the time/cost change`,
                              go: [
                                { label: "Classifications in Value-Added", tab: "value" },
                                { label: "Impact in AS-IS vs TO-BE", tab: "before-after" },
                              ],
                            });
                          }
                          const gwChanges = redesignReport?.evidence?.time?.gateway_changes;
                          if (Array.isArray(gwChanges) && gwChanges.length > 0) {
                            drivers.push({
                              key: "structure",
                              icon: "⑃",
                              color: { fg: "#0e6b68", bg: "#f0fdfa", border: "#99f6e4" },
                              text: `${gwChanges.length} structure change(s) (gateways/parallelism) re-sequence the flow`,
                              go: [{ label: "See the BPMN diagrams", tab: "bpmn" }],
                            });
                          }
                          if (!drivers.length) return null;
                          return (
                            <div style={{ marginTop: 12, padding: "10px 14px", background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 10 }}>
                              <div style={{ fontSize: 11, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8 }}>
                                What changes the AS-IS — and where to review it
                              </div>
                              <div style={{ display: "grid", gap: 8 }}>
                                {drivers.map((d) => (
                                  <div key={d.key} style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                                    <span aria-hidden="true" style={{ width: 24, height: 24, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 800, color: d.color.fg, background: d.color.bg, border: `1px solid ${d.color.border}`, borderRadius: 999 }}>
                                      {d.icon}
                                    </span>
                                    <span style={{ fontSize: 12.5, color: "#334155", flex: "1 1 260px" }}>{d.text}</span>
                                    <span style={{ display: "inline-flex", gap: 6, flexWrap: "wrap" }}>
                                      {d.go.map((g) => (
                                        <button
                                          key={g.tab + g.label}
                                          type="button"
                                          className="secondary-btn sm"
                                          onClick={() => setActiveTab(g.tab)}
                                          aria-label={`${g.label} — opens the ${g.label.split(" ").pop()} tab`}
                                        >
                                          {g.label} →
                                        </button>
                                      ))}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          );
                        })()}
                      </div>
                    );
                  })()}
                </div>

                <div className="role-card" style={{ marginTop: 20 }}>
                  <div className="section-title" style={{ marginBottom: 4, fontWeight: 600 }}>
                    RACI Assignment
                  </div>
                  {raciTaskGroups.length === 0 ? (
                    <div className="no-results">No RACI assignments available.</div>
                  ) : (
                    <div className="raci-flat-wrap">
                      <table className="raci-flat-table">
                        <thead>
                          <tr>
                            <th className="raci-flat-th-task">Task Name</th>
                            <th className="raci-flat-th-job">Job Name</th>
                            {[
                              { role: "R", label: "Responsible" },
                              { role: "A", label: "Accountable" },
                              { role: "C", label: "Consulted" },
                              { role: "I", label: "Informed" },
                            ].map(({ role, label }) => (
                              <th key={role} className="raci-flat-th-role">
                                <div className="raci-flat-role-header">
                                  <span className={`raci-legend-badge raci-${role.toLowerCase()}`}>
                                    {role}
                                  </span>
                                  <span className="raci-flat-role-label">{label}</span>
                                </div>
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {raciTaskGroups.map((tg, tgIdx) => {
                            const jobRows = tg.jobs.length > 0 ? tg.jobs : [null];
                            const isAlt = tgIdx % 2 !== 0;
                            const sub = isSubprocessRow(tg);
                            const open = !!expandedSubprocess[tg.task_id];
                            const rows = jobRows.map((job, jIdx) => (
                              <tr
                                key={job ? `${tg.task_id}-${job.job_id}-${job.role}` : `empty-${tg.task_id}`}
                                className={`raci-flat-row${isAlt ? " raci-flat-row-alt" : ""}${jIdx === 0 ? " raci-flat-row-first" : ""}`}
                              >
                                {jIdx === 0 && (
                                  <td rowSpan={jobRows.length} className="raci-flat-task-cell">
                                    <div className="raci-flat-task-inner">
                                      <span className="raci-flat-step">{tg.order}</span>
                                      <div className="raci-flat-task-text">
                                        <span className="raci-flat-task-name" style={isEliminated(tg.task_id) ? { textDecoration: "line-through", color: "#9ca3af" } : undefined}>
                                          {tg.task_name}
                                        </span>
                                        {sub && subprocessBadge}
                                        {sub && <SubprocessToggle id={tg.task_id} open={open} />}
                                        {isEliminated(tg.task_id) && eliminatedBadge}
                                        {sub && tg.composite_cost_usd != null && (
                                          <span style={{ display: "block", fontSize: 11, color: "#6b7280", marginTop: 2 }}>
                                            Composite: {money(tg.composite_cost_usd)}
                                            {tg.composite_minutes != null ? ` · ${Number(tg.composite_minutes).toFixed(0)} min` : ""}
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  </td>
                                )}
                                {job ? (
                                  <>
                                    <td className="raci-flat-job-name">
                                      {job.name}
                                      {/* This table is the measured AS-IS staffing, so the
                                          job itself is not swapped here — but leaving a
                                          pending reassignment invisible would contradict the
                                          Analyze tab, so the row states where it is heading. */}
                                      {(() => {
                                        const ov = raciOverrideFor(tg.task_id, job.role, job.job_id);
                                        if (!ov) return null;
                                        return (
                                          <span
                                            className="po-raci-tobe-chip"
                                            title="You reassigned this role on the Analyze tab. The TO-BE numbers already reflect it; saving records it in the new version."
                                          >
                                            → {ov.new_job_name} in TO-BE
                                          </span>
                                        );
                                      })()}
                                    </td>
                                    {["R", "A", "C", "I"].map((role) => (
                                      <td key={role} className="raci-flat-role-cell">
                                        {job.role === role ? (
                                          <span className={`raci-badge raci-${role.toLowerCase()}`}>
                                            {role}
                                          </span>
                                        ) : (
                                          <span className="raci-summary-empty">—</span>
                                        )}
                                      </td>
                                    ))}
                                  </>
                                ) : (
                                  <td colSpan={5} className="raci-flat-no-jobs">
                                    {sub
                                      ? "Sub-process — staffed inside its own process. Use “View RACI” to see who does this work."
                                      : "No jobs assigned to this task"}
                                  </td>
                                )}
                              </tr>
                            ));
                            if (sub && open) rows.push(<SubprocessRaciDetail key={`${tg.task_id}-detail`} row={tg} colSpan={6} />);
                            return rows;
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                  <div className="raci-legend" style={{ marginTop: 20 }}>
                    {[
                      { role: "R", label: "Responsible", desc: "does the work" },
                      { role: "A", label: "Accountable", desc: "owns outcome" },
                      { role: "C", label: "Consulted", desc: "input needed" },
                      { role: "I", label: "Informed", desc: "kept updated" },
                    ].map(({ role, label, desc }) => (
                      <div className="raci-legend-item" key={role}>
                        <span className={`raci-legend-badge raci-${role.toLowerCase()}`}>{role}</span>
                        {label} &mdash; {desc}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* 1b) Before/After (Devil's Quadrangle) Tab */}
            {["before-after", "flow", "redesign"].includes(activeTab) && redesignReport && (() => {
              const d = redesignReport.dimensions || {};
              const fmt = (x) =>
                x === null || x === undefined
                  ? "—"
                  : Number(x).toLocaleString(undefined, { maximumFractionDigits: 2 });
              const quant = [d.time, d.cost].filter(Boolean);
              const rowColor = (imp) => (imp == null ? "#6b7280" : imp ? "#059669" : "#dc2626");
              const arrow = (imp) => (imp == null ? "" : imp ? "▲" : "▼");
              const asF = redesignFlow?.as_is || {};
              const tbF = redesignFlow?.to_be || {};
              const pctChange = (a, b) =>
                a == null || b == null || a === 0
                  ? null
                  : Number((((b - a) / Math.abs(a)) * 100).toFixed(1));
              const prov = readiness.vaIncomplete ? " · provisional" : "";
              const flowRows = [];
              if (asF.cycle_time_minutes != null || tbF.cycle_time_minutes != null) {
                const a = asF.cycle_time_minutes, b = tbF.cycle_time_minutes;
                flowRows.push({ label: "Cycle time (min)", isTime: true, as_is: a, to_be: b, improved: a != null && b != null ? b < a : null, percent_change: pctChange(a, b) });
              }
              if (asF.cycle_time_efficiency_percent != null || tbF.cycle_time_efficiency_percent != null) {
                const a = asF.cycle_time_efficiency_percent, b = tbF.cycle_time_efficiency_percent;
                flowRows.push({ label: "Cycle-time efficiency (%)" + prov, isPercent: true, as_is: a, to_be: b, improved: a != null && b != null ? b > a : null, percent_change: pctChange(a, b) });
              }
              if (asF.value_adding_time_minutes != null || tbF.value_adding_time_minutes != null) {
                flowRows.push({ label: "Value-adding time (min)" + prov, isTime: true, as_is: asF.value_adding_time_minutes, to_be: tbF.value_adding_time_minutes, improved: null, percent_change: pctChange(asF.value_adding_time_minutes, tbF.value_adding_time_minutes) });
              }
              const flowFlags = [...new Set([...(asF.flags || []), ...(tbF.flags || [])])];
              const qHead = (
                <thead><tr>{["Dimension", "AS-IS", "TO-BE", "Change"].map((h, i) => (
                  <th key={h} style={{ padding: "8px 10px", fontSize: 11, color: "#6b7280", textAlign: i === 0 ? "left" : "right", borderBottom: "1px solid #e5e7eb" }}>{h}</th>
                ))}</tr></thead>
              );
              const numRow = (dim) => {
                // Time values past the hour mark carry their hour equivalent too.
                const isMinutes = String(dim.unit || "").toLowerCase().startsWith("minute");
                const cell = (v) => {
                  if ((isMinutes || dim.isTime) && v != null) return fmtMin(v);
                  if (dim.isPercent && v != null) return `${fmt(v)}%`;
                  return fmt(v);
                };
                return (
                  <tr key={dim.label}>
                    <td style={{ padding: "8px 10px", fontSize: 13, fontWeight: 600 }}>{dim.label}{dim.unit ? ` (${dim.unit})` : ""}</td>
                    <td style={{ padding: "8px 10px", fontSize: 13, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{cell(dim.as_is)}</td>
                    <td style={{ padding: "8px 10px", fontSize: 13, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{cell(dim.to_be)}</td>
                    <td style={{ padding: "8px 10px", fontSize: 13, textAlign: "right", fontWeight: 700, color: rowColor(dim.percent_change === 0 ? null : dim.improved), whiteSpace: "nowrap" }}>{dim.percent_change == null ? "—" : `${arrow(dim.percent_change === 0 ? null : dim.improved)} ${Math.abs(dim.percent_change)}%`}</td>
                  </tr>
                );
              };
              return (
                <div className="po-tab-pane">
                  {activeTab === "before-after" && (
                  <div className="role-card">
                    <div className="section-title">
                      AS-IS vs TO-BE — Devil&apos;s Quadrangle
                      {beforeAfterEngine !== "agent" && ` (${beforeAfterEngine === "rl" ? "RL Agent" : "BPR Model"})`}
                    </div>

                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
                      <span style={{ fontSize: 11, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.05em" }}>Engine</span>
                      {[
                        { key: "agent", label: "Agent" },
                        { key: "rl", label: "RL" },
                        { key: "bpr", label: "Model" },
                      ].map((e) => (
                        <button
                          key={e.key}
                          type="button"
                          className={`opt-tab-btn ${beforeAfterEngine === e.key ? "active" : ""}`}
                          style={{ padding: "4px 14px", fontSize: 12 }}
                          aria-pressed={beforeAfterEngine === e.key}
                          onClick={() => setBeforeAfterEngine(e.key)}
                        >
                          {e.label}
                        </button>
                      ))}
                    </div>

                    {beforeAfterEngine !== "agent" && (
                      <BeforeAfterEnginePanel
                        engine={beforeAfterEngine}
                        processId={selectedId}
                        money={money}
                        fmtMin={fmtMin}
                        rl={rl}
                        bpr={bpr}
                        goal={beforeAfterGoal}
                        setGoal={setBeforeAfterGoal}
                        includeSubprocess={beforeAfterIncludeSubprocess}
                        setIncludeSubprocess={setBeforeAfterIncludeSubprocess}
                      />
                    )}

                    {beforeAfterEngine === "agent" && (<>
                    {Array.isArray(optimizationData?.scenarios) && optimizationData.scenarios.length > 1 && (
                      <div style={{ margin: "10px 0 16px" }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: "#6b7280", marginBottom: 6 }}>
                          RANKED SCENARIOS — scored against your goal's Devil's-Quadrangle weights. Selecting one switches every tab to that candidate.
                        </div>
                        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                          {optimizationData.scenarios.map((sc) => {
                            const imp = sc.improvements || {};
                            const pct = (v) => `${v > 0 ? "−" : v < 0 ? "+" : ""}${Math.abs(v * 100).toFixed(1)}%`;
                            const pctUp = (v) => `${v > 0 ? "+" : ""}${(v * 100).toFixed(1)}`;
                            return (
                              <div key={sc.id} style={{
                                flex: "1 1 220px", minWidth: 210, borderRadius: 10, padding: "10px 14px",
                                border: sc.is_active ? "2px solid #0d9488" : "1px solid #eef2f7",
                                background: sc.is_active ? "#f0fdfa" : "#fff",
                              }}>
                                <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
                                  <span style={{ fontSize: 11, fontWeight: 800, color: "#9ca3af" }}>#{sc.rank}</span>
                                  <span style={{ fontSize: 13, fontWeight: 700 }}>{sc.name}</span>
                                  {sc.is_active && <span style={{ marginLeft: "auto", fontSize: 10, fontWeight: 800, color: "#0d9488", background: "#ccfbf1", borderRadius: 999, padding: "1px 8px" }}>ACTIVE</span>}
                                </div>
                                <div style={{ fontSize: 11.5, color: "#6b7280", margin: "3px 0 6px" }}>{sc.description}</div>
                                {sc.user_modified && (
                                  <div
                                    title="You changed the staffing of this candidate, so these percentages come from the backend re-score of YOUR version of it — not from the original ranked candidate."
                                    style={{ display: "inline-block", marginBottom: 5, fontSize: 10, fontWeight: 800, color: "#1d4ed8", background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 999, padding: "1px 8px" }}
                                  >
                                    ⇄ modified by you
                                  </div>
                                )}
                                <div style={{ fontSize: 11.5, color: "#374151", fontVariantNumeric: "tabular-nums" }}>
                                  time {pct(imp.time || 0)} · cost {pct(imp.cost || 0)} · quality {pctUp(imp.quality || 0)} · flex {pctUp(imp.flexibility || 0)}
                                  {/* The score is the backend's goal-weighted RANKING of the
                                      original candidate. Once the analyst has altered the
                                      staffing it no longer describes what is on screen, and
                                      recomputing it here would invent a ranking the engine
                                      never produced — so it is withdrawn, not guessed. */}
                                  {sc.user_modified ? (
                                    <span title="Ranking score applies to the original candidate; re-run Optimize to re-rank with your change." style={{ marginLeft: 8, fontWeight: 700, color: "#9ca3af" }}>
                                      score n/a
                                    </span>
                                  ) : (
                                    <span style={{ marginLeft: 8, fontWeight: 700, color: "#0e6b68" }}>score {num2(sc.score)}</span>
                                  )}
                                </div>
                                {!sc.is_active && (
                                  <button type="button" className="secondary-btn sm" style={{ marginTop: 6, padding: "1px 10px", fontSize: 11 }} onClick={() => selectScenario(sc)}>
                                    View this scenario
                                  </button>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    <p style={{ fontSize: 13, color: "#374151", margin: "6px 0 14px" }}>
                      {redesignReport.summary}
                    </p>
                    <div style={{ overflowX: "auto" }}>
                      <table style={{ borderCollapse: "collapse", width: "100%", minWidth: 480 }}>
                        {qHead}
                        <tbody>
                          {quant.map(numRow)}
                          {[
                            { dim: d.quality, key: "quality" },
                            { dim: d.flexibility, key: "flexibility" },
                          ].filter((x) => x.dim).map(({ dim, key }) => {
                            const asRaw = qfOverrides[`${key}_as_is`] ?? dim.as_is;
                            const tbRaw = qfOverrides[`${key}_to_be`] ?? dim.to_be;
                            const asNum = asRaw === null || asRaw === undefined || asRaw === "" ? null : Number(asRaw);
                            const tbNum = tbRaw === null || tbRaw === undefined || tbRaw === "" ? null : Number(tbRaw);
                            const improved = asNum != null && tbNum != null ? tbNum > asNum : null;
                            const pct = asNum != null && tbNum != null && asNum !== 0
                              ? Number((((tbNum - asNum) / Math.abs(asNum)) * 100).toFixed(1)) : null;
                            const edited = qfOverrides[`${key}_as_is`] != null || qfOverrides[`${key}_to_be`] != null;
                            const inStyle = { width: 62, textAlign: "right", fontSize: 13, padding: "3px 6px", border: "1px solid #d1d5db", borderRadius: 6, fontVariantNumeric: "tabular-nums" };
                            return (
                              <tr key={dim.label}>
                                <td style={{ padding: "8px 10px", fontSize: 13, fontWeight: 600 }} title={dim.qualitative || ""}>
                                  {dim.label}{dim.unit ? <span style={{ color: "#9ca3af", fontWeight: 400 }}> ({dim.unit})</span> : null}
                                  <span
                                    title={edited ? "Edited by you (overrides the AI estimate)" : "AI estimate — editable"}
                                    style={{ marginLeft: 6, fontSize: 10, fontWeight: 800, padding: "1px 6px", borderRadius: 999, border: `1px solid ${edited ? "#93c5fd" : "#fde68a"}`, background: edited ? "#dbeafe" : "#fffbeb", color: edited ? "#1d4ed8" : "#b45309" }}
                                  >
                                    {edited ? "You" : "AI"}
                                  </span>
                                </td>
                                <td style={{ padding: "6px 10px", textAlign: "right" }}>
                                  <input type="number" value={asRaw ?? ""} onChange={(e) => setQfOverride(`${key}_as_is`, e.target.value)} style={inStyle} />
                                </td>
                                <td style={{ padding: "6px 10px", textAlign: "right" }}>
                                  <input type="number" value={tbRaw ?? ""} onChange={(e) => setQfOverride(`${key}_to_be`, e.target.value)} style={inStyle} />
                                </td>
                                <td style={{ padding: "8px 10px", fontSize: 13, textAlign: "right", fontWeight: 700, color: rowColor(improved), whiteSpace: "nowrap" }}>
                                  {pct == null ? "—" : `${arrow(improved)} ${Math.abs(pct)}%`}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>

                    {(() => {
                      const num = (v) => (v === "" || v == null || Number.isNaN(Number(v)) ? null : Number(v));
                      const axes = [
                        { label: "Time", as_is: num(d.time?.as_is), to_be: num(d.time?.to_be), unit: "min", lowerIsBetter: true },
                        { label: "Cost", as_is: num(d.cost?.as_is), to_be: num(d.cost?.to_be), unit: "USD", lowerIsBetter: true },
                        { label: "Quality", as_is: num(qfOverrides.quality_as_is ?? d.quality?.as_is), to_be: num(qfOverrides.quality_to_be ?? d.quality?.to_be), unit: "/100", lowerIsBetter: false },
                        { label: "Flexibility", as_is: num(qfOverrides.flexibility_as_is ?? d.flexibility?.as_is), to_be: num(qfOverrides.flexibility_to_be ?? d.flexibility?.to_be), unit: "/100", lowerIsBetter: false },
                      ];
                      if (!axes.some((a) => a.as_is != null || a.to_be != null)) return null;
                      return (
                        <div style={{ marginTop: 16, border: "1px solid #eef2f7", borderRadius: 10, padding: "12px 16px", background: "#fcfcfd" }}>
                          <div style={{ fontSize: 12, fontWeight: 700, color: "#6b7280", marginBottom: 4 }}>DEVIL'S QUADRANGLE — the four trade-off dimensions at a glance</div>
                          <DevilsQuadrangleChart axes={axes} />
                        </div>
                      );
                    })()}

                    {redesignReport.evidence && (() => {
                      const ev = redesignReport.evidence;
                      const secTitle = { fontSize: 11, fontWeight: 800, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4 };
                      const line = { fontSize: 12, color: "#374151", margin: "2px 0" };
                      const mono = { fontFamily: "ui-monospace, monospace", fontSize: 11.5 };
                      const box = { border: "1px solid #eef2f7", borderRadius: 10, padding: "10px 14px", background: "#fcfcfd", flex: "1 1 300px", minWidth: "min(280px, 100%)" };
                      const t = ev.time || {}, c = ev.cost || {}, q = ev.quality || {}, f = ev.flexibility || {};
                      return (
                        <div style={{ marginTop: 16 }}>
                          <div style={{ fontSize: 12, fontWeight: 700, color: "#6b7280", marginBottom: 8 }}>
                            EVIDENCE — the underlying changes each dimension is computed from
                          </div>
                          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                            <div style={box}>
                              <div style={secTitle}>Time</div>
                              <div style={line}>Cycle Δ: <b>{t.cycle_delta_minutes ?? "—"} min</b> · CTE {t.cte_as_is_percent ?? "—"}% → {t.cte_to_be_percent ?? "—"}%</div>
                              {(t.gateway_changes || []).length > 0 ? t.gateway_changes.map((g, i) => (
                                <div key={i} style={{ ...line, ...mono }}>
                                  + {g.gateway_type} after task {g.after_task_id ?? "start"} → branches [{(g.branch_task_ids || []).join(", ")}]{g.converge_at_task_id ? ` join @${g.converge_at_task_id}` : ""}
                                </div>
                              )) : <div style={{ ...line, color: "#9ca3af" }}>No gateway (structure) changes proposed.</div>}
                              {(t.task_time_changes || []).map((x) => (
                                <div key={x.task_id} style={{ ...line, ...mono }}>
                                  {x.task_name}: {x.as_is_expected_minutes} → {x.to_be_expected_minutes} min ({x.delta_minutes > 0 ? "+" : ""}{x.delta_minutes})
                                </div>
                              ))}
                              {(t.eliminated_tasks || []).map((x) => (
                                <div key={`e${x.task_id}`} style={{ ...line, ...mono, color: "#b91c1c" }}>
                                  − eliminated: {x.task_name} (removes {num2(x.expected_minutes_removed)} expected min/case)
                                </div>
                              ))}
                            </div>
                            <div style={box}>
                              <div style={secTitle}>Cost</div>
                              <div style={line}>Cost Δ: <b>{c.cost_delta_usd != null ? `${money(c.cost_delta_usd)}` : "—"}</b> per case</div>
                              {(c.resource_changes || []).length > 0 ? c.resource_changes.map((r, i) => (
                                <div key={i} style={line}>
                                  <b>{r.task_name}</b>
                                  {r.role && <span style={{ color: "#9ca3af" }}> ({r.role})</span>}
                                  : {r.from_job}{r.from_rate != null ? ` (${money(r.from_rate)}/hr)` : ""} → {r.to_job}{r.to_rate != null ? ` (${money(r.to_rate)}/hr)` : ""}
                                  {/* Analyst-set rows carry no savings figure — only the
                                      backend can compute a path-weighted per-case saving,
                                      and the Cost Δ above already reflects it. */}
                                  {r.analyst_set && (
                                    <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 800, color: "#1d4ed8", background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 999, padding: "1px 7px" }}>
                                      set by you
                                    </span>
                                  )}
                                  {r.savings_per_case != null && <span style={{ color: "#15803d" }}> · saves {money(r.savings_per_case)}/case</span>}
                                  {r.reason && <span style={{ color: "#9ca3af" }}> — {fixDecimals(r.reason)}</span>}
                                </div>
                              )) : <div style={{ ...line, color: "#9ca3af" }}>No resource reassignments.</div>}
                              {(c.task_cost_changes || []).map((x) => (
                                <div key={x.task_id} style={{ ...line, ...mono }}>
                                  {x.task_name}: {money(x.as_is_cost_usd)} → {money(x.to_be_cost_usd)} ({x.delta_usd > 0 ? "+" : ""}{money(Math.abs(x.delta_usd))})
                                </div>
                              ))}
                            </div>
                            <div style={box}>
                              <div style={secTitle}>Quality</div>
                              <div style={line}>Rework share: {q.rework_share_as_is_percent ?? "—"}% → {q.rework_share_to_be_percent ?? "—"}% of cycle</div>
                              <div style={line}>NVA steps: {q.nva_share_as_is_percent ?? "—"}% → {q.nva_share_to_be_percent ?? "—"}% of steps</div>
                            </div>
                            <div style={box}>
                              <div style={secTitle}>Flexibility</div>
                              <div style={line}>Tasks made parallelizable: <b>{f.parallelized_task_count ?? 0}</b></div>
                              <div style={line}>Tasks with substitutable resources: <b>{f.substitutable_task_count ?? 0}</b></div>
                            </div>
                          </div>
                        </div>
                      );
                    })()}
                    </>)}
                  </div>
                  )}

                  {activeTab === "flow" && (
                    <div className="role-card">
                      <div className="section-title">Flow &amp; Bottlenecks</div>
                      <p style={{ fontSize: 12.5, color: "#6b7280", margin: "6px 0 12px" }}>
                        FBPM Ch.7 flow analysis — cycle time, cycle-time efficiency and value-adding time, AS-IS vs TO-BE.
                      </p>
                      {flowRows.length > 0 ? (
                        <div style={{ overflowX: "auto" }}>
                          <table style={{ borderCollapse: "collapse", width: "100%", minWidth: 480 }}>
                            {qHead}
                            <tbody>{flowRows.map(numRow)}</tbody>
                          </table>
                        </div>
                      ) : (
                        <div className="no-results">Flow metrics unavailable — run Optimize with the agent connected.</div>
                      )}
                      {(() => {
                        // WHY the TO-BE numbers moved (or honestly didn't): every delta in
                        // the table above is explained by a concrete change from the
                        // evidence block — the same explain-the-result pattern as the
                        // Value-Added analysis.
                        const ev = redesignReport.evidence || {};
                        const gws = ev.time?.gateway_changes || [];
                        const rcs = ev.cost?.resource_changes || [];
                        const improved = flowRows.some((r) => r.improved === true);
                        const reasons = [];
                        gws.forEach((g) => {
                          if (g.gateway_type === "PARALLEL" && (g.branch_task_ids || []).length >= 2) {
                            reasons.push({
                              c: "#0e6b68",
                              t: `Tasks ${g.branch_task_ids.join(" & ")} now run in parallel${g.after_task_id != null ? ` after task ${g.after_task_id}` : ""} — wall-clock time follows the longer branch instead of their sum (FBPM Ch.7, AND-block = max).`,
                            });
                          }
                        });
                        rcs.forEach((r) => {
                          reasons.push({
                            c: "#1d4ed8",
                            t: `'${r.task_name}' reassigned from ${r.from_job} (${money(r.from_rate)}/hr) to ${r.to_job} (${money(r.to_rate)}/hr)${r.savings_per_case != null ? ` — saves ${money(r.savings_per_case)} per case` : ""}.`,
                          });
                        });
                        return (
                          <div style={{ marginTop: 16 }}>
                            <div style={{ fontSize: 12, fontWeight: 700, color: "#6b7280", marginBottom: 6 }}>
                              WHY THE TO-BE {improved ? "IMPROVES" : "DOES NOT IMPROVE"} — each delta traced to its cause
                            </div>
                            {reasons.length > 0 ? (
                              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                                {reasons.map((r, i) => (
                                  <div key={i} style={{ borderLeft: `4px solid ${r.c}`, background: "#fcfcfd", border: `1px solid ${r.c}22`, borderLeftWidth: 4, borderRadius: 8, padding: "8px 12px", fontSize: 12.5, color: "#374151" }}>
                                    {r.t}
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <div style={{ background: "#fcfcfd", border: "1px solid #eef2f7", borderRadius: 8, padding: "8px 12px", fontSize: 12.5, color: "#6b7280" }}>
                                No structural or resource change was justified by the measured data — every candidate either conflicted
                                with an existing decision point or showed no proven cycle-time/cost benefit in the book-correct what-if,
                                so the TO-BE equals the AS-IS. See the Redesign tab for what was considered and why it was rejected.
                              </div>
                            )}
                          </div>
                        );
                      })()}
                      {redesignReport.bottlenecks && (
                        <div style={{ marginTop: 14, display: "flex", gap: 12, flexWrap: "wrap" }}>
                          {[
                            { label: "Non-value-adding", v: redesignReport.bottlenecks.nva_share_percent, c: "#b91c1c" },
                            { label: "Rework", v: redesignReport.bottlenecks.rework_share_percent, c: "#b45309" },
                            { label: "Cycle-time efficiency", v: redesignReport.bottlenecks.cycle_time_efficiency_percent, c: "#0e6b68" },
                          ].map((m) => (
                            <div key={m.label} style={{ flex: "1 1 130px", border: `1px solid ${m.c}22`, borderLeft: `4px solid ${m.c}`, borderRadius: 10, padding: "10px 14px", background: "#fff" }}>
                              <div style={{ fontSize: 20, fontWeight: 800, color: m.c }}>{m.v ?? "—"}%</div>
                              <div style={{ fontSize: 11, fontWeight: 600, color: "#6b7280" }}>{m.label}</div>
                            </div>
                          ))}
                        </div>
                      )}
                      {asF.waste && (() => {
                        // FBPM Ch.6 WASTE ANALYSIS — move / hold / overdo, from measured data
                        // (backend `waste` block). Bars are proportional to expected minutes
                        // per case; each issue-register row links to its catalog heuristic.
                        const w = asF.waste;
                        const t = w.totals || {};
                        const fams = [
                          { key: "hold", label: "HOLD — waiting", min: t.hold_waiting_minutes || 0, c: "#d97706", extra: null },
                          { key: "defect", label: "OVERDO — rework (defects)", min: t.overdo_rework_minutes || 0, c: "#9a3412", extra: t.overdo_rework_cost_usd ? `${money(t.overdo_rework_cost_usd)} labor` : null },
                          { key: "overproc", label: "OVERDO — overprocessing (NVA work)", min: t.overdo_overprocessing_minutes || 0, c: "#b91c1c", extra: null },
                        ];
                        const maxMin = Math.max(1, ...fams.map((f) => f.min));
                        const anyWaste = fams.some((f) => f.min > 0) || (t.move_handoff_count || 0) > 0;
                        return (
                          <div style={{ marginTop: 18 }}>
                            <div style={{ fontSize: 12, fontWeight: 700, color: "#6b7280", marginBottom: 6 }}>
                              WASTE ANALYSIS (FBPM Ch.6) — move / hold / overdo, expected per case
                            </div>
                            {!anyWaste ? (
                              <div style={{ background: "#f0fdf4", border: "1px solid #86efac", borderRadius: 8, padding: "8px 12px", fontSize: 12.5, color: "#166534" }}>
                                No measured waste: no waiting, rework, NVA work or handoffs recorded.
                              </div>
                            ) : (
                              <>
                                <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "stretch" }}>
                                  <div style={{ flex: "2 1 340px", border: "1px solid #eef2f7", borderRadius: 10, padding: "10px 14px", background: "#fff" }}>
                                    {fams.map((f) => (
                                      <div key={f.key} style={{ marginBottom: 8 }}>
                                        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11.5 }}>
                                          <span style={{ fontWeight: 700, color: "#374151" }}>{f.label}</span>
                                          <span style={{ fontVariantNumeric: "tabular-nums", color: f.c, fontWeight: 700 }}>
                                            {f.min.toFixed(1)} min{f.extra ? ` · ${f.extra}` : ""}
                                          </span>
                                        </div>
                                        <div style={{ height: 8, background: "#eef2f7", borderRadius: 4, marginTop: 3, overflow: "hidden" }}>
                                          <div style={{ height: "100%", width: `${(f.min / maxMin) * 100}%`, background: f.c, borderRadius: 4 }} />
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                  <div style={{ flex: "1 1 200px", border: "1px solid #0e6b6822", borderLeft: "4px solid #0e6b68", borderRadius: 10, padding: "10px 14px", background: "#fff" }}>
                                    <div style={{ fontSize: 11, fontWeight: 700, color: "#6b7280", textTransform: "uppercase" }}>MOVE — handoffs</div>
                                    <div style={{ fontSize: 22, fontWeight: 800, color: "#0e6b68" }}>{t.move_handoff_count ?? 0}</div>
                                    <div style={{ fontSize: 11, color: "#9ca3af" }}>changes of Responsible job between consecutive steps</div>
                                    {(w.handoffs || []).slice(0, 4).map((hh, i) => (
                                      <div key={i} style={{ fontSize: 11, color: "#374151", marginTop: 4 }}>
                                        {hh.from_job} → {hh.to_job}
                                        {hh.receiving_step_waiting_minutes > 0 && <span style={{ color: "#d97706" }}> · {num2(hh.receiving_step_waiting_minutes)}m wait at receiver</span>}
                                      </div>
                                    ))}
                                  </div>
                                </div>
                                {(w.tasks || []).length > 0 && (
                                  <details style={{ marginTop: 8 }}>
                                    <summary style={{ cursor: "pointer", fontSize: 13.5, fontWeight: 700, color: "#0e6b68" }}>
                                      Issue register — {w.tasks.length} step(s) with measured waste, each linked to its redesign heuristic
                                    </summary>
                                    <div style={{ overflowX: "auto", marginTop: 6 }}>
                                      <table style={{ borderCollapse: "collapse", width: "100%", minWidth: 620, fontSize: 12 }}>
                                        <thead>
                                          <tr>
                                            {["Step", "Waiting (min)", "Rework (min)", "Rework cost", "NVA work (min)", "Addressed by"].map((hh, i) => (
                                              <th key={hh} style={{ padding: "6px 10px", fontSize: 11, color: "#6b7280", textAlign: i === 0 || i === 5 ? "left" : "right", borderBottom: "1px solid #e5e7eb" }}>{hh}</th>
                                            ))}
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {w.tasks.map((r) => (
                                            <tr key={r.task_id}>
                                              <td style={{ padding: "6px 10px", fontWeight: 600 }}>
                                                <span style={isEliminated(r.task_id) ? { textDecoration: "line-through", color: "#9ca3af" } : undefined}>{r.task_name}</span>
                                                {isEliminated(r.task_id) && eliminatedBadge}
                                              </td>
                                              <td style={{ padding: "6px 10px", textAlign: "right", color: r.hold_waiting_minutes > 0 ? "#d97706" : "#d1d5db" }}>{r.hold_waiting_minutes ? num2(r.hold_waiting_minutes) : "—"}</td>
                                              <td style={{ padding: "6px 10px", textAlign: "right", color: r.overdo_rework_minutes > 0 ? "#9a3412" : "#d1d5db" }}>{r.overdo_rework_minutes ? num2(r.overdo_rework_minutes) : "—"}</td>
                                              <td style={{ padding: "6px 10px", textAlign: "right" }}>{r.overdo_rework_cost_usd ? money(r.overdo_rework_cost_usd) : "—"}</td>
                                              <td style={{ padding: "6px 10px", textAlign: "right", color: r.overdo_overprocessing_minutes > 0 ? "#b91c1c" : "#d1d5db" }}>{r.overdo_overprocessing_minutes ? num2(r.overdo_overprocessing_minutes) : "—"}</td>
                                              <td style={{ padding: "6px 10px" }}>
                                                {(r.suggested_heuristics || []).map((c2) => (
                                                  <span key={c2} style={{ fontSize: 10, fontWeight: 700, color: "#0e6b68", background: "#ccfbf1", borderRadius: 999, padding: "1px 7px", marginRight: 4 }}>{c2}</span>
                                                ))}
                                              </td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </div>
                                    <div style={{ fontSize: 12, color: "#5b6069", marginTop: 4 }}>{w.note}</div>
                                  </details>
                                )}
                              </>
                            )}
                          </div>
                        );
                      })()}
                      {(() => {
                        // ── QUEUEING (FBPM Ch.7 §7.2) — per-resource utilization + queue time.
                        // Deterministic, from the recorded demand + each job's staffing.
                        const q = redesignFlow?.as_is?.queueing;
                        if (!q) return null;
                        if (q.status === 'requires_arrival_rate') {
                          return (
                            <div style={{ marginTop: 18, padding: "10px 14px", background: "#fffbeb", border: "1px solid #f59e0b", borderRadius: 10, color: "#92400e", fontSize: 12.5 }}>
                              <strong>Queueing analysis needs one input:</strong> record how many cases arrive per week
                              (Edit Process → Description → “Cases arriving per week”). It unlocks per-resource utilization,
                              queue-time estimates and makes the extra-resources heuristic measurable.
                            </div>
                          );
                        }
                        if (q.status !== 'ok' || !(q.jobs || []).length) return null;
                        return (
                          <div style={{ marginTop: 18 }}>
                            <div style={{ fontSize: 12, fontWeight: 700, color: "#6b7280", marginBottom: 2 }}>
                              QUEUEING — per-resource utilization &amp; queue time at {q.arrival_rate_cases_per_week} cases/week (FBPM Ch.7 §7.2)
                            </div>
                            <div style={{ fontSize: 11.5, color: "#64748b", marginBottom: 8 }}>
                              Deterministic: workload = demand × each job&apos;s attributed minutes per case; capacity = staff × weekly hours.
                              Queue time is an M/M/c approximation (stated assumption). Sub-process staffing is analyzed inside its child process.
                            </div>
                            <div style={{ overflowX: "auto", border: "1px solid #eef2f7", borderRadius: 10 }}>
                              <table style={{ borderCollapse: "collapse", width: "100%", minWidth: 720, fontSize: 12 }}>
                                <thead><tr>
                                  {["Job", "Staff", "Service min/case", "Weekly load", "Utilization", "Queue wait / case"].map((h, i) => (
                                    <th key={h} style={{ padding: "7px 10px", fontSize: 10.5, color: "#6b7280", textAlign: i === 0 ? "left" : "right", textTransform: "uppercase", letterSpacing: "0.04em", borderBottom: "2px solid #e5e7eb", background: "#f8fafc" }}>{h}</th>
                                  ))}
                                </tr></thead>
                                <tbody>
                                  {q.jobs.map((jb) => {
                                    const u = jb.utilization_pct ?? 0;
                                    const uc = !jb.stable ? "#b91c1c" : u >= 85 ? "#b45309" : "#15803d";
                                    return (
                                      <tr key={jb.job_id} style={{ background: !jb.stable ? "#fef2f2" : undefined }}>
                                        <td style={{ padding: "7px 10px", fontWeight: 600 }}>
                                          {jb.job_name}
                                          {q.bottleneck_job?.job_id === jb.job_id && (
                                            <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 800, color: "#b91c1c", background: "#fee2e2", border: "1px solid #fecaca", borderRadius: 999, padding: "1px 7px" }}>queueing bottleneck</span>
                                          )}
                                        </td>
                                        <td style={{ padding: "7px 10px", textAlign: "right" }}>{jb.staff_count}</td>
                                        <td style={{ padding: "7px 10px", textAlign: "right" }}>{jb.service_minutes_per_case}</td>
                                        <td style={{ padding: "7px 10px", textAlign: "right" }}>{jb.weekly_workload_hours} h / {jb.weekly_capacity_hours} h</td>
                                        <td style={{ padding: "7px 10px", textAlign: "right", fontWeight: 800, color: uc }}>
                                          {jb.utilization_pct}%{!jb.stable && " ⚠"}
                                        </td>
                                        <td style={{ padding: "7px 10px", textAlign: "right" }}>
                                          {!jb.stable ? "unbounded — add staff" : jb.queue_wait_minutes_per_case != null ? fmtMin(jb.queue_wait_minutes_per_case) : "—"}
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                            <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 4 }}>
                              ⚠ over 100% = more work arrives per week than this staffing can do — the queue grows without bound; add staff
                              (extra-resources heuristic) or reduce the job&apos;s attributed work, then re-run.
                            </div>
                          </div>
                        );
                      })()}
                      {(() => {
                        // ── KNOCK-OUT (FBPM Ch.8) — run the check most likely to reject a
                        // case, per minute it costs, first. Deterministic from recorded
                        // reject rates; no reorder is applied automatically.
                        const k = redesignFlow?.as_is?.knock_out;
                        if (!k) return null;
                        if (k.status === 'requires_reject_rates') {
                          return (
                            <div style={{ marginTop: 18, padding: "10px 14px", background: "#fffbeb", border: "1px solid #f59e0b", borderRadius: 10, color: "#92400e", fontSize: 12.5 }}>
                              <strong>Knock-out analysis needs one input:</strong> record each check&apos;s reject rate on its XOR
                              branch (Flow Analysis panel → “reject” field) — the share of cases that check rejects. It computes
                              the cheapest-first check order per the knock-out principle.
                            </div>
                          );
                        }
                        return (
                          <div style={{ marginTop: 18 }}>
                            <div style={{ fontSize: 12, fontWeight: 700, color: "#6b7280", marginBottom: 2 }}>
                              KNOCK-OUT CHECK ORDER (FBPM Ch.8) — reject-power per minute of effort, from recorded reject rates
                            </div>
                            <div style={{ fontSize: 11.5, color: "#64748b", marginBottom: 8 }}>{k.principle}</div>
                            <div style={{ overflowX: "auto", border: "1px solid #eef2f7", borderRadius: 10 }}>
                              <table style={{ borderCollapse: "collapse", width: "100%", minWidth: 560, fontSize: 12 }}>
                                <thead><tr>
                                  {["Check (task before the XOR)", "Reject rate", "Effort (min)", "Knock-out power"].map((h, i) => (
                                    <th key={h} style={{ padding: "7px 10px", fontSize: 10.5, color: "#6b7280", textAlign: i === 0 ? "left" : "right", textTransform: "uppercase", letterSpacing: "0.04em", borderBottom: "2px solid #e5e7eb", background: "#f8fafc" }}>{h}</th>
                                  ))}
                                </tr></thead>
                                <tbody>
                                  {(k.checks || []).map((c, i) => (
                                    <tr key={c.task_id} style={{ background: i === 0 ? "#f0fdf4" : undefined }}>
                                      <td style={{ padding: "7px 10px", fontWeight: 600 }}>
                                        {i === 0 && <span aria-hidden="true" style={{ color: "#16a34a", fontWeight: 800, marginRight: 4 }}>1st</span>}
                                        {c.task_name}
                                      </td>
                                      <td style={{ padding: "7px 10px", textAlign: "right" }}>{Math.round(c.reject_rate * 100)}%</td>
                                      <td style={{ padding: "7px 10px", textAlign: "right" }}>{c.effort_minutes}</td>
                                      <td style={{ padding: "7px 10px", textAlign: "right", fontWeight: 800 }} title="Shown to 4 decimals, not 2 — reject_rate ÷ effort is often under 0.01, and 2 decimals would round several checks to the same 0.00 and hide the ranking this table exists to show.">
                                        {Number(c.knock_out_power).toLocaleString(undefined, { maximumFractionDigits: 4 })}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                            <div style={{ marginTop: 8, padding: "8px 12px", borderRadius: 8, fontSize: 12.5,
                              background: k.is_optimal ? "#f0fdf4" : "#fffbeb",
                              border: `1px solid ${k.is_optimal ? "#86efac" : "#fde68a"}`,
                              color: k.is_optimal ? "#166534" : "#92400e" }}>
                              {k.is_optimal ? "✓ " : "⚠ "}
                              <b>Current order:</b> {(k.current_order || []).join(" → ")} ·{" "}
                              <b>Recommended:</b> {(k.recommended_order || []).join(" → ")}
                              <div style={{ marginTop: 2 }}>{k.note}</div>
                            </div>
                          </div>
                        );
                      })()}
                      {(redesignReport.bottlenecks?.items || []).length > 0 && (() => {
                        const typeColor = {
                          "slowest-activity": "#0e6b68",
                          waiting: "#b45309",
                          rework: "#9a3412",
                          "non-value-adding": "#b91c1c",
                          "cost-concentration": "#1d4ed8",
                        };
                        return (
                          <div style={{ marginTop: 18 }}>
                            <div style={{ fontSize: 12, fontWeight: 700, color: "#6b7280", marginBottom: 2 }}>
                              BOTTLENECK DETAIL — measured root cause, affected step, quantified impact and evidence (AS-IS)
                            </div>
                            <div style={{ fontSize: 11.5, color: "#64748b", marginBottom: 8 }}>
                              Every figure here is <b>deterministic</b>: computed from the backend&apos;s Ch.7 flow analysis
                              (task times, activation probabilities, RACI rates) with fixed thresholds — no AI-generated
                              numbers or wording. Heuristic suggestions come from the FBPM Table A.1 catalog; several can
                              apply to one bottleneck.
                            </div>
                            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                              {redesignReport.bottlenecks.items.map((b, i) => {
                                const c = typeColor[b.type] || "#475569";
                                const isSubB = !!(b.is_subprocess || /^\[Sub-Process\]/.test(String(b.task_name || "")));
                                const sev = b.impact_percent_of_cycle == null ? null
                                  : b.impact_percent_of_cycle >= 25 ? { label: "High impact", bg: "#fef2f2", fg: "#b91c1c", bd: "#fecaca" }
                                  : b.impact_percent_of_cycle >= 10 ? { label: "Medium impact", bg: "#fffbeb", fg: "#b45309", bd: "#fde68a" }
                                  : { label: "Low impact", bg: "#f8fafc", fg: "#64748b", bd: "#e2e8f0" };
                                const taskRow = (asF.tasks || []).find((t) => t.task_id === b.task_id) || {};
                                const act = taskRow.activation_probability;
                                const perRun =
                                  b.type === "waiting" ? taskRow.waiting_time_minutes
                                  : b.type === "rework" ? taskRow.rework_time_minutes
                                  : (taskRow.processing_time_minutes || 0) + (taskRow.waiting_time_minutes || 0) + (taskRow.rework_time_minutes || 0);
                                return (
                                  <details key={i} style={{ border: `1px solid ${c}22`, borderLeft: `4px solid ${c}`, borderRadius: 8, background: "#fff" }}>
                                    <summary style={{ padding: "8px 12px", cursor: "pointer", listStyle: "none" }}>
                                      <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
                                        <span style={{ fontSize: 10, fontWeight: 800, color: c, textTransform: "uppercase", letterSpacing: "0.05em" }}>{b.type.replace(/-/g, " ")}</span>
                                        <span style={{ fontSize: 13, fontWeight: 700 }}>{b.task_name}</span>
                                        {isSubB && (
                                          <span title="Composite step — its duration and cost come from the child process's own flow analysis. Fix it inside the child." style={{ fontSize: 10, fontWeight: 800, color: "#5b21b6", background: "#ede9fe", border: "1px solid #c4b5fd", borderRadius: 999, padding: "1px 8px", whiteSpace: "nowrap" }}>
                                            Sub-Process
                                          </span>
                                        )}
                                        {sev && (
                                          <span style={{ fontSize: 10, fontWeight: 800, color: sev.fg, background: sev.bg, border: `1px solid ${sev.bd}`, borderRadius: 999, padding: "1px 8px", whiteSpace: "nowrap" }}>
                                            {sev.label}
                                          </span>
                                        )}
                                        <span style={{ marginLeft: "auto", fontSize: 12, fontWeight: 700, color: c }}>
                                          {num2(b.impact_minutes)} min/case{b.impact_percent_of_cycle != null ? ` · ${num2(b.impact_percent_of_cycle)}% of cycle` : ""}{b.impact_cost_usd != null ? ` · ${money(b.impact_cost_usd)}` : ""}
                                        </span>
                                        <span style={{ fontSize: 11, color: "#9ca3af" }}>▾ details</span>
                                      </div>
                                      <div style={{ fontSize: 12, color: "#374151", marginTop: 3 }}>{b.root_cause}</div>
                                      {b.impact_percent_of_cycle != null && (
                                        <div title={`${num2(b.impact_percent_of_cycle)}% of the expected cycle time`} style={{ height: 5, background: "#eef2f7", borderRadius: 3, marginTop: 5, overflow: "hidden" }}>
                                          <div style={{ height: "100%", width: `${Math.min(100, b.impact_percent_of_cycle)}%`, background: c, borderRadius: 3 }} />
                                        </div>
                                      )}
                                    </summary>
                                    <div style={{ padding: "0 12px 12px", borderTop: "1px dashed #eef2f7" }}>
                                      <div style={{ fontSize: 11, fontWeight: 800, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.05em", margin: "10px 0 4px" }}>Root cause, step by step</div>
                                      <ol style={{ margin: 0, paddingLeft: 18, fontSize: 12, color: "#374151", display: "flex", flexDirection: "column", gap: 3 }}>
                                        <li>
                                          This step runs in <b>{act != null ? `${(act * 100).toFixed(1)}%` : "an unknown share"}</b> of cases — its <i>activation probability</i>: the share of cases whose execution route (through the process's gateways) includes this step. A step behind a decision branch runs in fewer cases, so its expected impact is weighted down accordingly.
                                        </li>
                                        <li>
                                          When it does run, {b.type === "waiting" ? "it waits" : b.type === "rework" ? "it spends rework effort of" : "it takes"} <b>{perRun != null ? `${Number(perRun).toFixed(0)} min` : "—"}</b>
                                          {taskRow.processing_time_minutes != null && ` (processing ${num2(taskRow.processing_time_minutes)} · waiting ${num2(taskRow.waiting_time_minutes ?? 0)} · rework ${num2(taskRow.rework_time_minutes ?? 0)} min)`}.
                                        </li>
                                        <li>
                                          Expected impact per case = {perRun != null ? Number(perRun).toFixed(0) : "—"} min × {act != null ? `${(act * 100).toFixed(1)}%` : "—"} = <b>{num2(b.impact_minutes)} min</b>{b.impact_percent_of_cycle != null ? <> — <b>{num2(b.impact_percent_of_cycle)}%</b> of the {num2(asF.cycle_time_minutes)} min expected cycle</> : null}.
                                        </li>
                                        {b.impact_cost_usd != null && (
                                          <li>Cost dimension: at this step's applied hourly rate that impact is worth <b>{money(b.impact_cost_usd)}</b> per case.</li>
                                        )}
                                      </ol>
                                      {isSubB && b.child_process_id != null && (
                                        <div style={{ marginTop: 10, padding: "8px 12px", background: "#faf5ff", border: "1px solid #d8b4fe", borderRadius: 8, fontSize: 12, color: "#5b21b6", display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                                          <span>
                                            This bottleneck lives <b>inside a sub-process</b> — the heuristics below apply within the child
                                            process, not at this level. Optimize the child, then re-run here to see the composite improve.
                                          </span>
                                          <button
                                            type="button"
                                            className="secondary-btn sm"
                                            onClick={() => navigate(`/process-optimization/results/${encodeURIComponent(String(b.child_process_id))}`)}
                                            aria-label="Open the child process's optimization page"
                                          >
                                            Open child process →
                                          </button>
                                        </div>
                                      )}
                                      {(() => {
                                        // Table A.1 maps SEVERAL heuristics per problem class — show
                                        // every applicable one, each cross-linked to the catalog verdict.
                                        const codes = Array.isArray(b.suggested_heuristics) && b.suggested_heuristics.length
                                          ? b.suggested_heuristics
                                          : b.suggested_heuristic ? [b.suggested_heuristic] : [];
                                        if (!codes.length) return null;
                                        return (
                                          <div style={{ marginTop: 10 }}>
                                            <div style={{ fontSize: 11, fontWeight: 800, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4 }}>
                                              FBPM heuristics for this bottleneck (Table A.1 — several can apply)
                                            </div>
                                            <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                                              {codes.map((code) => {
                                                const row = (redesignReport.catalog_review || []).find((h) => h.code === code);
                                                const st = row?.status;
                                                const aff = row?.affects || {};
                                                const affList = ["time", "cost", "quality", "flexibility"].filter((k) => aff[k]);
                                                return (
                                                  <div key={code} style={{ display: "flex", alignItems: "baseline", gap: 6, flexWrap: "wrap", fontSize: 12 }}>
                                                    <b>{row?.name || code}</b>
                                                    <span style={{ fontSize: 10, fontWeight: 700, borderRadius: 999, padding: "1px 8px",
                                                      background: st === "applied" ? "#ccfbf1" : st === "recommended" ? "#fdf3d8" : "#eef2f7",
                                                      color: st === "applied" ? "#0e6b68" : st === "recommended" ? "#b45309" : "#475569" }}>
                                                      {st === "applied" ? "already applied by this optimization" : st === "recommended" ? "recommended — apply it in the Redesign tab" : "in the catalog (see Redesign tab)"}
                                                    </span>
                                                    {affList.map((k) => (
                                                      <span key={k} title={`Applying it would move the ${k} dimension of the Devil's Quadrangle`} style={{ fontSize: 10, fontWeight: 700, color: "#475569", background: "#eef2f7", borderRadius: 999, padding: "1px 7px", textTransform: "capitalize" }}>{k}</span>
                                                    ))}
                                                    {row?.rule_of_thumb && <span style={{ flexBasis: "100%", fontSize: 11.5, color: "#6b7280" }}>Rule of thumb: {row.rule_of_thumb}</span>}
                                                  </div>
                                                );
                                              })}
                                            </div>
                                          </div>
                                        );
                                      })()}
                                      <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 8, fontFamily: "ui-monospace, monospace" }}>{b.evidence}</div>
                                    </div>
                                  </details>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })()}
                      {Array.isArray(asF.tasks) && asF.tasks.length > 0 && (() => {
                        const flagged = new Set((redesignReport.bottlenecks?.items || []).map((b) => b.task_id));
                        return (
                          <div style={{ marginTop: 18 }}>
                            <div style={{ fontSize: 12, fontWeight: 700, color: "#6b7280", marginBottom: 6 }}>
                              PER-ACTIVITY TIMING (Little&apos;s Law) — processing vs waiting vs rework, expected per case
                            </div>
                            <div style={{ overflowX: "auto" }}>
                              <table style={{ borderCollapse: "collapse", width: "100%", minWidth: 560, fontSize: 12 }}>
                                <thead>
                                  <tr>
                                    {["Step", "Composition", "Processing (min)", "Waiting (min)", "Rework (min)", "Activation %", "Expected cost"].map((h, i) => (
                                      <th
                                        key={h}
                                        title={h === "Activation %" ? "The share of cases whose execution route (through the process's gateways) includes this step. Steps behind decision branches run in fewer cases, so their times and costs are weighted by this probability in every expected value." : h === "Composition" ? "Processing (teal) vs waiting (amber) vs rework (brick) share of the step's elapsed time" : undefined}
                                        style={{ padding: "6px 10px", fontSize: 11, color: "#6b7280", textAlign: i <= 1 ? "left" : "right", borderBottom: "1px solid #e5e7eb", cursor: ["Activation %", "Composition"].includes(h) ? "help" : undefined, textDecoration: h === "Activation %" ? "underline dotted" : undefined }}
                                      >{h}</th>
                                    ))}
                                  </tr>
                                </thead>
                                <tbody>
                                  {asF.tasks.map((t) => (
                                    <tr key={t.task_id} style={flagged.has(t.task_id) ? { background: "#fffbeb" } : undefined}>
                                      <td style={{ padding: "6px 10px", fontWeight: 600 }}>
                                        {t.task_name}
                                        {isSubprocessRow(t) && subprocessBadge}
                                        {flagged.has(t.task_id) && <span title="appears in bottleneck detail above" style={{ marginLeft: 6, color: "#b45309" }}>⚑</span>}
                                        {isEliminated(t.task_id) && eliminatedBadge}
                                      </td>
                                      <td style={{ padding: "6px 10px", minWidth: 110 }}>
                                        {(() => {
                                          const p = Number(t.processing_time_minutes || 0), w = Number(t.waiting_time_minutes || 0), r = Number(t.rework_time_minutes || 0);
                                          const tot = p + w + r;
                                          if (!tot) return <span style={{ color: "#d1d5db" }}>—</span>;
                                          const seg = (v, color, name) => v > 0 && (
                                            <div key={name} title={`${name}: ${num2(v)} min (${((v / tot) * 100).toFixed(0)}%)`} style={{ width: `${(v / tot) * 100}%`, background: color, borderRadius: 3 }} />
                                          );
                                          return (
                                            <div style={{ display: "flex", gap: 2, height: 8 }}>
                                              {seg(p, TIME_STACK.processing, "Processing")}
                                              {seg(w, TIME_STACK.waiting, "Waiting")}
                                              {seg(r, TIME_STACK.rework, "Rework")}
                                            </div>
                                          );
                                        })()}
                                      </td>
                                      <td style={{ padding: "6px 10px", textAlign: "right" }}>{num2(t.processing_time_minutes)}</td>
                                      <td style={{ padding: "6px 10px", textAlign: "right", color: t.waiting_time_minutes > 0 ? "#b45309" : undefined }}>{num2(t.waiting_time_minutes)}</td>
                                      <td style={{ padding: "6px 10px", textAlign: "right", color: t.rework_time_minutes > 0 ? "#9a3412" : undefined }}>{num2(t.rework_time_minutes)}</td>
                                      <td style={{ padding: "6px 10px", textAlign: "right" }}>{t.activation_probability != null ? (t.activation_probability * 100).toFixed(1) : "—"}</td>
                                      <td style={{ padding: "6px 10px", textAlign: "right" }}>{t.expected_cost_usd != null ? money(t.expected_cost_usd) : "—"}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        );
                      })()}
                      {flowFlags.length > 0 && (
                        <div style={{ marginTop: 12, fontSize: 12, color: "#4c4f56", fontWeight: 600 }}>
                          {flowFlags.map((f, i) => (<div key={i}>⚠ {f}</div>))}
                        </div>
                      )}
                    </div>
                  )}

                  {activeTab === "redesign" && (() => {
                    const catalogByCode = new Map((redesignReport.catalog_review || []).map((x) => [x.code, x]));
                    const dqChips = (code) => {
                      const cr = catalogByCode.get(code);
                      if (!cr || !cr.affects) return null;
                      return ["time", "cost", "quality", "flexibility"]
                        .filter((k) => cr.affects[k])
                        .map((k) => (
                          <span key={k} title={`Per FBPM Table A.1, this heuristic moves the ${k} dimension of the Devil's Quadrangle`} style={{ fontSize: 10, fontWeight: 700, color: "#475569", background: "#eef2f7", borderRadius: 999, padding: "1px 7px", textTransform: "capitalize" }}>{k}</span>
                        ));
                    };
                    const expectedEffect = (h) => {
                      const cr = catalogByCode.get(h.code);
                      const dims = cr?.affects
                        ? ["time", "cost", "quality", "flexibility"].filter((k) => cr.affects[k])
                        : [];
                      if (!dims.length) return null;
                      return (
                        <div style={{ fontSize: 11.5, color: "#0e6b68", marginTop: 3 }}>
                          Expected effect if adopted: moves the <b>{dims.join(" + ")}</b> dimension{dims.length > 1 ? "s" : ""} of the
                          Devil's Quadrangle. Its numeric impact cannot be computed from the recorded data (that is why it is
                          recommended, not auto-applied) — the measured basis is {h.basis || "the detected bottleneck"}.
                        </div>
                      );
                    };
                    return (
                    <div className="role-card">
                      <div className="section-title">Redesign — heuristics (FBPM Ch.8)</div>

                      {/* Agent (deterministic /cms/optimize, below) vs RL (learned
                          search, /cms/rl-optimize) — a separate pipeline with its own
                          goal-driven redesign. Toggle replaces the view rather than
                          showing both, since this tab is already long. */}
                      <div className="opt-network-tabs" role="tablist" aria-label="Redesign source" style={{ marginBottom: 16 }}>
                        <button
                          role="tab"
                          aria-selected={redesignMode === "agent"}
                          className={`opt-tab-btn ${redesignMode === "agent" ? "active" : ""}`}
                          onClick={() => setRedesignMode("agent")}
                        >
                          Agent
                        </button>
                        <button
                          role="tab"
                          aria-selected={redesignMode === "rl"}
                          className={`opt-tab-btn ${redesignMode === "rl" ? "active" : ""}`}
                          onClick={() => setRedesignMode("rl")}
                        >
                          RL
                        </button>
                        <button
                          role="tab"
                          aria-selected={redesignMode === "bpr"}
                          className={`opt-tab-btn ${redesignMode === "bpr" ? "active" : ""}`}
                          onClick={() => setRedesignMode("bpr")}
                        >
                          Model
                        </button>
                      </div>

                      {redesignMode === "rl" && (
                        <RlRedesignPanel
                          processId={selectedId}
                          money={money}
                          num2={num2}
                          fmtMin={fmtMin}
                          fixDecimals={fixDecimals}
                          preview={rl.preview}
                          previewLoading={rl.previewLoading}
                          previewError={rl.previewError}
                          loadPreview={rl.loadPreview}
                          result={rl.result}
                          running={rl.running}
                          runError={rl.runError}
                          run={rl.run}
                          onViewBpmn={() => { setActiveTab("bpmn"); setActiveBpmnTab("rl"); }}
                        />
                      )}

                      {redesignMode === "bpr" && (
                        <BprRedesignPanel
                          processId={selectedId}
                          money={money}
                          fmtMin={fmtMin}
                          fixDecimals={fixDecimals}
                          healthy={bpr.healthy}
                          checkHealth={bpr.checkHealth}
                          result={bpr.result}
                          running={bpr.running}
                          runError={bpr.runError}
                          run={bpr.run}
                          onViewBpmn={() => { setActiveTab("bpmn"); setActiveBpmnTab("bpr"); }}
                        />
                      )}

                      {redesignMode === "agent" && (<>
                      {((redesignReport.applied_heuristics || []).length > 0 || Object.keys(adoptedHeuristics).length > 0) && (
                        <div style={{ marginTop: 8 }}>
                          <div style={{ fontSize: 12, fontWeight: 700, color: "#6b7280", marginBottom: 6 }}>APPLIED</div>
                          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                            {/* APPLIED = the optimization already executed this heuristic and its
                                measured effect is IN the TO-BE numbers — the strongest state on
                                this tab, so it reads unmistakably green. */}
                            {(redesignReport.applied_heuristics || []).map((h, i) => (
                              <div key={`a${i}`} style={{ background: "#f0fdf4", border: "1.5px solid #86efac", borderLeft: "4px solid #16a34a", borderRadius: 8, padding: "8px 12px" }}>
                                <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
                                  <span aria-hidden="true" style={{ color: "#16a34a", fontWeight: 800 }}>✓</span>
                                  <span style={{ fontSize: 13, fontWeight: 700, color: "#14532d" }}>{h.name}</span>
                                  <span style={{ fontSize: 11, fontWeight: 700, color: "#15803d", background: "#dcfce7", border: "1px solid #86efac", borderRadius: 999, padding: "1px 8px" }}>✓ applied — its effect is in the TO-BE numbers</span>
                                  {dqChips(h.code)}
                                </div>
                                {h.rationale && <div style={{ fontSize: 12, color: "#4d7c0f", marginTop: 3 }}>{h.rationale}</div>}
                              </div>
                            ))}
                            {Object.values(adoptedHeuristics).map((h) => (
                              <div key={h.code} style={{ background: "#f0fdfa", border: "1.5px solid #0e6b68", borderRadius: 8, padding: "8px 12px" }}>
                                <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
                                  <span style={{ fontSize: 13, fontWeight: 700 }}>{h.name}</span>
                                  <span style={{ fontSize: 11, color: "#0e6b68", background: "#ccfbf1", borderRadius: 999, padding: "1px 8px" }}>✓ adopted by you — recorded with the saved version</span>
                                  {dqChips(h.code)}
                                  <button
                                    type="button"
                                    className="secondary-btn sm"
                                    style={{ marginLeft: "auto", padding: "1px 10px", fontSize: 11 }}
                                    onClick={() => toggleAdoptHeuristic(h)}
                                  >
                                    Undo
                                  </button>
                                </div>
                                {h.basis && <div style={{ fontSize: 11, color: "#b45309", marginTop: 3 }}>because {h.basis}</div>}
                                {h.rationale && <div style={{ fontSize: 12, color: "#6b7280", marginTop: 3 }}>{h.rationale}</div>}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {(redesignReport.recommended_heuristics || []).filter((h) => !adoptedHeuristics[h.code]).length > 0 && (
                        <div style={{ marginTop: 16 }}>
                          <div style={{ fontSize: 12, fontWeight: 700, color: "#6b7280", marginBottom: 6 }}>RECOMMENDED — chosen from the measured bottleneck. Apply to adopt; adopted heuristics persist to the redesign scenario on Save.</div>
                          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                            {redesignReport.recommended_heuristics.filter((h) => !adoptedHeuristics[h.code]).map((h, i) => (
                              <div key={h.code || i} style={{ background: "#fffdf6", border: "1px solid #f0e7cf", borderRadius: 8, padding: "8px 12px" }}>
                                <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
                                  <span style={{ fontSize: 13, fontWeight: 700 }}>{h.name}</span>
                                  {h.basis && (<span style={{ fontSize: 11, color: "#b45309", background: "#fdf3d8", borderRadius: 999, padding: "1px 8px" }}>because {h.basis}</span>)}
                                  {dqChips(h.code)}
                                  <button
                                    type="button"
                                    className="primary-btn sm"
                                    style={{ marginLeft: "auto", padding: "1px 12px", fontSize: 11 }}
                                    onClick={() => toggleAdoptHeuristic(h)}
                                  >
                                    Apply
                                  </button>
                                </div>
                                {h.rationale && <div style={{ fontSize: 12, color: "#6b7280", marginTop: 3 }}>{h.rationale}</div>}
                                {expectedEffect(h)}
                                {h.impact_note && (
                                  <div style={{ marginTop: 4, fontSize: 11.5, display: "flex", alignItems: "baseline", gap: 6 }}>
                                    <span style={{
                                      flexShrink: 0, fontSize: 10, fontWeight: 800, borderRadius: 999, padding: "1px 8px",
                                      background: h.impact_computable ? "#dcfce7" : "#f1f5f9",
                                      color: h.impact_computable ? "#15803d" : "#475569",
                                      border: `1px solid ${h.impact_computable ? "#86efac" : "#e2e8f0"}`,
                                    }}>
                                      {h.impact_computable ? "measurable now" : "no immediate number change"}
                                    </span>
                                    <span style={{ color: "#6b7280" }}>{h.impact_note}</span>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {(redesignReport.applied_heuristics || []).length === 0 && (redesignReport.recommended_heuristics || []).length === 0 && (
                        <div className="no-results">No heuristics yet — run Optimize to analyze bottlenecks.</div>
                      )}

                      {/* Sub-process results sit BETWEEN the parent's heuristics and the
                          rules matrix below, because everything above this point is about
                          this process and everything in here is about a different one.
                          Unlike RL and BPR, greedy keeps the two completely separate: its
                          catalog_review carries no source="subprocess" rows, so these
                          would otherwise have nowhere to appear at all. */}
                      {(() => {
                        const projection = optimizationData?.subprocess_redesign_projection;
                        const subHeuristics = optimizationData?.subprocess_heuristics
                          || redesignReport.subprocess_heuristics;
                        if (!projection) return null;
                        return (
                          <div style={{ marginTop: 18, paddingTop: 14, borderTop: "1px solid #eef2f7" }}>
                            <SubprocessChildResults
                              projection={projection}
                              fmtMin={fmtMin}
                              money={money}
                            />
                            {Array.isArray(subHeuristics) && subHeuristics.length > 0 && (
                              <div style={{ marginTop: 14 }}>
                                <div style={{ fontSize: 12, fontWeight: 700, color: "#6b7280", marginBottom: 6 }}>
                                  HEURISTICS APPLIED INSIDE SUB-PROCESSES
                                </div>
                                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                                  {subHeuristics.map((h, i) => (
                                    <div key={`${h.code}-${h.subprocess?.child_process_id ?? i}`} style={{ background: "#fdfcff", border: "1px solid #ede9fe", borderLeft: "4px solid #7c3aed", borderRadius: 8, padding: "8px 12px" }}>
                                      <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
                                        <span aria-hidden="true" style={{ color: "#7c3aed", fontWeight: 800 }}>✓</span>
                                        <span style={{ fontSize: 13, fontWeight: 700, color: "#4c1d95" }}>{h.name}</span>
                                        {h.subprocess?.child_process_name && (
                                          <ChildChip name={h.subprocess.child_process_name} />
                                        )}
                                      </div>
                                      {/* measured_verdict is the one that states the child's
                                          own percentage and says so; `rationale` quotes the
                                          stored-to-to_be move, which on a child with a
                                          measurement_gap reads far larger than the real
                                          saving. Prefer the verdict. */}
                                      {(h.measured_verdict || h.rationale) && (
                                        <div style={{ fontSize: 12, color: "#6b7280", marginTop: 3 }}>
                                          {fixDecimals(h.measured_verdict || h.rationale)}
                                        </div>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })()}

                      {Array.isArray(redesignReport.catalog_review) && redesignReport.catalog_review.length > 0 && (() => {
                        // The RULES MATRIX: all 29 Table A.1 heuristics — the deterministic
                        // trigger each one is checked against, whether it was met (with the
                        // measured verdict), and whether applying it moves the CMS numbers.
                        // Reading order = decision order for the analyst:
                        //   1. APPLIED (measurable, executed — effect already in the TO-BE)
                        //   2. RECOMMENDED (worth doing, but its effect needs manual data
                        //      updates before the CMS can measure it)
                        //   3. MEASURABLE BUT NOT APPLIED (the engine could score it, the
                        //      process's current data just doesn't meet its trigger)
                        //   4. the rest (trigger not met AND not immediately measurable)
                        const groupOf = (h) =>
                          h.status === "applied" ? 0
                          : h.status === "recommended" ? 1
                          : h.impact_computable ? 2
                          : 3;
                        const GROUP_LABELS = [
                          "✓ Applied — measured effect already in the TO-BE numbers",
                          "Recommended — apply manually, then update the task data and re-run to measure",
                          "Measurable but not applied — the engine can score these; this process's data doesn't meet their trigger",
                          "Remaining catalog — trigger not met and no immediately measurable effect",
                        ];
                        const rows = [...redesignReport.catalog_review].sort(
                          (a, b) => groupOf(a) - groupOf(b) || String(a.name).localeCompare(String(b.name)),
                        );
                        const statusChip = (s) => {
                          const cfg = s === "applied"
                            ? { t: "✓ applied", bg: "#dcfce7", ink: "#15803d", bd: "#86efac" }
                            : s === "recommended"
                              ? { t: "recommended", bg: "#fdf3d8", ink: "#b45309", bd: "#fde68a" }
                              : { t: "rule not met", bg: "#f1f5f9", ink: "#64748b", bd: "#e2e8f0" };
                          return <span style={{ fontSize: 10, fontWeight: 800, whiteSpace: "nowrap", borderRadius: 999, padding: "1px 8px", background: cfg.bg, color: cfg.ink, border: `1px solid ${cfg.bd}` }}>{cfg.t}</span>;
                        };
                        const dqChipsSmall = (a) => ["time", "cost", "quality", "flexibility"]
                          .filter((k) => a && a[k])
                          .map((k) => (
                            <span key={k} style={{ fontSize: 9, fontWeight: 700, color: "#475569", background: "#eef2f7", borderRadius: 999, padding: "1px 6px", marginRight: 3, textTransform: "capitalize" }}>{k}</span>
                          ));
                        const counts = rows.reduce((m, h) => { m[h.status] = (m[h.status] || 0) + 1; return m; }, {});
                        return (
                          <div style={{ marginTop: 20 }}>
                            <div style={{ fontSize: 12, fontWeight: 700, color: "#6b7280", marginBottom: 6 }}>
                              HEURISTIC RULES MATRIX — all {rows.length} Table A.1 heuristics: the trigger rule the pipeline checks, whether it was met on this process, and whether applying it changes the CMS numbers.
                              <span style={{ fontWeight: 400 }}> ({counts.applied || 0} applied · {counts.recommended || 0} recommended · {counts.not_applicable || 0} rule not met)</span>
                            </div>
                            <div style={{ overflowX: "auto", border: "1px solid #eef2f7", borderRadius: 10 }}>
                              <table style={{ borderCollapse: "collapse", width: "100%", minWidth: 900, fontSize: 12 }}>
                                <thead>
                                  <tr>
                                    {["Heuristic", "Trigger / implementation rule", "Rule met?", "Measured verdict on this process", "DQ dimensions", "Measurable impact on CMS values?"].map((h, i) => (
                                      <th key={h} style={{ padding: "8px 10px", fontSize: 10.5, color: "#6b7280", textAlign: "left", textTransform: "uppercase", letterSpacing: "0.04em", borderBottom: "2px solid #e5e7eb", background: "#f8fafc", position: i === 0 ? "sticky" : undefined, left: i === 0 ? 0 : undefined, zIndex: i === 0 ? 1 : undefined }}>{h}</th>
                                    ))}
                                  </tr>
                                </thead>
                                <tbody>
                                  {rows.map((h, idx) => (
                                    <React.Fragment key={h.code}>
                                      {(idx === 0 || groupOf(rows[idx - 1]) !== groupOf(h)) && (
                                        <tr>
                                          <td colSpan={6} style={{ padding: "7px 10px", fontSize: 10.5, fontWeight: 800, letterSpacing: "0.04em", textTransform: "uppercase",
                                            background: groupOf(h) === 0 ? "#dcfce7" : groupOf(h) === 1 ? "#fef9c3" : groupOf(h) === 2 ? "#e0f2fe" : "#f1f5f9",
                                            color: groupOf(h) === 0 ? "#166534" : groupOf(h) === 1 ? "#854d0e" : groupOf(h) === 2 ? "#075985" : "#475569",
                                            borderTop: "1px solid #e5e7eb" }}>
                                            {GROUP_LABELS[groupOf(h)]}
                                          </td>
                                        </tr>
                                      )}
                                    <tr style={{ verticalAlign: "top", background: h.status === "applied" ? "#f0fdf4" : h.status === "recommended" ? "#fffdf6" : idx % 2 ? "#fcfcfd" : "#fff" }}>
                                      <td style={{ padding: "8px 10px", fontWeight: 700, whiteSpace: "nowrap", position: "sticky", left: 0, background: "inherit", zIndex: 1, ...(h.status === "applied" ? { boxShadow: "inset 3px 0 0 #16a34a" } : {}) }}>
                                        {h.status === "applied" && <span aria-hidden="true" style={{ color: "#16a34a", fontWeight: 800, marginRight: 4 }}>✓</span>}
                                        {h.name}
                                        <div style={{ fontSize: 9.5, fontWeight: 400, color: "#9ca3af", textTransform: "uppercase" }}>{String(h.category || "").replace(/_/g, " ")}</div>
                                      </td>
                                      <td style={{ padding: "8px 10px", color: "#374151", maxWidth: 280 }}>{h.rule || h.rule_of_thumb}</td>
                                      <td style={{ padding: "8px 10px" }}>{statusChip(h.status)}</td>
                                      <td style={{ padding: "8px 10px", color: "#6b7280", maxWidth: 300 }}>{fixDecimals(h.reason)}</td>
                                      <td style={{ padding: "8px 10px", whiteSpace: "nowrap" }}>{dqChipsSmall(h.affects)}</td>
                                      <td style={{ padding: "8px 10px", maxWidth: 260 }}>
                                        <span style={{ fontSize: 10, fontWeight: 800, borderRadius: 999, padding: "1px 8px", whiteSpace: "nowrap",
                                          background: h.impact_computable ? "#dcfce7" : "#f1f5f9",
                                          color: h.impact_computable ? "#15803d" : "#475569",
                                          border: `1px solid ${h.impact_computable ? "#86efac" : "#e2e8f0"}` }}>
                                          {h.impact_computable ? "measurable now" : "no immediate change"}
                                        </span>
                                        <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 3 }}>{h.impact_note}</div>
                                      </td>
                                    </tr>
                                    </React.Fragment>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        );
                      })()}
                      </>)}
                    </div>
                    );
                  })()}
                </div>
              );
            })()}

            {/* ── Analysis error banner (outside modal, shown on the BPMN tab) ── */}
            {analyzeError && activeTab === "bpmn" && !analyzing && (
              <div style={{ margin: "0 0 14px", padding: "12px 16px", background: "#fef2f2", border: "1px solid #fca5a5", borderRadius: 10, color: "#991b1b", fontSize: 13, display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                <span><strong>⚠ Analysis failed:</strong> {analyzeError}</span>
                <button type="button" className="primary-btn sm" onClick={() => runAnalysis(selectedId, { openModal: true })} disabled={analyzing}>
                  Retry
                </button>
              </div>
            )}

            {/* ── Analysis Results Modal ── */}
            <ProcessAdvisoryModal
              open={showAnalysisModal && !!analysisResult}
              processName={analysisResult?.process_name}
              verdict={analysisResult?.advisory?.overall_verdict}
              findings={analysisResult?.advisory?.findings}
              restructuring={restructuring}
              restructureError={restructureError}
              onRestructure={handleRestructureProcess}
              onCancel={() => setShowAnalysisModal(false)}
            />

            {/* Comparison Report Tab */}
            {activeTab === "comparison" && (
              <div className="po-tab-pane">
                <div className="role-card">
                  <div className="section-title">Comparison Report</div>
                  <ComparisonReportPanel
                    processId={selectedId}
                    money={money}
                    fmtMin={fmtMin}
                    num2={num2}
                    fixDecimals={fixDecimals}
                    result={comparisonReport.result}
                    running={comparisonReport.running}
                    runError={comparisonReport.runError}
                    run={comparisonReport.run}
                  />
                </div>
              </div>
            )}

            {/* 2) BPMN Tab */}
            {activeTab === "bpmn" && (
              <div className="po-tab-pane">
                <div className="role-card">
                  <div className="section-title">BPMN Diagram</div>
                  <div className="opt-network-tabs" role="tablist" aria-label="BPMN diagram view">
                    <button
                      role="tab"
                      aria-selected={activeBpmnTab === "current"}
                      className={`opt-tab-btn ${activeBpmnTab === "current" ? "active" : ""}`}
                      onClick={() => { setActiveBpmnTab("current"); setBpmnOffset({ x: 0, y: 0 }); }}
                    >
                      Current Process
                    </button>
                    <button
                      role="tab"
                      aria-selected={activeBpmnTab === "optimized"}
                      className={`opt-tab-btn ${activeBpmnTab === "optimized" ? "active" : ""}`}
                      onClick={() => { setActiveBpmnTab("optimized"); setBpmnOffset({ x: 0, y: 0 }); }}
                    >
                      Optimized Process
                    </button>
                    <button
                      role="tab"
                      aria-selected={activeBpmnTab === "advisory"}
                      className={`opt-tab-btn ${activeBpmnTab === "advisory" ? "active" : ""}`}
                      onClick={() => {
                        setActiveBpmnTab("advisory");
                        setBpmnOffset({ x: 0, y: 0 });
                        // Already prefetched on load: shows the modal if it has landed,
                        // queues it if still running, or re-runs it if it errored.
                        runAnalysis(selectedId, { openModal: true });
                      }}
                    >
                      Analyze Process
                      {advisoryImage && <span className="pa-ready-dot" title="Advisory BPMN ready" />}
                    </button>
                    <button
                      role="tab"
                      aria-selected={activeBpmnTab === "rl"}
                      className={`opt-tab-btn ${activeBpmnTab === "rl" ? "active" : ""}`}
                      onClick={() => { setActiveBpmnTab("rl"); setBpmnOffset({ x: 0, y: 0 }); }}
                    >
                      RL BPMN
                      {rl.result?.final_bpmn_xml && <span className="pa-ready-dot" title="RL redesign ready" />}
                    </button>
                    <button
                      role="tab"
                      aria-selected={activeBpmnTab === "bpr"}
                      className={`opt-tab-btn ${activeBpmnTab === "bpr" ? "active" : ""}`}
                      onClick={() => { setActiveBpmnTab("bpr"); setBpmnOffset({ x: 0, y: 0 }); }}
                    >
                      Model BPMN
                      {extractBprBpmnXml(bpr.result) && <span className="pa-ready-dot" title="BPR redesign ready" />}
                    </button>
                  </div>

                  {/* View mode: the interactive canvas is the CMS BPMN editor's engine in
                      read-only form — pan, zoom, and click any activity/gateway/event/flow
                      to identify it. The static image remains one toggle away. RL BPMN and
                      Model BPMN have no static-image equivalent (both APIs return XML only,
                      when they return a diagram at all), so the toggle is hidden for them. */}
                  {activeBpmnTab !== "rl" && activeBpmnTab !== "bpr" && (
                  <div style={{ display: "flex", alignItems: "center", gap: 8, margin: "10px 0 14px", flexWrap: "wrap" }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.05em" }}>View as</span>
                    <button
                      type="button"
                      className={`opt-tab-btn ${bpmnViewMode === "interactive" ? "active" : ""}`}
                      style={{ padding: "4px 14px", fontSize: 12 }}
                      onClick={() => setBpmnViewMode("interactive")}
                      aria-pressed={bpmnViewMode === "interactive"}
                    >
                      Interactive diagram
                    </button>
                    <button
                      type="button"
                      className={`opt-tab-btn ${bpmnViewMode === "image" ? "active" : ""}`}
                      style={{ padding: "4px 14px", fontSize: 12 }}
                      onClick={() => setBpmnViewMode("image")}
                      aria-pressed={bpmnViewMode === "image"}
                    >
                      Static image
                    </button>
                    {bpmnViewMode === "interactive" && (
                      <button
                        type="button"
                        className="secondary-btn sm"
                        onClick={() => loadBpmnXml(activeBpmnTab, selectedId)}
                        disabled={bpmnXmlLoading}
                        aria-label="Reload the diagram from the server"
                      >
                        {bpmnXmlLoading ? "Loading…" : "↻ Refresh diagram"}
                      </button>
                    )}
                  </div>
                  )}

                  {bpmnViewMode === "interactive" && (activeBpmnTab === "current" || activeBpmnTab === "optimized") && (
                    <div>
                      {bpmnXmlError && !bpmnXmlLoading && (
                        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", padding: "10px 14px", marginBottom: 10, background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 10, color: "#b91c1c", fontSize: 12.5 }}>
                          <span style={{ flex: 1, minWidth: 220 }}>{bpmnXmlError}</span>
                          <button
                            type="button"
                            className="secondary-btn sm"
                            onClick={() => loadBpmnXml(activeBpmnTab, selectedId)}
                            aria-label="Retry loading the diagram"
                          >
                            ↻ Retry
                          </button>
                        </div>
                      )}
                      <BpmnInteractiveViewer
                        xml={bpmnXml[activeBpmnTab]}
                        height={520}
                        emptyMessage={bpmnXmlLoading ? "Loading the diagram…" : `No ${activeBpmnTab} diagram loaded yet.`}
                        onViewerReady={setInteractiveViewer}
                      />
                      {(() => {
                        // TOKEN WALKTHROUGH — replays backend-enumerated routes (same
                        // paths[] the Path View table below reads) on the diagram above.
                        // `simulation` adds the all-cases mode: its path_case_counts say
                        // how the 10,000 Monte-Carlo cases split across those routes.
                        const flow = activeBpmnTab === "current" ? redesignFlow?.as_is : redesignFlow?.to_be;
                        if (!Array.isArray(flow?.paths) || !flow.paths.length) return null;
                        return <BpmnPathAnimator viewer={interactiveViewer} paths={flow.paths} tasks={flow.tasks} simulation={flow.simulation} />;
                      })()}
                      {(() => {
                        // ── SIMULATION (FBPM Ch.7 §7.4) on the diagram tab: the Monte-Carlo
                        // distribution behind the flow the diagram shows. AS-IS for Current,
                        // active-scenario TO-BE for Optimized.
                        const sim = activeBpmnTab === "current"
                          ? redesignFlow?.as_is?.simulation
                          : redesignFlow?.to_be?.simulation || redesignFlow?.as_is?.simulation;
                        if (!sim?.cycle_time_minutes) return null;
                        const hist = sim.histogram_cycle_minutes || [];
                        const maxShare = Math.max(...hist.map((h) => h.share_pct), 1);
                        return (
                          <div style={{ marginTop: 14, padding: "12px 16px", border: "1px solid #eef2f7", borderRadius: 10, background: "#fcfcfd" }}>
                            <div style={{ fontSize: 12, fontWeight: 700, color: "#6b7280", marginBottom: 2 }}>
                              SIMULATION — {sim.iterations.toLocaleString()} Monte-Carlo runs of the {activeBpmnTab === "current" ? "current" : "optimized"} flow shown above
                            </div>
                            <div style={{ fontSize: 11.5, color: "#64748b", marginBottom: 8 }}>
                              Each run walks one path of this diagram (chosen by the recorded branch probabilities) with task times drawn ±20% around
                              their expected values — a stated assumption until optimistic/pessimistic times are recorded. Deterministic seed: same data, same result.
                            </div>
                            <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginBottom: 10 }}>
                              {[["P50 (median)", sim.cycle_time_minutes.p50], ["P90", sim.cycle_time_minutes.p90], ["P95", sim.cycle_time_minutes.p95]].map(([lb, v]) => (
                                <div key={lb} style={{ border: "1px solid #e5e7eb", borderRadius: 8, padding: "6px 12px", background: "#fff" }}>
                                  <div style={{ fontSize: 10, fontWeight: 700, color: "#6b7280", textTransform: "uppercase" }}>{lb}</div>
                                  <div style={{ fontSize: 14, fontWeight: 800, color: "#0e6b68", fontVariantNumeric: "tabular-nums" }}>{fmtMin(v)}</div>
                                </div>
                              ))}
                              <div style={{ border: "1px solid #e5e7eb", borderRadius: 8, padding: "6px 12px", background: "#fff" }}>
                                <div style={{ fontSize: 10, fontWeight: 700, color: "#6b7280", textTransform: "uppercase" }}>Cost P90</div>
                                <div style={{ fontSize: 14, fontWeight: 800, color: "#1d4ed8", fontVariantNumeric: "tabular-nums" }}>{money(sim.labor_cost_usd?.p90 ?? 0)}</div>
                              </div>
                            </div>
                            {hist.length > 0 && (
                              <div style={{ display: "flex", gap: 8 }}>
                                {/* Y axis: share of simulated cases */}
                                <div style={{ display: "flex", flexDirection: "column", justifyContent: "space-between", alignItems: "flex-end", height: 84, paddingBottom: 22 }}>
                                  <span style={{ fontSize: 9, color: "#9ca3af", fontVariantNumeric: "tabular-nums" }}>{Math.round(maxShare)}%</span>
                                  <span style={{ fontSize: 9, color: "#9ca3af", fontVariantNumeric: "tabular-nums" }}>{Math.round(maxShare / 2)}%</span>
                                  <span style={{ fontSize: 9, color: "#9ca3af" }}>0%</span>
                                </div>
                                <div style={{ flex: 1 }}>
                                  <div
                                    style={{ display: "flex", alignItems: "flex-end", gap: 3, height: 62, borderLeft: "1px solid #e5e7eb", borderBottom: "1px solid #e5e7eb", paddingLeft: 2 }}
                                    role="img"
                                    aria-label={`Histogram: share of the ${sim.iterations.toLocaleString()} simulated cases (vertical axis, 0 to ${Math.round(maxShare)} percent) by cycle time per case (horizontal axis, ${Math.round(hist[0].from_minutes)} to ${Math.round(hist[hist.length - 1].to_minutes)} minutes)`}
                                  >
                                    {hist.map((h, i) => (
                                      <div key={i} title={`${Math.round(h.from_minutes)}–${Math.round(h.to_minutes)} min: ${h.share_pct}% of simulated cases`}
                                        style={{ flex: 1, height: `${Math.max(3, (h.share_pct / maxShare) * 100)}%`, background: "#0d9488", opacity: 0.5 + 0.5 * (h.share_pct / maxShare), borderRadius: "3px 3px 0 0" }} />
                                    ))}
                                  </div>
                                  {/* X axis: cycle-time bins with start/middle/end ticks */}
                                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 9.5, color: "#6b7280", marginTop: 2, fontVariantNumeric: "tabular-nums" }}>
                                    <span>{Math.round(hist[0].from_minutes)} min</span>
                                    <span>{Math.round((hist[0].from_minutes + hist[hist.length - 1].to_minutes) / 2)} min</span>
                                    <span>{Math.round(hist[hist.length - 1].to_minutes)} min</span>
                                  </div>
                                  <div style={{ textAlign: "center", fontSize: 10, fontWeight: 700, color: "#6b7280", marginTop: 2 }}>
                                    Cycle time per case (minutes) — bar height = % of {sim.iterations.toLocaleString()} simulated cases
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  )}

                  {bpmnViewMode === "interactive" && activeBpmnTab === "advisory" && (
                    <div>
                      {analyzing ? (
                        <AnalyzeProgress />
                      ) : (
                        <>
                          {bpmnXmlError && !bpmnXmlLoading && (
                            <div style={{ padding: "10px 14px", marginBottom: 10, background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 10, color: "#92400e", fontSize: 12.5 }}>
                              {bpmnXmlError}
                            </div>
                          )}
                          <BpmnInteractiveViewer
                            xml={bpmnXml.advisory}
                            height={520}
                            emptyMessage={bpmnXmlLoading ? "Loading the analyzed diagram…" : "The analyzed (restructured) diagram appears here once the analysis has run."}
                          />
                        </>
                      )}
                    </div>
                  )}

                  {bpmnViewMode === "image" && (activeBpmnTab === "current" || activeBpmnTab === "optimized") && (
                  <div
                    className="opt-graph-display"
                    onMouseMove={(e) => {
                      const d = bpmnDragRef.current;
                      if (!d.active) return;
                      setBpmnOffset({ x: d.baseX + (e.clientX - d.startX), y: d.baseY + (e.clientY - d.startY) });
                    }}
                    onMouseUp={() => {
                      bpmnDragRef.current = { active: false, startX: 0, startY: 0, baseX: 0, baseY: 0 };
                    }}
                    onMouseLeave={() => {
                      bpmnDragRef.current = { active: false, startX: 0, startY: 0, baseX: 0, baseY: 0 };
                    }}
                  >
                    {bpmnLoading ? (
                      <div style={{ padding: "40px", textAlign: "center", color: "#6b7280" }}>
                        Loading BPMN...
                      </div>
                    ) : !currentBpmnImage ? (
                      <div style={{ padding: "40px", textAlign: "center", color: "#6b7280" }}>
                        No BPMN available for {activeBpmnTab} process
                      </div>
                    ) : (
                      <div className="opt-graph-container">
                        <div
                          className="po-zoom-wrap"
                          onMouseDown={(e) => {
                            if (bpmnZoom <= 1) return;
                            bpmnDragRef.current = {
                              active: true,
                              startX: e.clientX,
                              startY: e.clientY,
                              baseX: bpmnOffset.x,
                              baseY: bpmnOffset.y,
                            };
                          }}
                          style={{ width: "100%", overflow: "hidden", cursor: bpmnZoom > 1 ? "grab" : "default" }}
                        >
                          <img
                            src={currentBpmnImage}
                            alt={`${activeBpmnTab === "current" ? "Current" : "Optimized"} BPMN`}
                            className="opt-graph-image-large"
                            style={{
                              transform: `translate(${bpmnOffset.x}px, ${bpmnOffset.y}px) scale(${bpmnZoom})`,
                              transformOrigin: "center center",
                            }}
                          />
                        </div>
                        <div className="po-zoom-controls" style={{ marginTop: 12 }}>
                          <button
                            type="button"
                            onClick={() => setBpmnZoom((z) => {
                              const nz = Math.max(0.1, Math.min(2, z - 0.05));
                              if (nz === 0.1) setBpmnOffset({ x: 0, y: 0 });
                              return nz;
                            })}
                            title="Zoom Out"
                            aria-label="Zoom Out"
                          >
                            −
                          </button>
                          <span className="po-zoom-level">{Math.round(bpmnZoom * 100)}%</span>
                          <button
                            type="button"
                            onClick={() => setBpmnZoom((z) => Math.max(0.1, Math.min(2, z + 0.05)))}
                            title="Zoom In"
                            aria-label="Zoom In"
                          >
                            +
                          </button>
                          <button
                            type="button"
                            onClick={() => { setBpmnZoom(1); setBpmnOffset({ x: 0, y: 0 }); }}
                            title="Reset"
                            aria-label="Reset"
                          >
                            Reset
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                  )}

                  {/* ── Advisory Process panel ── (same container as Current/Optimized) */}
                  {bpmnViewMode === "image" && activeBpmnTab === "advisory" && (
                    <div
                      className="opt-graph-display"
                      onMouseMove={(e) => {
                        const d = bpmnDragRef.current;
                        if (!d.active) return;
                        setBpmnOffset({ x: d.baseX + (e.clientX - d.startX), y: d.baseY + (e.clientY - d.startY) });
                      }}
                      onMouseUp={() => { bpmnDragRef.current = { active: false, startX: 0, startY: 0, baseX: 0, baseY: 0 }; }}
                      onMouseLeave={() => { bpmnDragRef.current = { active: false, startX: 0, startY: 0, baseX: 0, baseY: 0 }; }}
                    >
                      {analyzing ? (
                        <AnalyzeProgress />
                      ) : advisoryImage ? (
                        <div className="opt-graph-container">
                          <div
                            className="po-zoom-wrap"
                            onMouseDown={(e) => {
                              if (bpmnZoom <= 1) return;
                              bpmnDragRef.current = { active: true, startX: e.clientX, startY: e.clientY, baseX: bpmnOffset.x, baseY: bpmnOffset.y };
                            }}
                            style={{ width: "100%", overflow: "hidden", cursor: bpmnZoom > 1 ? "grab" : "default" }}
                          >
                            <img
                              src={advisoryImage}
                              alt="Advisory / Restructured BPMN"
                              className="opt-graph-image-large"
                              style={{ transform: `translate(${bpmnOffset.x}px, ${bpmnOffset.y}px) scale(${bpmnZoom})`, transformOrigin: "center center" }}
                            />
                          </div>
                          <div className="po-zoom-controls" style={{ marginTop: 12 }}>
                            <button type="button" onClick={() => setBpmnZoom((z) => { const nz = Math.max(0.1, Math.min(2, z - 0.05)); if (nz === 0.1) setBpmnOffset({ x: 0, y: 0 }); return nz; })} title="Zoom Out" aria-label="Zoom Out">−</button>
                            <span className="po-zoom-level">{Math.round(bpmnZoom * 100)}%</span>
                            <button type="button" onClick={() => setBpmnZoom((z) => Math.max(0.1, Math.min(2, z + 0.05)))} title="Zoom In" aria-label="Zoom In">+</button>
                            <button type="button" onClick={() => { setBpmnZoom(1); setBpmnOffset({ x: 0, y: 0 }); }} title="Reset" aria-label="Reset">Reset</button>
                          </div>
                        </div>
                      ) : (
                        <div className="pa-advisory-placeholder">
                          <div className="pa-advisory-icon">🔍</div>
                          <div className="pa-advisory-title">No Advisory Diagram Yet</div>
                          <div className="pa-advisory-desc">
                            Click <strong>Analyze Process</strong> to run the BPMN advisory analysis,
                            then <strong>Restructure Process</strong> to auto-fix issues and generate the revised diagram.
                          </div>
                          <button
                            type="button"
                            className="primary-btn"
                            onClick={() => runAnalysis(selectedId, { openModal: true })}
                            disabled={analyzing}
                            style={{ marginTop: 16 }}
                          >
                            {analyzing ? "Analyzing…" : "Analyze Process"}
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                  {/* ── RL BPMN — the redesigned diagram from the last RL run (separate
                      pipeline, /cms/rl-optimize). Shows whatever goal was last run
                      (e.g. "Fastest" if that's what the analyst picked); no image mode,
                      the RL API only returns XML. ── */}
                  {activeBpmnTab === "rl" && (
                    <div>
                      {rl.result?.final_bpmn_xml ? (
                        <>
                          <div style={{ marginBottom: 10, fontSize: 12.5, color: "#0e6b68", background: "#f0fdfa", border: "1px solid #99f6e4", borderRadius: 8, padding: "8px 12px" }}>
                            Last RL run — goal: <strong>{RL_GOAL_OPTIONS.find((g) => g.key === rl.result.goal)?.label || rl.result.goal}</strong>
                            {rl.result.overall_improvement && (
                              <>
                                {" · "}cycle time {rl.result.overall_improvement.time_improvement_pct != null ? `${rl.result.overall_improvement.time_improvement_pct > 0 ? "▼" : "▲"} ${Math.abs(rl.result.overall_improvement.time_improvement_pct).toFixed(1)}%` : "—"}
                                {" · "}cost {rl.result.overall_improvement.cost_improvement_pct != null ? `${rl.result.overall_improvement.cost_improvement_pct > 0 ? "▼" : "▲"} ${Math.abs(rl.result.overall_improvement.cost_improvement_pct).toFixed(1)}%` : "—"}
                              </>
                            )}
                          </div>
                          <BpmnInteractiveViewer xml={rl.result.final_bpmn_xml} height={520} emptyMessage="No RL-redesigned diagram available." />
                        </>
                      ) : rl.running ? (
                        <div className="no-results">Training the RL agent (typically 4–15s)…</div>
                      ) : (
                        <div className="pa-advisory-placeholder">
                          <div className="pa-advisory-title">No RL Redesign Yet</div>
                          <div className="pa-advisory-desc">
                            Go to the <strong>Redesign</strong> tab, switch to <strong>RL</strong>, pick a goal, and run it — the
                            redesigned diagram from your last run will appear here.
                          </div>
                          <button
                            type="button"
                            className="primary-btn"
                            onClick={() => { setActiveTab("redesign"); setRedesignMode("rl"); }}
                            style={{ marginTop: 16 }}
                          >
                            Go to Redesign → RL
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                  {/* ── Model BPMN — the TO-BE diagram from the last BPR Redesign run
                      (separate pipeline, /cms/bpr-redesign, an external already-trained ML
                      model), real BPMN 2.0 + BPMNDI XML from the endpoint itself (bpmn_xml);
                      every applied heuristic that has a BPMN-representable effect is already
                      reflected in its shape (eliminated tasks gone, composed tasks merged,
                      parallelized pairs wrapped in a split/join gateway) — knock_out and
                      extra_resources are the two heuristics that never change the diagram even
                      when applied, which is expected, not a bug. bpmn_xml_source tells us
                      whether the diagram came from a stored source, was generated on the fly,
                      or (source: "missing") couldn't be produced at all — in that last case
                      bpmn_xml is null, an expected/harmless case, and we show an explicit
                      "unavailable" placeholder rather than an empty canvas. ── */}
                  {activeBpmnTab === "bpr" && (() => {
                    const bprToBeXml = extractBprBpmnXml(bpr.result);
                    const bprSource = getBprBpmnSource(bpr.result);
                    const bprMissing = bpr.result && (bprSource === "missing" || !bprToBeXml);
                    return (
                    <div>
                      {bprToBeXml && !bprMissing ? (
                        <>
                          <div style={{ marginBottom: 10, fontSize: 12.5, color: "#0e6b68", background: "#f0fdfa", border: "1px solid #99f6e4", borderRadius: 8, padding: "8px 12px" }}>
                            Last BPR Redesign run
                            {bpr.result?.cycle_time_reduction_pct != null && (
                              <>{" · "}cycle time {bpr.result.cycle_time_reduction_pct > 0 ? "▼" : "▲"} {Math.abs(bpr.result.cycle_time_reduction_pct).toFixed(1)}%</>
                            )}
                            {bpr.result?.labor_cost_reduction_pct != null && (
                              <>{" · "}cost {bpr.result.labor_cost_reduction_pct > 0 ? "▼" : "▲"} {Math.abs(bpr.result.labor_cost_reduction_pct).toFixed(1)}%</>
                            )}
                          </div>
                          <BpmnInteractiveViewer xml={bprToBeXml} height={520} emptyMessage="No BPR-redesigned diagram available." />
                        </>
                      ) : bpr.running ? (
                        <div className="no-results">Sending this process to the external BPR Redesign model…</div>
                      ) : bpr.result ? (
                        <div className="pa-advisory-placeholder">
                          <div className="pa-advisory-title">Diagram Unavailable</div>
                          <div className="pa-advisory-desc">
                            Diagram unavailable for this process — the BPR Redesign result came back but
                            no BPMN diagram could be produced (a common, harmless case) — every other
                            result (cycle time, cost, heuristics) is still available on the
                            <strong> Redesign → Model</strong> tab.
                          </div>
                        </div>
                      ) : (
                        <div className="pa-advisory-placeholder">
                          <div className="pa-advisory-icon">🔍</div>
                          <div className="pa-advisory-title">No BPR Redesign Yet</div>
                          <div className="pa-advisory-desc">
                            Go to the <strong>Redesign</strong> tab, switch to <strong>Model</strong>, and run it — the
                            redesigned diagram from your last run will appear here.
                          </div>
                          <button
                            type="button"
                            className="primary-btn"
                            onClick={() => { setActiveTab("redesign"); setRedesignMode("bpr"); }}
                            style={{ marginTop: 16 }}
                          >
                            Go to Redesign → Model
                          </button>
                        </div>
                      )}
                    </div>
                    );
                  })()}

                  {activeBpmnTab !== "rl" && activeBpmnTab !== "bpr" && (() => {
                    // PATH VIEW — the exact execution routes the flow-analysis numbers were
                    // computed from (backend-enumerated). Current shows AS-IS; Optimized and
                    // Analyze (restructured) reuse the TO-BE paths. Not applicable to RL BPMN
                    // or Model BPMN, which come from separate pipelines with their own (or no) path data.
                    const flow = activeBpmnTab === "current"
                      ? optimizationData?.redesign_flow?.as_is
                      : optimizationData?.redesign_flow?.to_be;
                    const other = activeBpmnTab === "current"
                      ? optimizationData?.redesign_flow?.to_be
                      : optimizationData?.redesign_flow?.as_is;
                    const rows = Array.isArray(flow?.paths) ? flow.paths : [];
                    if (!rows.length) return null;
                    const identical = JSON.stringify(flow?.paths) === JSON.stringify(other?.paths);
                    // Critical path = the longest enumerated route: it bounds the cycle
                    // time and is where the CTE numerator's processing time lives (Ch.7).
                    const maxDur = Math.max(...rows.map((p) => Number(p.duration_minutes) || 0));
                    const criticalIdx = maxDur > 0 ? rows.findIndex((p) => (Number(p.duration_minutes) || 0) === maxDur) : -1;
                    const dimCount = sharedPrefixLength(rows);
                    return (
                      <div style={{ marginTop: 16 }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: "#6b7280", marginBottom: 6 }}>
                          PATH VIEW ({activeBpmnTab === "current" ? "current AS-IS" : "optimized TO-BE"}) — every execution route with its probability and duration
                          {identical && <span style={{ fontWeight: 400 }}> · identical to the {activeBpmnTab === "current" ? "TO-BE" : "AS-IS"} (the redesign proposes no structural change)</span>}
                          {criticalIdx >= 0 && <span style={{ fontWeight: 400 }}> · the highlighted route is the <b style={{ color: "#b45309" }}>critical path</b> (longest duration — it bounds the cycle time)</span>}
                          {dimCount > 0 && <span style={{ fontWeight: 400 }}> · the first {dimCount} step{dimCount === 1 ? "" : "s"} are shared by every route (shown <span style={{ color: "#94a3b8" }}>dimmed</span>) — colour marks where each path diverges</span>}
                        </div>
                        <div style={{ overflowX: "auto" }}>
                          <table style={{ borderCollapse: "collapse", width: "100%", minWidth: 520, tableLayout: "fixed", fontSize: 12 }}>
                            <colgroup>
                              <col style={{ width: 40 }} />
                              <col style={{ width: 96 }} />
                              <col style={{ width: 140 }} />
                              <col />
                            </colgroup>
                            <thead>
                              <tr>
                                {["#", "Probability", "Duration (min)", "Route"].map((h, i) => (
                                  <th key={h} style={{ padding: "6px 10px", fontSize: 11, color: "#6b7280", textAlign: i === 3 ? "left" : "right", borderBottom: "1px solid #e5e7eb" }}>{h}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {rows.map((p, i) => (
                                <tr key={i} style={i === criticalIdx ? { background: "#fef7e6", boxShadow: "inset 3px 0 0 #b45309" } : undefined}>
                                  <td style={{ padding: "6px 10px", textAlign: "right", color: "#9ca3af" }}>{i + 1}</td>
                                  <td style={{ padding: "6px 10px", textAlign: "right", fontWeight: 700 }}>{(p.probability * 100).toFixed(1)}%</td>
                                  <td style={{ padding: "6px 10px", textAlign: "right" }}>
                                    {p.duration_minutes}
                                    {i === criticalIdx && (
                                      <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 800, color: "#b45309", background: "#fde68a55", border: "1px solid #f4cf7f", borderRadius: 6, padding: "1px 6px", whiteSpace: "nowrap" }}>⏱ critical path</span>
                                    )}
                                  </td>
                                  <td style={{ padding: "6px 10px", maxWidth: 0 }} title={(p.route || []).join(" → ") || "straight to End"}>
                                    <PathFlowStrip route={p.route} dimCount={dimCount} />
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>
            )}

            {/* 3) Cost Optimization Tab */}
            {activeTab === "analyze" && co?.enabled && (
              <div className="po-tab-pane">
                <div className="role-card">
                  <div className="section-title">Cost Optimization</div>
                  <div className="opt-cost-card">
                    <div style={{ position: "relative", zIndex: 1 }}>
                      {(() => {
                        // Single source of truth: the same backend (Ch.7) expected labor
                        // cost per case the Overview baseline shows — never the agent's
                        // unweighted assignment-pool sum (which disagreed with Overview).
                        const asF = optimizationData?.redesign_flow?.as_is || {};
                        const tbF = optimizationData?.redesign_flow?.to_be || {};
                        const orig = asF.labor_cost_per_case ?? co.summary.originalTotalCost ?? 0;
                        const opt = tbF.labor_cost_per_case ?? co.summary.optimizedTotalCost ?? 0;
                        const savings = Math.max(0, orig - opt);
                        const pct = orig > 0 ? ((savings / orig) * 100).toFixed(1) : 0;
                        const replaced = (co.replacements || []).length;
                        return (
                          <>
                            <p className="opt-section-description">
                              {replaced > 0
                                ? co.description
                                : (co.message || "No cheaper skill-qualified replacement was found — current job assignments are already cost-optimal for this company's candidate pool.")}
                            </p>
                            <div className="opt-cost-summary">
                              <div className="opt-cost-metric original">
                                <div className="opt-cost-metric-label">Cost / case (AS-IS)</div>
                                <div className="opt-cost-metric-value">{money(orig)}</div>
                              </div>
                              <div className="opt-arrow-container">
                                <div className="opt-cost-arrow-animated"><span>→</span></div>
                              </div>
                              <div className="opt-cost-metric optimized">
                                <div className="opt-cost-metric-label">Cost / case (TO-BE)</div>
                                <div className="opt-cost-metric-value">{money(opt)}</div>
                              </div>
                              <div className="opt-cost-metric savings">
                                <div className="opt-cost-metric-label">Savings / case</div>
                                <div className="opt-cost-metric-value">
                                  {money(savings)}
                                  <span className="opt-percentage-badge">{pct}%</span>
                                </div>
                              </div>
                            </div>
                            <div className="opt-tasks-info-bar">
                              <span>Tasks with cheaper reassignments: </span>
                              <strong>{co.summary.tasksOptimized || 0}/{co.summary.tasksAnalyzed}</strong>
                              {replaced > (co.summary.tasksOptimized || 0) && (
                                <span style={{ marginLeft: 6, fontSize: 12, color: "#6b7280" }}>
                                  ({replaced} role assignment(s) swapped — a task can have several role-holders replaced)
                                </span>
                              )}
                              <span style={{ marginLeft: 12, fontSize: 11, color: "#6b7280" }}>
                                Costs are the backend's path-weighted expected labor cost per case — identical to the Overview baseline.
                              </span>
                            </div>
                          </>
                        );
                      })()}
                      {Array.isArray(co.replacements) && co.replacements.length > 0 && (
                        <details className="opt-details-section" open={appliedSelection != null || Object.keys(deselectedReplacements).length > 0 || undefined}>
                          <summary className="opt-details-summary cost">
                            View Detailed Replacements ({co.replacements.length})
                          </summary>
                          <div className="opt-details-content">
                            {/* ── Analyst decision bar: keep all, some, or none of the swaps ── */}
                            {(() => {
                              const total = co.replacements.length;
                              const chosenCount = co.replacements.filter((r) => !deselectedReplacements[replacementKey(r)]).length;
                              return (
                                <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", padding: "10px 12px", marginBottom: 12, background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 10 }}>
                                  <span style={{ fontSize: 12.5, color: "#334155" }}>
                                    You decide which reassignments count: untick any swap, then re-score.
                                    The TO-BE cost is recomputed by the backend engine and every tab updates to the accepted set.
                                  </span>
                                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginLeft: "auto", flexWrap: "wrap" }}>
                                    <button
                                      type="button"
                                      className="secondary-btn sm"
                                      onClick={() => setDeselectedReplacements({})}
                                      disabled={applyingSelection || Object.keys(deselectedReplacements).length === 0}
                                      aria-label="Reselect every suggested reassignment"
                                    >
                                      Select all
                                    </button>
                                    <button
                                      type="button"
                                      className="secondary-btn sm"
                                      onClick={() => setDeselectedReplacements(Object.fromEntries(co.replacements.map((r) => [replacementKey(r), true])))}
                                      disabled={applyingSelection || chosenCount === 0}
                                      aria-label="Deselect every suggested reassignment"
                                    >
                                      Select none
                                    </button>
                                    <button
                                      type="button"
                                      className="primary-btn sm"
                                      onClick={applySelectedReplacements}
                                      disabled={applyingSelection}
                                      aria-label={`Re-score the process with ${chosenCount} of ${total} reassignments applied`}
                                    >
                                      {applyingSelection ? "Re-scoring…" : `Apply selected (${chosenCount}/${total})`}
                                    </button>
                                  </div>
                                  {appliedSelection != null && !applyingSelection && (
                                    <span style={{ flexBasis: "100%", fontSize: 12, fontWeight: 700, color: "#0f766e" }}>
                                      ✓ TO-BE now reflects {appliedSelection.applied} of {appliedSelection.total} reassignment(s) — Overview, AS-IS vs TO-BE and the scenario cards are updated.
                                      {appliedSelection.applied === 0 && " With none accepted, the TO-BE cost equals the AS-IS cost."}
                                    </span>
                                  )}
                                  {selectionError && (
                                    <span role="alert" style={{ flexBasis: "100%", fontSize: 12, color: "#b91c1c" }}>{selectionError}</span>
                                  )}
                                  {selectionWarnings.map((w) => (
                                    <span key={w} role="alert" style={{ flexBasis: "100%", fontSize: 12, color: "#b45309" }}>⚠ {w}</span>
                                  ))}
                                </div>
                              );
                            })()}
                            {co.replacements.map((r) => (
                              // One task can carry several replacements (one per replaced
                              // role-holder) — key on the replaced job too, or React
                              // collapses/mis-renders the duplicates.
                              <div
                                key={`${r.task_id}-${r.original_job_id ?? r.original_job?.job_id ?? "j"}`}
                                className="opt-replacement-card"
                                style={deselectedReplacements[replacementKey(r)] ? { opacity: 0.55 } : undefined}
                              >
                                <div className="replacement-header">
                                  <label style={{ display: "inline-flex", alignItems: "center", gap: 6, marginRight: 10, cursor: "pointer", fontSize: 12, fontWeight: 700, color: "#334155" }}>
                                    <input
                                      type="checkbox"
                                      checked={!deselectedReplacements[replacementKey(r)]}
                                      onChange={(ev) => {
                                        const k = replacementKey(r);
                                        const on = ev.target.checked;
                                        setDeselectedReplacements((p) => {
                                          const n = { ...p };
                                          if (on) delete n[k];
                                          else n[k] = true;
                                          return n;
                                        });
                                      }}
                                      aria-label={`Include the reassignment of ${r.original_job?.name || r.original_job_name || "this job"} on ${r.task_name || "this task"}`}
                                    />
                                    Include
                                  </label>
                                  <span className="task-label">Task:</span>
                                  <span className="task-name">{r.task_name || "-"}</span>
                                </div>
                                {(r.kept_job || (Array.isArray(r.removed_jobs) && r.removed_jobs.length > 0)) && (
                                  <div className="resolution-jobs-section">
                                    {r.kept_job && (
                                      <div className="kept-job-badge">
                                        <span className="job-status-label">Kept:</span>
                                        <span className="job-name">
                                          {typeof r.kept_job === "object" ? r.kept_job.name || "-" : r.kept_job}
                                        </span>
                                      </div>
                                    )}
                                    {Array.isArray(r.removed_jobs) && r.removed_jobs.length > 0 && (
                                      <div className="removed-jobs-container">
                                        <span className="removed-label">Removed:</span>
                                        <div className="removed-jobs-list">
                                          {r.removed_jobs.map((job) => (
                                            <span key={typeof job === "object" ? job.name : job} className="removed-job-badge">
                                              {typeof job === "object" ? job.name || "-" : job}
                                            </span>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                )}
                                <div className="replacement-flow">
                                  <div className="job-badge original">
                                    <div className="job-info">
                                      <span className="job-name">{r.original_job?.name || "-"}</span>
                                      <span className="job-rate">{money(r.original_job?.hourlyRate ?? 0)}/hr</span>
                                    </div>
                                  </div>
                                  <div className="flow-arrow">→</div>
                                  <div className="job-badge new">
                                    <div className="job-info">
                                      <span className="job-name">{r.new_job?.name || "-"}</span>
                                      <span className="job-rate">{money(r.new_job?.hourlyRate ?? 0)}/hr</span>
                                    </div>
                                  </div>
                                </div>
                                <div className="opt-savings-badge-enhanced">
                                  Savings: {money(r.cost_savings ?? 0)}
                                </div>
                                {(r.explanation || r.reason) && (
                                  <div className="replacement-explanation">{fixDecimals(r.explanation || r.reason)}</div>
                                )}
                                {r.raci_role && (
                                  <div style={{ marginTop: 4, fontSize: 11, color: "#6b7280" }}>
                                    Replaced role: <span className={`raci-badge raci-${String(r.raci_role).toLowerCase()}`}>{r.raci_role}</span>
                                    <span style={{ marginLeft: 6 }}>— the swap changes this role-holder's rate; it shows in the TO-BE cost, the RACI tables, and the “Resources only” scenario.</span>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </details>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* 4) Workload Balancing Tab */}
            {activeTab === "analyze" && (
              <div className="po-tab-pane">
                {/* Sub-process steps hold no job at THIS level (their people work inside the
                    child process), so they can never appear in the job-centric workload
                    table below. List them explicitly with their composite cost/time and the
                    child's own RACI, so this tab accounts for the whole process. */}
                {(() => {
                  const subs = (optimizationData?.raci_assignments?.tasks || []).filter(isSubprocessRow);
                  if (!subs.length) return null;
                  return (
                    <div className="role-card" style={{ marginBottom: 20 }}>
                      <div className="section-title">Sub-process steps ({subs.length})</div>
                      <div style={{ fontSize: 12, color: "#6b7280", margin: "6px 0 12px" }}>
                        These steps are whole child processes embedded in this one. Their people are staffed and
                        measured <b>inside their own process</b>, so they carry no job or utilization here — the
                        workload table below covers this process&apos;s own tasks only. Expand a step to see the
                        child process&apos;s RACI.
                      </div>
                      <div className="raci-flat-wrap">
                        <table className="raci-flat-table">
                          <thead>
                            <tr>
                              <th className="raci-flat-th-task">Step</th>
                              <th className="raci-flat-th-role" style={{ textAlign: "right" }}>Duration (min)</th>
                              <th className="raci-flat-th-role" style={{ textAlign: "right" }}>Composite cost</th>
                              <th className="raci-flat-th-role">Staffed in</th>
                            </tr>
                          </thead>
                          <tbody>
                            {subs.map((s) => {
                              const open = !!expandedSubprocess[s.task_id];
                              const rows = [(
                                <tr key={s.task_id} className="raci-flat-row raci-flat-row-first">
                                  <td className="raci-flat-task-cell">
                                    <div className="raci-flat-task-inner">
                                      <span className="raci-flat-step">{s.order}</span>
                                      <div className="raci-flat-task-text">
                                        <span className="raci-flat-task-name">{s.task_name}</span>
                                        {subprocessBadge}
                                        <SubprocessToggle id={s.task_id} open={open} />
                                      </div>
                                    </div>
                                  </td>
                                  <td className="raci-flat-role-cell" style={{ textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                                    {s.composite_minutes != null ? Number(s.composite_minutes).toFixed(0) : "—"}
                                  </td>
                                  <td className="raci-flat-role-cell" style={{ textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                                    {s.composite_cost_usd != null ? money(s.composite_cost_usd) : "—"}
                                  </td>
                                  <td className="raci-flat-job-name">{s.child_raci?.process_name || `process #${s.child_process_id}`}</td>
                                </tr>
                              )];
                              if (open) rows.push(<SubprocessRaciDetail key={`${s.task_id}-d`} row={s} colSpan={4} />);
                              return rows;
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  );
                })()}
                <div className="role-card">
                  <div className="section-title">Workload Balancing</div>

                  {/* View toggle — outside the table, so it reads as a control over
                      the whole section rather than a column header. Task-focused is
                      the default and the only editable view. */}
                  <div className="po-raci-viewbar">
                    <div className="po-raci-viewbar-text">
                      {raciView === "task"
                        ? "Who holds each RACI role on each step — edit a role-holder to reassign it."
                        : "Every job involved in this process with its measured utilization."}
                    </div>
                    <div className="po-raci-viewtoggle" role="group" aria-label="RACI table view">
                      <button
                        type="button"
                        className={`opt-tab-btn ${raciView === "task" ? "active" : ""}`}
                        aria-pressed={raciView === "task"}
                        onClick={() => setRaciView("task")}
                      >
                        Task focused
                      </button>
                      <button
                        type="button"
                        className={`opt-tab-btn ${raciView === "job" ? "active" : ""}`}
                        aria-pressed={raciView === "job"}
                        onClick={() => setRaciView("job")}
                      >
                        Job focused
                      </button>
                    </div>
                  </div>

                  {/* ── TASK-FOCUSED: the Overview RACI shape (no utilization
                      columns — those are per job, not per step), plus an edit
                      control on every real role-holder. ── */}
                  {raciView === "task" && (
                    <div style={{ marginBottom: 16 }}>
                      {Object.keys(manualOverrides).length > 0 && (
                        <div className="po-raci-pending">
                          <span>
                            <b>{Object.keys(manualOverrides).length} reassignment(s) set by you</b> are
                            in effect: every metric on this page has been re-scored by the backend for
                            them, and Save will persist them into the new version's staffing.
                          </span>
                        </div>
                      )}
                      {effectiveRaciTaskGroups.length === 0 ? (
                        <div className="no-results">No RACI assignments available.</div>
                      ) : (
                        <div className="raci-flat-wrap">
                          <table className="raci-flat-table">
                            <thead>
                              <tr>
                                <th className="raci-flat-th-task">Task Name</th>
                                <th className="raci-flat-th-job">Job Name</th>
                                {[
                                  { role: "R", label: "Responsible" },
                                  { role: "A", label: "Accountable" },
                                  { role: "C", label: "Consulted" },
                                  { role: "I", label: "Informed" },
                                ].map(({ role, label }) => (
                                  <th key={role} className="raci-flat-th-role">
                                    <div className="raci-flat-role-header">
                                      <span className={`raci-legend-badge raci-${role.toLowerCase()}`}>{role}</span>
                                      <span className="raci-flat-role-label">{label}</span>
                                    </div>
                                  </th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {effectiveRaciTaskGroups.map((tg, tgIdx) => {
                                const jobRows = tg.jobs.length > 0 ? tg.jobs : [null];
                                const isAlt = tgIdx % 2 !== 0;
                                const sub = isSubprocessRow(tg);
                                const open = !!expandedSubprocess[tg.task_id];
                                const eliminated = isEliminated(tg.task_id);
                                const rows = jobRows.map((job, jIdx) => (
                                  <tr
                                    key={job ? `${tg.task_id}-${job.replaced_job_id ?? job.job_id}-${job.role}` : `empty-${tg.task_id}`}
                                    className={`raci-flat-row${isAlt ? " raci-flat-row-alt" : ""}${jIdx === 0 ? " raci-flat-row-first" : ""}`}
                                  >
                                    {jIdx === 0 && (
                                      <td rowSpan={jobRows.length} className="raci-flat-task-cell">
                                        <div className="raci-flat-task-inner">
                                          <span className="raci-flat-step">{tg.order}</span>
                                          <div className="raci-flat-task-text">
                                            <span className="raci-flat-task-name" style={eliminated ? { textDecoration: "line-through", color: "#9ca3af" } : undefined}>
                                              {tg.task_name}
                                            </span>
                                            {sub && subprocessBadge}
                                            {sub && <SubprocessToggle id={tg.task_id} open={open} />}
                                            {eliminated && eliminatedBadge}
                                          </div>
                                        </div>
                                      </td>
                                    )}
                                    {job ? (
                                      <>
                                        <td className="raci-flat-job-name">
                                          <div className="po-raci-jobcell">
                                            <span className={job.overridden ? "po-raci-jobname is-changed" : "po-raci-jobname"}>
                                              {job.name}
                                            </span>
                                            {/* Editable only where a reassignment is meaningful:
                                                a sub-process is staffed in its child process, and
                                                an eliminated step has no TO-BE staffing to change. */}
                                            {!sub && !eliminated && (
                                              <button
                                                type="button"
                                                className="po-raci-editbtn"
                                                disabled={applyingSelection}
                                                title={`Reassign the ${ROLE_LABELS[job.role] || job.role} of “${tg.task_name}”`}
                                                aria-label={`Reassign ${job.name} as ${ROLE_LABELS[job.role] || job.role} on ${tg.task_name}`}
                                                onClick={() => {
                                                  setEditError("");
                                                  setEditTarget({
                                                    taskId: tg.task_id,
                                                    taskName: tg.task_name,
                                                    role: job.role,
                                                    // The job being replaced is the ORIGINAL holder,
                                                    // so re-editing a row already changed this session
                                                    // updates that same override instead of chaining
                                                    // a second replacement onto the new job.
                                                    job: {
                                                      job_id: job.replaced_job_id ?? job.job_id,
                                                      name: job.replaced_job_name ?? job.name,
                                                      hourly_rate: job.overridden
                                                        ? job.replaced_job_rate
                                                        : job.hourly_rate,
                                                    },
                                                  });
                                                }}
                                              >
                                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                                  <path d="M12 20h9" />
                                                  <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" />
                                                </svg>
                                              </button>
                                            )}
                                          </div>
                                          {job.overridden && (
                                            <div className="po-raci-was">
                                              was {job.replaced_job_name}
                                              <button
                                                type="button"
                                                className="po-raci-undo"
                                                disabled={applyingSelection}
                                                onClick={() => revertManualOverride(tg.task_id, job.role, job.replaced_job_id)}
                                              >
                                                undo
                                              </button>
                                            </div>
                                          )}
                                        </td>
                                        {["R", "A", "C", "I"].map((role) => (
                                          <td key={role} className="raci-flat-role-cell">
                                            {job.role === role ? (
                                              <span className={`raci-badge raci-${role.toLowerCase()}`}>{role}</span>
                                            ) : (
                                              <span className="raci-summary-empty">—</span>
                                            )}
                                          </td>
                                        ))}
                                      </>
                                    ) : (
                                      <td colSpan={5} className="raci-flat-no-jobs">
                                        {sub
                                          ? "Sub-process — staffed inside its own process. Use “View RACI” to see who does this work."
                                          : "No jobs assigned to this task"}
                                      </td>
                                    )}
                                  </tr>
                                ));
                                if (sub && open) rows.push(<SubprocessRaciDetail key={`${tg.task_id}-detail`} row={tg} colSpan={6} />);
                                return rows;
                              })}
                            </tbody>
                          </table>
                        </div>
                      )}
                      {selectionError && (
                        <div role="alert" style={{ marginTop: 8, fontSize: 12, color: "#b91c1c" }}>{selectionError}</div>
                      )}
                    </div>
                  )}

                  {raciView === "job" && effectiveWorkloadJobs.length > 0 && (
                    <div style={{ marginBottom: 16 }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: "#6b7280", marginBottom: 6 }}>
                        MEASURED UTILIZATION — every job involved in this process. Utilization is <u>company-wide</u> (all the job's
                        tasks across every process — same figure and formula as the CMS Job Workload page; threshold {wb?.threshold?.overutilized_above_pct ?? 90}%).
                        You can reassign a job manually in the CMS if you judge its load differently.
                      </div>
                      <div className="raci-flat-wrap">
                        <table className="raci-flat-table">
                          <thead>
                            <tr>
                              <th className="raci-flat-th-task">Job</th>
                              <th className="raci-flat-th-role" style={{ minWidth: 150 }}>Utilization (company-wide)</th>
                              <th className="raci-flat-th-role" style={{ minWidth: 120 }} title="The slice of the job's weekly workload that comes from THIS process's tasks (same CMS workload data, filtered to this process), as % of the job's effective weekly capacity.">Utilization (this process)</th>
                              <th className="raci-flat-th-job">Task in this process</th>
                              {[
                                { role: "R", label: "Responsible" },
                                { role: "A", label: "Accountable" },
                                { role: "C", label: "Consulted" },
                                { role: "I", label: "Informed" },
                              ].map(({ role, label }) => (
                                <th key={role} className="raci-flat-th-role">
                                  <div className="raci-flat-role-header">
                                    <span className={`raci-legend-badge raci-${role.toLowerCase()}`}>{role}</span>
                                    <span className="raci-flat-role-label">{label}</span>
                                  </div>
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {effectiveWorkloadJobs.map((j, jgIdx) => {
                              const pct = j.utilization_percent;
                              const color = j.utilization_unmeasured
                                ? "#9ca3af"
                                : j.overutilized
                                  ? "#b91c1c"
                                  : pct != null && pct > 70
                                    ? "#b45309"
                                    : "#15803d";
                              // One row per TASK, not per role: a job holding both R and A
                              // on the same task is one assignment to read, not two — grouping
                              // here merges them into a single row with every held role lit,
                              // rather than repeating the task name once per role.
                              const byTask = new Map();
                              (j.assignments || []).forEach((entry) => {
                                const key = String(entry.task_id);
                                const group = byTask.get(key) || {
                                  task_id: entry.task_id,
                                  task_name: entry.task_name,
                                  roles: new Set(),
                                  reassigned: false,
                                  replaced_job_name: null,
                                };
                                group.roles.add(String(entry.role || "").toUpperCase());
                                if (entry.reassigned) {
                                  group.reassigned = true;
                                  group.replaced_job_name = group.replaced_job_name || entry.replaced_job_name;
                                }
                                byTask.set(key, group);
                              });
                              const assignments = byTask.size ? [...byTask.values()] : [null];
                              const isAlt = jgIdx % 2 !== 0;
                              return assignments.map((a, aIdx) => (
                                <tr
                                  key={`${j.job_id}-${a ? a.task_id : "none"}`}
                                  className={`raci-flat-row${isAlt ? " raci-flat-row-alt" : ""}${aIdx === 0 ? " raci-flat-row-first" : ""}`}
                                >
                                  {aIdx === 0 && (
                                    <>
                                      <td rowSpan={assignments.length} className="raci-flat-task-cell">
                                        <div className="raci-flat-task-inner">
                                          <div className="raci-flat-task-text">
                                            <span className="raci-flat-task-name">{j.job_name}</span>
                                            <span style={{ display: "block", fontSize: 11, color: "#6b7280", marginTop: 2 }} title="Smoothed weekly workload hours (every task this job holds, across ALL processes, frequency-adjusted) / effective weekly capacity (max hours per day × days per week, minus the capacity buffer)">
                                              {j.weekly_workload_hrs != null ? Number(j.weekly_workload_hrs).toFixed(1) : "—"} / {j.weekly_capacity_hrs != null ? Number(j.weekly_capacity_hrs).toFixed(1) : "—"} hrs/wk
                                            </span>
                                          </div>
                                        </div>
                                      </td>
                                      <td rowSpan={assignments.length} style={{ padding: "8px 12px", verticalAlign: "top", minWidth: 150 }}>
                                        <span style={{ fontWeight: 700, color, fontVariantNumeric: "tabular-nums" }}>{pct != null ? `${Number(pct).toFixed(1)}%` : "—"}</span>
                                        <span style={{ marginLeft: 6, fontSize: 11, color }}>
                                          {j.utilization_unmeasured
                                            ? "not yet measured — reassigned by you"
                                            : j.overutilized
                                              ? "over-utilized"
                                              : (j.band || "within range")}
                                        </span>
                                        {pct != null && (
                                          <div title={`${Number(pct).toFixed(1)}% of effective weekly capacity (threshold ${wb?.threshold?.overutilized_above_pct ?? 90}%)`} style={{ position: "relative", height: 6, background: "#eef2f7", borderRadius: 4, marginTop: 4, overflow: "hidden" }}>
                                            <div style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: `${Math.min(100, Number(pct))}%`, background: j.overutilized ? "#b91c1c" : "#0d9488", borderRadius: 4 }} />
                                            <div style={{ position: "absolute", left: `${Math.min(100, Number(wb?.threshold?.overutilized_above_pct ?? 90))}%`, top: -1, bottom: -1, width: 2, background: "#9a3412", opacity: 0.7 }} />
                                          </div>
                                        )}
                                      </td>
                                      <td rowSpan={assignments.length} style={{ padding: "8px 12px", verticalAlign: "top", minWidth: 120 }}>
                                        {j.process_utilization_percent != null ? (
                                          <>
                                            <span style={{ fontWeight: 700, color: "#334155", fontVariantNumeric: "tabular-nums" }}>{Number(j.process_utilization_percent).toFixed(1)}%</span>
                                            <span style={{ display: "block", fontSize: 11, color: "#6b7280", marginTop: 2 }} title="Weekly hours this job spends on THIS process's tasks (frequency-adjusted, from the same CMS workload data)">
                                              {j.process_weekly_hrs != null ? `${Number(j.process_weekly_hrs).toFixed(1)} hrs/wk on this process` : ""}
                                            </span>
                                          </>
                                        ) : (
                                          <span style={{ color: "#9ca3af" }}>—</span>
                                        )}
                                      </td>
                                    </>
                                  )}
                                  {a ? (
                                    <>
                                      <td className="raci-flat-job-name">
                                        <span style={isEliminated(a.task_id) ? { textDecoration: "line-through", color: "#9ca3af" } : undefined}>{a.task_name}</span>
                                        {isEliminated(a.task_id) && eliminatedBadge}
                                        {a.reassigned && reassignedBadge(a.replaced_job_name)}
                                      </td>
                                      {["R", "A", "C", "I"].map((role) => (
                                        <td key={role} className="raci-flat-role-cell">
                                          {a.roles.has(role) ? (
                                            <span className={`raci-badge raci-${role.toLowerCase()}`}>{role}</span>
                                          ) : (
                                            <span className="raci-summary-empty">—</span>
                                          )}
                                        </td>
                                      ))}
                                    </>
                                  ) : (
                                    <td colSpan={5} className="raci-flat-no-jobs">No task assignments recorded</td>
                                  )}
                                </tr>
                              ));
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                  {raciView === "job" && effectiveWorkloadJobs.length === 0 && (
                    <div className="no-results" style={{ marginBottom: 16 }}>
                      No per-job utilization was reported for this process. Switch to{" "}
                      <b>Task focused</b> to see and edit who holds each role.
                    </div>
                  )}
                  {!wb?.enabled ? (
                    <div className="opt-resolution-card">
                      <div style={{ position: "relative", zIndex: 1, textAlign: "center", padding: "24px 16px" }}>
                        <div style={{ fontSize: 15, fontWeight: 700, color: "#065f46", marginBottom: 6 }}>
                          No job exceeds the utilization threshold
                        </div>
                        <div style={{ fontSize: 13, color: "#374151" }}>
                          {wb?.message || "All measured utilizations are within range — no automatic redistribution was applied."}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="opt-resolution-card">
                      <div style={{ position: "relative", zIndex: 1 }}>
                        <div className="opt-resolution-summary">
                          <div className="opt-resolution-metric before">
                            <div className="opt-resolution-metric-label">Overloaded Jobs</div>
                            <div className="opt-resolution-metric-value">{wb.overloaded_jobs.length}</div>
                          </div>
                          <div className="opt-resolution-metric after">
                            <div className="opt-resolution-metric-label">Tasks Distributed</div>
                            <div className="opt-resolution-metric-value">
                              {wb.overloaded_jobs.reduce((acc, j) => acc + j.tasks.filter((t) => t.status === "moved").length, 0)}
                            </div>
                          </div>
                          <div className="opt-resolution-metric savings">
                            <div className="opt-resolution-metric-label">Jobs Balanced</div>
                            <div className="opt-resolution-metric-value">
                              {wb.receiving_jobs.length}
                              <span className="opt-percentage-badge">balanced</span>
                            </div>
                          </div>
                        </div>

                        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, margin: "14px 0 6px", alignItems: "center" }}>
                          <span style={{ fontSize: 12, fontWeight: 600, color: "#6b7280", marginRight: 4 }}>Tasks absorbed by:</span>
                          {wb.receiving_jobs.map((rj) => (
                            <span
                              key={rj.job_name}
                              style={{ display: "inline-flex", alignItems: "center", gap: 5, background: "linear-gradient(135deg,#d1fae5,#a7f3d0)", border: "1.5px solid #34d399", borderRadius: 20, padding: "3px 10px", fontSize: 12, fontWeight: 600, color: "#065f46" }}
                            >
                              {rj.job_name}
                              <span style={{ background: "#10b981", color: "#fff", borderRadius: 10, padding: "1px 6px", fontSize: 11 }}>
                                +{rj.tasks_received}
                              </span>
                            </span>
                          ))}
                        </div>

                        <details className="opt-details-section" open>
                          <summary className="opt-details-summary resolution">
                            View Overloaded Job Details ({wb.overloaded_jobs.length})
                          </summary>
                          <div className="opt-details-content">
                            {wb.overloaded_jobs.map((job) => (
                              <div key={job.job_name} className="opt-resolution-task-card">
                                <div className="resolution-task-header" style={{ justifyContent: "space-between" }}>
                                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                    <span className="task-label">Job:</span>
                                    <span className="task-name">{job.job_name}</span>
                                    <span style={{ background: "#fef2f2", border: "1.5px solid #f87171", color: "#b91c1c", borderRadius: 12, padding: "2px 8px", fontSize: 11, fontWeight: 700 }}>
                                      Overutilized {job.utilization_before}%
                                    </span>
                                  </div>
                                </div>

                                <div style={{ display: "grid", gridTemplateColumns: "1fr 32px 1fr", gap: "0 8px", marginBottom: 8 }}>
                                  <div style={{ fontSize: 11, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.06em", paddingBottom: 4, borderBottom: "1.5px dashed #e5e7eb" }}>
                                    Tasks Previously Assigned
                                  </div>
                                  <div />
                                  <div style={{ fontSize: 11, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.06em", paddingBottom: 4, borderBottom: "1.5px dashed #e5e7eb" }}>
                                    Redistributed To
                                  </div>
                                </div>

                                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                                  {job.tasks.map((t) => (
                                    <div key={`${t.task_name}-${t.raci_role}`} style={{ display: "grid", gridTemplateColumns: "1fr 32px 1fr", gap: "0 8px", alignItems: "center" }}>
                                      <span style={{ display: "flex", alignItems: "center", gap: 6, background: t.status === "moved" ? "#fef3c7" : "#f0fdf4", border: `1.5px solid ${t.status === "moved" ? "#fbbf24" : "#86efac"}`, borderRadius: 8, padding: "4px 10px", fontSize: 12, fontWeight: 600, color: t.status === "moved" ? "#92400e" : "#166534" }}>
                                        {t.task_name}
                                        {t.raci_role && (
                                          <span className={`raci-badge raci-${t.raci_role.toLowerCase()}`} style={{ fontSize: 10, padding: "1px 6px" }}>
                                            {t.raci_role}
                                          </span>
                                        )}
                                      </span>
                                      <span style={{ textAlign: "center", fontSize: 16, fontWeight: 700, color: t.status === "moved" ? "#7c3aed" : "#d1d5db" }}>→</span>
                                      {t.status === "moved" ? (
                                        <span style={{ background: "#ede9fe", border: "1.5px solid #7c3aed", borderRadius: 8, padding: "4px 10px", fontSize: 12, fontWeight: 600, color: "#4c1d95" }}>
                                          👤 {t.redistributed_to}
                                        </span>
                                      ) : (
                                        <span style={{ fontSize: 12, fontWeight: 600, color: "#059669", paddingLeft: 4 }}>
                                          ✓ Kept with {job.job_name}
                                        </span>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                        </details>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* 6) Value Added Analysis Tab */}
            {activeTab === "value" && (
              <div className="po-tab-pane">
                {valueAnalysisLoading && <VaProgress />}
                {valueAnalysisError && !valueAnalysisLoading && (
                  <div className="role-card">
                    <div className="no-results" style={{ color: "#b91c1c" }}>{valueAnalysisError}</div>
                  </div>
                )}
                {valueAnalysis?._fallback && !valueAnalysisLoading && (
                  <div style={{ margin: "0 0 14px", padding: "10px 14px", background: "#fffbeb", border: "1.5px solid #f59e0b", borderRadius: 10, color: "#92400e", fontSize: 13, display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                    <span>
                      <strong>⚠ AI value analysis unavailable.</strong> Showing your <b>previously saved classifications</b> instead,
                      because {valueAnalysis._fallback.reason}. The saved values below are still authoritative and editable.
                    </span>
                    <button type="button" className="primary-btn sm" onClick={retryValueAnalysis}>
                      Retry AI analysis
                    </button>
                  </div>
                )}
                {valueAnalysis && !valueAnalysisLoading && (
                  <>
                    {(() => {
                      // Sub-process steps are NEVER classified at this level (no own
                      // description) — say so explicitly and route the user to the
                      // child process, instead of silently omitting the rows.
                      const subs =
                        (Array.isArray(valueAnalysis.sub_process_steps) && valueAnalysis.sub_process_steps.length
                          ? valueAnalysis.sub_process_steps
                          : raciTaskGroups
                              .filter((t) => t.is_subprocess)
                              .map((t) => ({ child_process_id: t.child_process_id, name: String(t.task_name || "").replace(/^\[Sub-Process\]\s*/, ""), order: t.order }))) || [];
                      if (!subs.length) return null;
                      return (
                        <div style={{ margin: "0 0 14px", padding: "10px 14px", background: "#faf5ff", border: "1px solid #d8b4fe", borderRadius: 10, color: "#5b21b6", fontSize: 12.5 }}>
                          <strong>◈ {subs.length} sub-process step{subs.length === 1 ? "" : "s"} not classified here.</strong>{" "}
                          A sub-process has no description of its own to decompose — its VA/BVA/NVA content comes from the
                          classifications INSIDE its child process. Run the Value-Added analysis there:
                          <span style={{ display: "inline-flex", gap: 6, flexWrap: "wrap", marginLeft: 8, verticalAlign: "middle" }}>
                            {subs.map((s) => (
                              <button
                                key={s.child_process_id}
                                type="button"
                                className="secondary-btn sm"
                                onClick={() => navigate(`/process-optimization/results/${encodeURIComponent(String(s.child_process_id))}`)}
                                aria-label={`Open the optimization page of child process ${s.name} to classify its steps`}
                              >
                                Step {s.order}: {s.name} →
                              </button>
                            ))}
                          </span>
                        </div>
                      );
                    })()}
                    {valueAnalysis.validation && (valueAnalysis.validation.rule_category_mismatches > 0 || (valueAnalysis.validation.steps_with_rule_cited ?? 0) < (valueAnalysis.validation.steps_total ?? 0)) && (
                      <div style={{ margin: "0 0 14px", padding: "10px 14px", background: "#fffbeb", border: "1px solid #f59e0b", borderRadius: 10, color: "#92400e", fontSize: 12.5 }}>
                        <strong>Rule audit:</strong> {valueAnalysis.validation.steps_with_rule_cited}/{valueAnalysis.validation.steps_total} steps cite the Ch.6 test that decided them
                        {valueAnalysis.validation.rule_category_mismatches > 0 && (
                          <> · <b>{valueAnalysis.validation.rule_category_mismatches} step(s) cite a rule that contradicts their category</b> (marked ⚠ below) — review those manually before saving</>
                        )}.
                      </div>
                    )}
                    <div className="role-card">
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
                        <div className="section-title">Process Summary</div>
                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                          {vaSaveMsg && <span style={{ fontSize: 12, color: "#6b7280" }}>{vaSaveMsg}</span>}
                          <button
                            type="button"
                            className="secondary-btn sm"
                            onClick={handleSaveVaClassifications}
                            disabled={savingVa}
                            title="Save AI classifications — fills unclassified steps; only steps you tick are overwritten. Your unticked edits are protected."
                          >
                            {savingVa ? "Saving…" : "Save classifications"}
                          </button>
                        </div>
                      </div>
                      <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginTop: 16, marginBottom: 20 }}>
                        {[
                          { label: "Total Tasks",    value: vaaSummary.total_tasks ?? "-",            accent: "#6366f1" },
                          { label: "Steps Analyzed", value: vaaSummary.total_steps_analyzed ?? "-",   accent: "#0891b2" },
                          { label: "VA %",           value: `${vaaSummary.overall_va_percentage  ?? 0}%`, accent: "#15803d" },
                          { label: "BVA %",          value: `${vaaSummary.overall_bva_percentage ?? 0}%`, accent: "#1d4ed8" },
                          { label: "NVA %",          value: `${vaaSummary.overall_nva_percentage ?? 0}%`, accent: "#b91c1c" },
                        ].map((m) => (
                          <div key={m.label} style={{ flex: "1 1 120px", background: "#fff", border: `2px solid ${m.accent}22`, borderLeft: `4px solid ${m.accent}`, borderRadius: 12, padding: "16px 20px", textAlign: "center", boxShadow: "0 2px 8px rgba(0,0,0,0.05)" }}>
                            <div style={{ fontSize: 11, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.6px", marginBottom: 8 }}>{m.label}</div>
                            <div style={{ fontSize: 26, fontWeight: 800, color: m.accent }}>{m.value}</div>
                          </div>
                        ))}
                      </div>

                      <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 8 }}>
                        {["VA", "BVA", "NVA"].map((cat) => {
                          const c = getCategoryColor(cat);
                          const labels = { VA: "VA — Value Added", BVA: "BVA — Business Value Added", NVA: "NVA — Non-Value Added" };
                          return (
                            <span key={cat} style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "5px 12px", background: c.bg, border: `1.5px solid ${c.border}`, borderRadius: 20, fontSize: 12, fontWeight: 600, color: c.color }}>
                              <span style={{ width: 8, height: 8, borderRadius: "50%", background: c.color, display: "inline-block" }} />
                              {labels[cat]}
                            </span>
                          );
                        })}
                      </div>

                      {Array.isArray(vaaSummary.tasks_with_high_va) && vaaSummary.tasks_with_high_va.length > 0 && (
                        <div style={{ marginTop: 10, padding: "10px 14px", background: "#f0fdf4", border: "1.5px solid #86efac", borderRadius: 10, fontSize: 13 }}>
                          <span style={{ fontWeight: 700, color: "#15803d" }}>High VA Tasks: </span>
                          {vaaSummary.tasks_with_high_va.join(", ")}
                        </div>
                      )}
                      {Array.isArray(vaaSummary.tasks_with_high_nva) && vaaSummary.tasks_with_high_nva.length > 0 && (
                        <div style={{ marginTop: 8, padding: "10px 14px", background: "#fef2f2", border: "1.5px solid #fca5a5", borderRadius: 10, fontSize: 13 }}>
                          <span style={{ fontWeight: 700, color: "#b91c1c" }}>High NVA Tasks: </span>
                          {vaaSummary.tasks_with_high_nva.join(", ")}
                        </div>
                      )}
                    </div>

                    <div className="role-card" style={{ marginTop: 20 }}>
                      <div className="section-title">Task Classifications</div>
                      <div style={{ display: "flex", flexDirection: "column", gap: 16, marginTop: 16 }}>
                        {vaaTasks.map((task) => {
                          const isOpen = !!expandedTasks[task.task_id];
                          const dom = getCategoryColor(task.dominant_category);
                          // A sub-process step: not classifiable here — say so plainly
                          // instead of offering a control that cannot apply.
                          if (task._is_subprocess) {
                            return (
                              <div key={task.task_id} style={{ border: "1.5px solid #c4b5fd", borderRadius: 14, overflow: "hidden", background: "#faf5ff" }}>
                                <div style={{ padding: "14px 20px", display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                                  <span style={{ fontSize: 15, fontWeight: 700, color: "#1e293b", flex: 1, minWidth: 200 }}>
                                    {task.task_name}
                                    {subprocessBadge}
                                  </span>
                                  <span style={{ fontSize: 11, fontWeight: 800, color: "#5b21b6", background: "#ede9fe", border: "1px solid #c4b5fd", borderRadius: 999, padding: "2px 10px" }}>
                                    not classified here
                                  </span>
                                </div>
                                <div style={{ padding: "0 20px 14px", fontSize: 12.5, color: "#6b21a8", lineHeight: 1.5 }}>
                                  Value-added analysis classifies a step by breaking its <b>description</b> into steps and testing each
                                  against the VA / BVA / NVA questions. A sub-process is a <b>composite step</b> — it has no description of
                                  its own; it <i>is</i> another process. Its value content is therefore classified inside{" "}
                                  <b>{String(task.task_name || "").replace("[Sub-Process] ", "")}</b>, and rolls up from there into this
                                  process&apos;s value-adding time and CTE. Open that process&apos;s Value-Added tab to classify its steps.
                                </div>
                              </div>
                            );
                          }
                          return (
                            <div key={task.task_id} style={{ border: "1.5px solid #e5e7eb", borderLeft: vaOverwrite[task.task_id] ? "4px solid #0e6b68" : "1.5px solid #e5e7eb", borderRadius: 14, overflow: "hidden", boxShadow: "0 2px 8px rgba(0,0,0,0.04)" }}>
                              <div style={{ display: "flex", alignItems: "stretch", background: "#f8fafc" }}>
                                <label
                                  title="Tick to let AI overwrite this step's saved classification when you press Save"
                                  onClick={(e) => e.stopPropagation()}
                                  style={{ display: "flex", alignItems: "center", gap: 6, padding: "0 14px", borderRight: "1px solid #e5e7eb", cursor: "pointer", color: vaOverwrite[task.task_id] ? "#0e6b68" : "#9ca3af", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em" }}
                                >
                                  <input
                                    type="checkbox"
                                    checked={!!vaOverwrite[task.task_id]}
                                    onChange={() => toggleVaOverwrite(task.task_id)}
                                    style={{ width: 15, height: 15, cursor: "pointer", accentColor: "#0e6b68" }}
                                  />
                                  Overwrite
                                </label>
                              <button
                                type="button"
                                onClick={() => setExpandedTasks((prev) => ({ ...prev, [task.task_id]: !prev[task.task_id] }))}
                                style={{ flex: 1, minWidth: 0, background: "#f8fafc", border: "none", padding: "14px 20px", display: "flex", alignItems: "center", gap: 12, cursor: "pointer", textAlign: "left" }}
                              >
                                <span style={{ fontSize: 15, fontWeight: 700, color: "#1e293b", flex: 1 }}>
                                  <span style={isEliminated(task.task_id) ? { textDecoration: "line-through", color: "#9ca3af" } : undefined}>{task.task_name}</span>
                                  {isEliminated(task.task_id) && eliminatedBadge}
                                  {/* Deterministic marker (keyword-based, no AI): the description mixes
                                      refinement and correction wording — the BVA/NVA boundary case that
                                      deserves a human look no matter what the AI chose. */}
                                  {task.borderline && (
                                    <span
                                      title={task.borderline_reason || "The description mixes refinement and correction wording — review the category."}
                                      style={{ marginLeft: 8, fontSize: 10, fontWeight: 800, color: "#92400e", background: "#fef3c7", border: "1px solid #fcd34d", borderRadius: 999, padding: "2px 9px", verticalAlign: "middle", whiteSpace: "nowrap" }}
                                    >
                                      ⚠ borderline — review
                                    </span>
                                  )}
                                </span>
                                <select
                                  value={vaEdits[task.task_id] || String(task.dominant_category || "").toUpperCase()}
                                  onClick={(e) => e.stopPropagation()}
                                  onChange={(e) => setVaEdit(task.task_id, e.target.value)}
                                  title={vaEdits[task.task_id] && vaEdits[task.task_id] !== String(task.dominant_category || "").toUpperCase()
                                    ? `You changed this from ${task.user_override ? "your saved classification" : "the AI suggestion"} (${task.dominant_category}) — saves as your (USER) classification`
                                    : task.user_override
                                      ? `Your saved (USER) classification — kept on re-analysis. The AI suggested ${task.ai_suggested_category || "a different category"} this run; your choice wins and is never AI-overwritten.`
                                      : "AI-suggested classification — change it if you disagree; your choice saves as USER and is never AI-overwritten"}
                                  style={{
                                    background: dom.bg, color: dom.color, border: `1.5px solid ${vaEdits[task.task_id] && vaEdits[task.task_id] !== String(task.dominant_category || "").toUpperCase() ? "#1d4ed8" : dom.border}`,
                                    borderRadius: 20, padding: "3px 10px", fontSize: 12, fontWeight: 700, cursor: "pointer",
                                  }}
                                >
                                  {["VA", "BVA", "NVA"].map((c) => (<option key={c} value={c}>{c}</option>))}
                                </select>
                                {vaEdits[task.task_id] && vaEdits[task.task_id] !== String(task.dominant_category || "").toUpperCase() && (
                                  <span style={{ fontSize: 10, fontWeight: 800, padding: "1px 6px", borderRadius: 999, border: "1px solid #93c5fd", background: "#dbeafe", color: "#1d4ed8" }}>You</span>
                                )}
                                {task.user_override && !vaEdits[task.task_id] && (
                                  <span
                                    title={`Your saved (USER) classification, kept on re-analysis${task.ai_suggested_category ? ` — the AI suggested ${task.ai_suggested_category} this run` : ""}. USER overrides win and are never AI-overwritten.`}
                                    style={{ fontSize: 10, fontWeight: 800, padding: "1px 7px", borderRadius: 999, border: "1px solid #99f6e4", background: "#f0fdfa", color: "#0e6b68", whiteSpace: "nowrap" }}
                                  >
                                    ★ your override{task.ai_suggested_category ? ` · AI: ${task.ai_suggested_category}` : ""}
                                  </span>
                                )}
                                {task.va_percentage != null ? (
                                  <>
                                    <span style={{ fontSize: 12, color: "#15803d", fontWeight: 600, minWidth: 48, textAlign: "right" }}>VA {task.va_percentage}%</span>
                                    <span style={{ fontSize: 12, color: "#1d4ed8", fontWeight: 600, minWidth: 52, textAlign: "right" }}>BVA {task.bva_percentage}%</span>
                                    <span style={{ fontSize: 12, color: "#b91c1c", fontWeight: 600, minWidth: 52, textAlign: "right" }}>NVA {task.nva_percentage}%</span>
                                  </>
                                ) : (
                                  <span title={task._saved_source ? `Saved classification (source: ${task._saved_source})` : "Saved classification"} style={{ fontSize: 11, color: "#6b7280", fontWeight: 600 }}>
                                    saved{task._saved_source ? ` · ${task._saved_source}` : ""}
                                  </span>
                                )}
                                <span style={{ fontSize: 16, color: "#6b7280", marginLeft: 8, transition: "transform 0.2s", transform: isOpen ? "rotate(90deg)" : "rotate(0deg)" }}>▶</span>
                              </button>
                              </div>

                              {isOpen && (
                                <div style={{ padding: "0 20px 20px" }}>
                                  <div style={{ display: "flex", flexWrap: "wrap", gap: 12, padding: "12px 0 16px", borderBottom: "1px dashed #e5e7eb" }}>
                                    <div style={{ flex: 1, minWidth: 220 }}>
                                      <div style={{ fontSize: 11, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 6 }}>Assigned Jobs</div>
                                      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                                        {(task.jobs || []).map((j) => {
                                          // These chips come from the value-analysis response,
                                          // which reports AS-IS staffing. A pending
                                          // reassignment of this job is stated on the chip so
                                          // the tab does not name someone the TO-BE has
                                          // already replaced.
                                          const ov = ["R", "A", "C", "I"]
                                            .map((role) => raciOverrideFor(task.task_id, role, j.job_id))
                                            .find(Boolean);
                                          return (
                                            <span key={j.job_id} style={{ background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 8, padding: "4px 10px", fontSize: 12, fontWeight: 600, color: "#1e40af" }}>
                                              {j.name} <span style={{ color: "#6b7280", fontWeight: 400 }}>({money(j.hourly_rate)}/hr)</span>
                                              {ov && (
                                                <span title="Reassigned on the Analyze tab — the TO-BE numbers already reflect this." style={{ marginLeft: 6, color: "#0f766e", fontWeight: 800 }}>
                                                  → {ov.new_job_name}
                                                </span>
                                              )}
                                            </span>
                                          );
                                        })}
                                      </div>
                                    </div>
                                    <div style={{ flex: 1, minWidth: 220 }}>
                                      <div style={{ fontSize: 11, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 6 }}>Action Recommendation</div>
                                      <div style={{ fontSize: 13, color: "#1e293b", fontWeight: 600, padding: "6px 12px", background: "#fefce8", border: "1px solid #fde047", borderRadius: 8 }}>
                                        {task.action_recommendation || "—"}
                                      </div>
                                    </div>
                                  </div>
                                  {Array.isArray(task.elimination_candidates) && task.elimination_candidates.length > 0 && (
                                    <div style={{ margin: "12px 0", padding: "10px 14px", background: "#fef2f2", border: "1.5px solid #fca5a5", borderRadius: 10 }}>
                                      <span style={{ fontWeight: 700, color: "#b91c1c", fontSize: 12 }}>Elimination Candidates: </span>
                                      <span style={{ fontSize: 12, color: "#7f1d1d" }}>{task.elimination_candidates.join("; ")}</span>
                                    </div>
                                  )}
                                  <div style={{ overflowX: "auto", marginTop: 12 }}>
                                  <table style={{ width: "100%", minWidth: 520, borderCollapse: "collapse", fontSize: 13 }}>
                                    <thead>
                                      <tr style={{ background: "#f1f5f9" }}>
                                        <th style={{ padding: "10px 12px", textAlign: "left", fontWeight: 700, color: "#374151", borderBottom: "2px solid #e5e7eb", width: 36 }}>#</th>
                                        <th style={{ padding: "10px 12px", textAlign: "left", fontWeight: 700, color: "#374151", borderBottom: "2px solid #e5e7eb" }}>Step</th>
                                        <th style={{ padding: "10px 12px", textAlign: "center", fontWeight: 700, color: "#374151", borderBottom: "2px solid #e5e7eb", width: 72 }}>Category</th>
                                        <th style={{ padding: "10px 12px", textAlign: "left", fontWeight: 700, color: "#374151", borderBottom: "2px solid #e5e7eb" }}>Reasoning</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {(task.steps || []).map((step) => {
                                        const c = getCategoryColor(step.category);
                                        return (
                                          <tr key={step.step_number} style={{ borderBottom: "1px solid #f1f5f9" }}>
                                            <td style={{ padding: "10px 12px", color: "#9ca3af", fontWeight: 600, verticalAlign: "top" }}>{step.step_number}</td>
                                            <td style={{ padding: "10px 12px", color: "#1e293b", fontWeight: 500, verticalAlign: "top" }}>{step.step_name}</td>
                                            <td style={{ padding: "10px 12px", textAlign: "center", verticalAlign: "top" }}>
                                              <span style={{ display: "inline-block", background: c.bg, color: c.color, border: `1.5px solid ${c.border}`, borderRadius: 20, padding: "3px 10px", fontSize: 11, fontWeight: 700 }}>
                                                {step.category}
                                              </span>
                                              {/* The FBPM Ch.6 test that decided the category — the audit trail
                                                  that makes the classification verifiable, not a black box. */}
                                              {step.rule && (
                                                <span
                                                  title={step.rule_consistent === false
                                                    ? "The cited rule's family contradicts the category — review this step manually."
                                                    : "The FBPM Ch.6 test the classifier cited for this category."}
                                                  style={{ display: "inline-block", marginTop: 4, fontSize: 10, fontWeight: 700, borderRadius: 6, padding: "1px 6px", whiteSpace: "nowrap",
                                                    ...(step.rule_consistent === false
                                                      ? { background: "#fef2f2", color: "#b91c1c", border: "1px solid #fecaca" }
                                                      : { background: "#f8fafc", color: "#475569", border: "1px solid #e2e8f0" }) }}
                                                >
                                                  {step.rule_consistent === false ? "⚠ " : ""}{step.rule}
                                                </span>
                                              )}
                                            </td>
                                            <td style={{ padding: "10px 12px", color: "#4b5563", fontStyle: "italic", verticalAlign: "top" }}>{step.reasoning}</td>
                                          </tr>
                                        );
                                      })}
                                    </tbody>
                                  </table>
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
            {activeTab === "save" && (
              <div className="po-tab-pane">
                <div className="role-card">
                  <div className="section-title">Final review — the complete TO-BE process</div>
                  <p style={{ fontSize: 13, color: "#374151", margin: "6px 0 14px" }}>
                    Everything below is what saving will record: the full task list with assignments,
                    the RACI matrix, all four Devil's-Quadrangle values, and what this redesign achieved.
                    Saving creates a NEW process version (the current version is never modified) and the
                    AS-IS / TO-BE scenario pair under the process's Redesign Scenarios tab.
                  </p>

                  {/* Engine composition first: everything below it — the review table,
                      the quadrangle tiles, the path view — describes the version this
                      selection produces, so the choice has to come before them. */}
                  <EngineSelector selection={engineSelection} />
                  <ProvenanceNotices summary={engineSelection.summary} />

                  {/* Sits above the tiles and the review table because it CHANGES
                      them: each tick re-asks the endpoint with the new adopt list,
                      and everything below re-describes the payload that comes back.
                      A control that rewrites the page beneath it cannot sit at the
                      bottom of that page. */}
                  <SaveHeuristicsChecklist selection={engineSelection} fmtMin={fmtMin} />

                  {redesignReport?.summary && (
                    <div style={{ background: "#faf6ee", border: "1px solid #e6dfd2", borderRadius: 10, padding: "10px 14px", fontSize: 13, color: "#374151", marginBottom: 14 }}>
                      <b>Achieved:</b> {redesignReport.summary}
                    </div>
                  )}

                  {(() => {
                    const d = redesignReport?.dimensions || {};
                    const tiles = [
                      { key: "time", label: "Time", unit: "min", c: "#0e6b68" },
                      { key: "cost", label: "Cost", unit: "USD", c: "#1d4ed8" },
                      { key: "quality", label: "Quality", unit: "/100", c: "#b45309" },
                      { key: "flexibility", label: "Flexibility", unit: "/100", c: "#7c3aed" },
                    ];
                    return (
                      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 16 }}>
                        {tiles.map(({ key, label, unit, c }) => {
                          const dim = d[key] || {};
                          const asV = qfOverrides[`${key}_as_is`] ?? dim.as_is;
                          const tbV = qfOverrides[`${key}_to_be`] ?? dim.to_be;
                          return (
                            <div key={key} style={{ flex: "1 1 140px", border: `1px solid ${c}22`, borderLeft: `4px solid ${c}`, borderRadius: 10, padding: "10px 14px", background: "#fff" }}>
                              <div style={{ fontSize: 11, fontWeight: 700, color: "#6b7280", textTransform: "uppercase" }}>{label} <span style={{ fontWeight: 400 }}>({unit})</span></div>
                              <div style={{ fontSize: 16, fontWeight: 800, color: c, marginTop: 4, fontVariantNumeric: "tabular-nums" }}>
                                {asV ?? "—"} → {tbV ?? "—"}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}

                  {/* What each SELECTED engine says its full redesign is worth. Kept
                      outside the tiles above, which show only what the save writes —
                      the two are different quantities and must not be read as one. */}
                  <EnginePotentialBand natives={engineNativeKpis} money={money} fmtMin={fmtMin} />

                  {/* Why those tiles do not move when a heuristic is adopted. Without
                      it, adopting three heuristics and watching the numbers sit still
                      reads as a broken button rather than as the honest answer. */}
                  <KpiExclusionNote summary={engineSelection.summary} adoptedHeuristics={effectiveAdoptedHeuristics} />

                  {/* Heuristics the engines applied that no field can carry. Without
                      this list a version saved from an RL run that touched RACI would
                      silently drop those changes — workflow[].job_id holds one job per
                      item, not a RACI set. */}
                  <AdoptList
                    summary={engineSelection.summary}
                    // Reads the merged set so a row cannot show "not adopted" while
                    // a checklist tick has already recorded it; writes still go to
                    // the manual state, which is the only one a click owns.
                    adoptedHeuristics={effectiveAdoptedHeuristics}
                    onToggleAdopt={toggleAdoptHeuristic}
                    // The checklist above already carries every applied-but-not-
                    // persistable row with the same Record toggle on it.
                    suppressAppliedSection={engineSelection.heuristicRows.length > 0}
                  />

                  {Object.keys(effectiveAdoptedHeuristics).length > 0 && (
                    <div style={{ marginBottom: 14 }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: "#6b7280", marginBottom: 4 }}>ADOPTED HEURISTICS (recorded with the scenario)</div>
                      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                        {Object.values(effectiveAdoptedHeuristics).map((h) => (
                          <span key={h.code} style={{ fontSize: 12, fontWeight: 600, color: "#0e6b68", background: "#ccfbf1", borderRadius: 999, padding: "2px 10px" }}>{h.name}</span>
                        ))}
                      </div>
                    </div>
                  )}

                  {(() => {
                    // RACI-based time & cost distribution: every R/A/C/I job per task with its
                    // attributed minutes and cost (from the agent's RACI matrix — the same
                    // attributed-time split the CMS records), plus a per-job cost rollup.
                    const matrix = optimizationData?.raci_assignments?.matrix;
                    const rows = Array.isArray(matrix?.rows) ? matrix.rows : [];
                    if (!rows.length) {
                      // Fallback: flat assignment list when the RACI matrix is unavailable.
                      const flat = optimizationData?.task_assignments || [];
                      if (!flat.length) return null;
                      return (
                        <div style={{ marginBottom: 16 }}>
                          <div style={{ fontSize: 12, fontWeight: 700, color: "#6b7280", marginBottom: 6 }}>COMPLETE TASK LIST — final assignments to be saved</div>
                          <div style={{ overflowX: "auto" }}>
                            <table style={{ borderCollapse: "collapse", width: "100%", minWidth: 540, fontSize: 12 }}>
                              <tbody>
                                {flat.map((t, i) => (
                                  <tr key={t.task_id || i}>
                                    <td style={{ padding: "6px 10px", fontWeight: 600 }}>{t.task_name}</td>
                                    <td style={{ padding: "6px 10px" }}>{t.resource_name}</td>
                                    <td style={{ padding: "6px 10px", textAlign: "right" }}>{money(t.cost ?? 0)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      );
                    }
                    const cell = (arr) => (Array.isArray(arr) ? arr : arr ? [arr] : []);
                    const jobRollup = new Map();
                    for (const r of rows) {
                      for (const [role, entries] of [["R", cell(r.R)], ["A", cell(r.A)], ["C", cell(r.C)], ["I", cell(r.I)]]) {
                        for (const e of entries) {
                          if (!e || !e.job_name) continue;
                          // Pool suggestions are not staffing — keep them out of the rollup.
                          if (e.is_pool_suggestion) continue;
                          const key = e.job_id ?? e.job_name;
                          if (!jobRollup.has(key)) jobRollup.set(key, { job_name: e.job_name, roles: new Set(), minutes: 0, cost: 0 });
                          const j = jobRollup.get(key);
                          j.roles.add(role);
                          j.minutes += Number(e.attributed_time_minutes || 0);
                          j.cost += Number(e.cost_usd || 0);
                        }
                      }
                    }
                    return (
                      <div style={{ marginBottom: 16 }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: "#6b7280", marginBottom: 6 }}>
                          RACI TIME &amp; COST DISTRIBUTION — every R/A/C/I job per task with its attributed minutes and cost, as recorded with the version
                        </div>
                        <div className="raci-flat-wrap">
                          <table className="raci-flat-table">
                            <thead>
                              <tr>
                                <th className="raci-flat-th-task">Task Name</th>
                                <th className="raci-flat-th-job">Job Name</th>
                                {[
                                  { role: "R", label: "Responsible" },
                                  { role: "A", label: "Accountable" },
                                  { role: "C", label: "Consulted" },
                                  { role: "I", label: "Informed" },
                                ].map(({ role, label }) => (
                                  <th key={role} className="raci-flat-th-role">
                                    <div className="raci-flat-role-header">
                                      <span className={`raci-legend-badge raci-${role.toLowerCase()}`}>{role}</span>
                                      <span className="raci-flat-role-label">{label}</span>
                                    </div>
                                  </th>
                                ))}
                                <th className="raci-flat-th-role" style={{ textAlign: "right" }} title="The CMS time-allocation percentage of this job's role on the task — the share of the task's (processing + rework) time attributed to it. Attributed minutes = task time × this percentage.">% Time</th>
                                <th className="raci-flat-th-role" style={{ textAlign: "right" }}>Time (min)</th>
                                <th className="raci-flat-th-role" style={{ textAlign: "right" }}>Cost</th>
                              </tr>
                            </thead>
                            <tbody>
                              {rows.map((r, tgIdx) => {
                                const entries = [
                                  ...cell(r.R).map((e) => ({ ...e, _role: "R" })),
                                  ...cell(r.A).map((e) => ({ ...e, _role: "A" })),
                                  ...cell(r.C).map((e) => ({ ...e, _role: "C" })),
                                  ...cell(r.I).map((e) => ({ ...e, _role: "I" })),
                                  // Real staffing only — pool candidates are replacement
                                  // suggestions (Analyze tab), never extra RACI rows.
                                ].filter((e) => e && e.job_name && !e.is_pool_suggestion);
                                const jobRows = entries.length ? entries : [null];
                                const isAlt = tgIdx % 2 !== 0;
                                const subOpen = !!expandedSubprocess[r.task_id];
                                const outRows = jobRows.map((e, jIdx) => (
                                  <tr
                                    key={`${r.task_id}-${e ? `${e.job_id ?? e.job_name}-${e._role}` : "none"}`}
                                    className={`raci-flat-row${isAlt ? " raci-flat-row-alt" : ""}${jIdx === 0 ? " raci-flat-row-first" : ""}`}
                                  >
                                    {jIdx === 0 && (
                                      <td rowSpan={jobRows.length} className="raci-flat-task-cell">
                                        <div className="raci-flat-task-inner">
                                          <span className="raci-flat-step">{r.order ?? tgIdx + 1}</span>
                                          <div className="raci-flat-task-text">
                                            <span className="raci-flat-task-name" style={isEliminated(r.task_id) ? { textDecoration: "line-through", color: "#9ca3af" } : undefined}>{r.task_name}</span>
                                            {isSubprocessRow(r) && subprocessBadge}
                                            {isSubprocessRow(r) && <SubprocessToggle id={r.task_id} open={subOpen} />}
                                            {isEliminated(r.task_id) && eliminatedBadge}
                                            <span style={{ display: "block", fontSize: 11, color: "#6b7280", marginTop: 2 }}>
                                              {isSubprocessRow(r) ? "Composite cost" : "Task total"}: <b>{money(r.task_total_cost_usd ?? 0)}</b>
                                            </span>
                                          </div>
                                        </div>
                                      </td>
                                    )}
                                    {e ? (
                                      <>
                                        {/* This table IS the staffing that will be written, so a
                                            hand-picked reassignment has to appear here — showing
                                            the outgoing job under a heading that says "final
                                            assignments to be saved" would be a lie. */}
                                        <td className="raci-flat-job-name">
                                          {(() => {
                                            const ov = raciOverrideFor(r.task_id, e._role, e.job_id);
                                            if (!ov) return e.job_name;
                                            return (
                                              <>
                                                <span className="po-raci-jobname is-changed">{ov.new_job_name}</span>
                                                <span className="po-raci-was">was {e.job_name}</span>
                                              </>
                                            );
                                          })()}
                                        </td>
                                        {["R", "A", "C", "I"].map((role) => (
                                          <td key={role} className="raci-flat-role-cell">
                                            {e._role === role ? (
                                              <span className={`raci-badge raci-${role.toLowerCase()}`}>{role}</span>
                                            ) : (
                                              <span className="raci-summary-empty">—</span>
                                            )}
                                          </td>
                                        ))}
                                        <td className="raci-flat-role-cell" style={{ textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                                          {e.time_allocation_percentage != null ? `${Number(e.time_allocation_percentage)}%` : "—"}
                                        </td>
                                        <td className="raci-flat-role-cell" style={{ textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                                          {e.attributed_time_minutes != null ? Number(e.attributed_time_minutes).toFixed(0) : "—"}
                                        </td>
                                        <td className="raci-flat-role-cell" style={{ textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                                          {money(e.cost_usd ?? 0)}
                                        </td>
                                      </>
                                    ) : (
                                      <td colSpan={8} className="raci-flat-no-jobs">
                                        {isSubprocessRow(r)
                                          ? "Sub-process — staffed inside its own process. Use “View RACI” to see who does this work; the cost shown is the child's composite labor cost."
                                          : "No jobs assigned to this task"}
                                      </td>
                                    )}
                                  </tr>
                                ));
                                if (isSubprocessRow(r) && subOpen) {
                                  outRows.push(<SubprocessRaciDetail key={`${r.task_id}-detail`} row={r} colSpan={9} />);
                                }
                                return outRows;
                              })}
                              <tr>
                                <td colSpan={8} style={{ padding: "10px 12px", textAlign: "right", fontWeight: 700, borderTop: "2px solid #e5e7eb", background: "#f8fafc" }}>
                                  Total process cost (all RACI roles) — AS-IS
                                </td>
                                <td style={{ padding: "10px 12px", textAlign: "right", fontWeight: 800, borderTop: "2px solid #e5e7eb", background: "#f8fafc", fontVariantNumeric: "tabular-nums" }}>
                                  {money(matrix.total_process_cost_usd ?? 0)}
                                </td>
                              </tr>
                              {matrix.final_staffing_total_cost_usd != null && matrix.final_staffing_total_cost_usd !== matrix.total_process_cost_usd && (
                                <tr>
                                  <td colSpan={8} title="The sum of the rows above — the workload-balanced + cost-optimized staffing plan. Differs from the AS-IS total exactly by the accepted reassignments' savings." style={{ padding: "10px 12px", textAlign: "right", fontWeight: 700, background: "#eff6ff", color: "#1d4ed8" }}>
                                    Optimized staffing plan (the rows above, after reassignments)
                                    {/* The job names in the rows above DO show the analyst's
                                        reassignments, so an unrefreshed total here would
                                        contradict the very rows it claims to sum. */}
                                    {staffingStale && (
                                      <span
                                        title="This total comes from the optimization's RACI matrix, which the what-if re-score did not return. It does not yet include your reassignment(s); the saved version's cost per case does."
                                        style={{ marginLeft: 8, fontSize: 10, fontWeight: 800, color: "#b45309", background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 999, padding: "2px 8px", whiteSpace: "nowrap" }}
                                      >
                                        ⚠ excludes your reassignment(s)
                                      </span>
                                    )}
                                  </td>
                                  <td style={{ padding: "10px 12px", textAlign: "right", fontWeight: 800, background: "#eff6ff", color: "#1d4ed8", fontVariantNumeric: "tabular-nums" }}>
                                    {money(matrix.final_staffing_total_cost_usd)}
                                  </td>
                                </tr>
                              )}
                              {matrix.to_be_total_process_cost_usd != null && matrix.to_be_total_process_cost_usd !== (matrix.final_staffing_total_cost_usd ?? matrix.total_process_cost_usd) && (
                                <tr>
                                  <td colSpan={8} style={{ padding: "10px 12px", textAlign: "right", fontWeight: 700, background: "#f0fdf9", color: "#0f766e" }}>
                                    TO-BE total — excludes the {(matrix.eliminated_task_ids || []).length || eliminatedTasks.length} eliminated step(s)
                                  </td>
                                  <td style={{ padding: "10px 12px", textAlign: "right", fontWeight: 800, background: "#f0fdf9", color: "#0f766e", fontVariantNumeric: "tabular-nums" }}>
                                    {money(matrix.to_be_total_process_cost_usd)}
                                  </td>
                                </tr>
                              )}
                              {matrix.subprocess_composite_cost_usd > 0 && (
                                <>
                                  <tr>
                                    <td colSpan={8} title="Sum of the embedded sub-processes' own labor cost (from each child's flow analysis)." style={{ padding: "10px 12px", textAlign: "right", fontWeight: 700, background: "#faf5ff", color: "#5b21b6" }}>
                                      + {matrix.subprocess_count} sub-process(es) composite cost (staffed in child processes)
                                    </td>
                                    <td style={{ padding: "10px 12px", textAlign: "right", fontWeight: 800, background: "#faf5ff", color: "#5b21b6", fontVariantNumeric: "tabular-nums" }}>
                                      {money(matrix.subprocess_composite_cost_usd)}
                                    </td>
                                  </tr>
                                  <tr>
                                    <td colSpan={8} title="What staffing the whole process — every R/A/C/I role on every step, including the work inside sub-processes — costs per case. Matches the Overview's headline staffing figure." style={{ padding: "10px 12px", textAlign: "right", fontWeight: 800, background: "#f5f3ff", color: "#4c1d95", borderTop: "2px solid #ddd6fe" }}>
                                      TOTAL — all steps including sub-processes
                                    </td>
                                    <td style={{ padding: "10px 12px", textAlign: "right", fontWeight: 800, background: "#f5f3ff", color: "#4c1d95", borderTop: "2px solid #ddd6fe", fontVariantNumeric: "tabular-nums" }}>
                                      {money((matrix.total_process_cost_usd ?? 0) + matrix.subprocess_composite_cost_usd)}
                                    </td>
                                  </tr>
                                </>
                              )}
                            </tbody>
                          </table>
                        </div>
                        {jobRollup.size > 0 && (
                          <details style={{ marginTop: 10 }}>
                            <summary style={{ cursor: "pointer", fontSize: 12.5, fontWeight: 700, color: "#0e6b68" }}>
                              Per-job cost rollup ({jobRollup.size} jobs) — roles held, attributed time, total cost share
                            </summary>
                            <div className="raci-flat-wrap" style={{ marginTop: 8 }}>
                              <table className="raci-flat-table">
                                <thead>
                                  <tr>
                                    <th className="raci-flat-th-task">Job</th>
                                    {[
                                      { role: "R", label: "Responsible" },
                                      { role: "A", label: "Accountable" },
                                      { role: "C", label: "Consulted" },
                                      { role: "I", label: "Informed" },
                                    ].map(({ role, label }) => (
                                      <th key={role} className="raci-flat-th-role">
                                        <div className="raci-flat-role-header">
                                          <span className={`raci-legend-badge raci-${role.toLowerCase()}`}>{role}</span>
                                          <span className="raci-flat-role-label">{label}</span>
                                        </div>
                                      </th>
                                    ))}
                                    <th className="raci-flat-th-role" style={{ textAlign: "right" }}>Attributed time</th>
                                    <th className="raci-flat-th-role" style={{ textAlign: "right" }}>Cost share</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {[...jobRollup.values()].sort((a, b) => b.cost - a.cost).map((j, idx) => (
                                    <tr key={j.job_name} className={`raci-flat-row raci-flat-row-first${idx % 2 ? " raci-flat-row-alt" : ""}`}>
                                      <td className="raci-flat-task-cell">
                                        <div className="raci-flat-task-inner">
                                          <div className="raci-flat-task-text">
                                            <span className="raci-flat-task-name">{j.job_name}</span>
                                          </div>
                                        </div>
                                      </td>
                                      {["R", "A", "C", "I"].map((role) => (
                                        <td key={role} className="raci-flat-role-cell">
                                          {j.roles.has(role) ? (
                                            <span className={`raci-badge raci-${role.toLowerCase()}`}>{role}</span>
                                          ) : (
                                            <span className="raci-summary-empty">—</span>
                                          )}
                                        </td>
                                      ))}
                                      <td className="raci-flat-role-cell" style={{ textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{j.minutes.toFixed(0)} min</td>
                                      <td className="raci-flat-role-cell" style={{ textAlign: "right", fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>{money(j.cost)}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </details>
                        )}
                      </div>
                    );
                  })()}

                  {(() => {
                    const rows = optimizationData?.redesign_flow?.to_be?.paths || [];
                    if (!rows.length) return null;
                    return (
                      <details style={{ marginBottom: 16 }}>
                        <summary style={{ cursor: "pointer", fontSize: 12.5, fontWeight: 700, color: "#0e6b68" }}>
                          TO-BE path view ({rows.length} routes) — the execution paths this version will have
                        </summary>
                        <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 6, fontSize: 12 }}>
                          {(() => {
                            const maxDur = Math.max(...rows.map((p) => Number(p.duration_minutes) || 0));
                            const criticalIdx = maxDur > 0 ? rows.findIndex((p) => (Number(p.duration_minutes) || 0) === maxDur) : -1;
                            const dimCount = sharedPrefixLength(rows);
                            return rows.map((p, i) => (
                              <div key={i} style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "nowrap", ...(i === criticalIdx ? { background: "#fef7e6", boxShadow: "inset 3px 0 0 #b45309", borderRadius: 6, padding: "2px 4px" } : {}) }}>
                                <span style={{ fontWeight: 700, minWidth: 52, textAlign: "right", flexShrink: 0 }}>{(p.probability * 100).toFixed(1)}%</span>
                                <span style={{ color: "#6b7280", minWidth: 70, textAlign: "right", flexShrink: 0 }}>{p.duration_minutes} min</span>
                                {i === criticalIdx && (
                                  <span style={{ fontSize: 10, fontWeight: 800, color: "#b45309", background: "#fde68a55", border: "1px solid #f4cf7f", borderRadius: 6, padding: "1px 6px", whiteSpace: "nowrap", flexShrink: 0 }}>⏱ critical path</span>
                                )}
                                <div style={{ flex: 1, minWidth: 0 }}><PathFlowStrip route={p.route} dimCount={dimCount} /></div>
                              </div>
                            ));
                          })()}
                        </div>
                      </details>
                    );
                  })()}

                  {readiness.issues.length > 0 && (
                    <div style={{ fontSize: 12.5, color: "#b45309", marginBottom: 12 }}>
                      ⚠ Analysis is incomplete — you can still save, but quality &amp; cycle-time efficiency
                      will be recorded as provisional. Complete the Value-Added tab for final values.
                    </div>
                  )}
                  {(() => {
                    // What of the staffing plan will actually PERSIST with this save.
                    const reps = Array.isArray(co?.replacements) ? co.replacements : [];
                    // Counted from the merged override set, so a hand-picked
                    // reassignment is reported even when the agent proposed none.
                    const acceptedCount = pendingOverrides.length;
                    const manualCount = Object.keys(manualOverrides).length;
                    if (!reps.length && !manualCount) return null;
                    return (
                      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", fontSize: 12.5, color: "#334155", background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 8, padding: "8px 12px", marginBottom: 12 }}>
                        <span aria-hidden="true" style={{ fontWeight: 800, color: "#1d4ed8" }}>⇄</span>
                        <span>
                          <b>{acceptedCount}</b> job reassignment(s)
                          {reps.length > 0 ? ` (of ${reps.length} suggested` : ""}
                          {reps.length > 0 && manualCount > 0 ? `, ${manualCount} set by you` : ""}
                          {reps.length > 0 ? ")" : manualCount > 0 ? " — all set by you" : ""}{" "}
                          are accepted and will be{" "}
                          <b>persisted into the new version&apos;s staffing</b> (the affected tasks are re-versioned;
                          the original version keeps its people).
                        </span>
                        <button
                          type="button"
                          className="secondary-btn sm"
                          onClick={() => setActiveTab("analyze")}
                          aria-label="Change which reassignments are accepted in the Analyze tab"
                        >
                          Change selection →
                        </button>
                      </div>
                    );
                  })()}
                  {/* The payload has stopped matching the ticks above it — a refused
                      adopt call, or the engine re-running under the selection. Both
                      mean the version on screen is not the one described, so the
                      button says why instead of writing the wrong thing. */}
                  {engineSelection.saveBlocked && (
                    <div style={{ fontSize: 12.5, color: "#991b1b", background: "#fef2f2", border: "1px solid #fca5a5", borderRadius: 8, padding: "8px 12px", marginBottom: 10 }}>
                      {engineSelection.saveBlockedReason}
                    </div>
                  )}
                  <button
                    className="primary-btn"
                    onClick={requestSave}
                    disabled={!selectedId || saving || engineSelection.loading || engineSelection.saveBlocked}
                    title={engineSelection.saveBlockedReason || undefined}
                  >
                    {saving ? "Saving…" : engineSelection.loading ? "Rebuilding…" : "Save optimized version"}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        <RaciJobEditModal
          open={!!editTarget}
          taskName={editTarget?.taskName}
          role={editTarget?.role}
          currentJob={editTarget?.job}
          candidates={
            editTarget
              ? buildCandidateList({
                  optimizationData,
                  taskId: editTarget.taskId,
                  role: editTarget.role,
                  currentJob: editTarget.job,
                  allJobs,
                }).candidates
              : []
          }
          busy={applyingSelection}
          error={editError}
          money={money}
          onConfirm={applyManualOverride}
          onCancel={() => {
            setEditTarget(null);
            setEditError("");
          }}
        />

        {/* The confirm step. It only appears when engines composed the payload, and
            it is the single place that reconciles the redesign the analyst reviewed
            with the subset a process version can actually store. */}
        <FormModal
          open={confirmOpen}
          title="Confirm what will be saved"
          onCancel={() => setConfirmOpen(false)}
          footer={
            <>
              <button type="button" className="secondary-btn" onClick={() => setConfirmOpen(false)} disabled={saving}>
                Cancel
              </button>
              <button type="button" className="primary-btn" onClick={confirmSave} disabled={saving} style={{ marginLeft: 8 }}>
                {saving ? "Saving…" : "Save this version"}
              </button>
            </>
          }
        >
          <ConfirmSaveBody summary={engineSelection.summary} />
        </FormModal>

        <FormModal
          open={showMinTasksModal}
          title="Not enough tasks"
          onCancel={() => setShowMinTasksModal(false)}
          footer={
            <button type="button" className="primary-btn" onClick={() => setShowMinTasksModal(false)}>
              OK
            </button>
          }
        >
          <div style={{ fontSize: 14, color: "#334155" }}>
            At least 3 tasks are required in a process to run optimization. Please add more tasks and try again.
          </div>
        </FormModal>

        <FormModal
          open={saveModal.open}
          title={saveModal.title || "Process Saved"}
          onCancel={() => setSaveModal({ open: false, title: "", message: "" })}
          footer={
            <>
              {savedVersion?.process_id && (
                <button
                  type="button"
                  className="primary-btn"
                  onClick={() => {
                    setSaveModal({ open: false, title: "", message: "" });
                    optimize(savedVersion.process_id, GOAL_PRESETS[goalKey].weights, { includeSubprocessRedesigns: agentIncludeSubprocess });
                    navigate(`/process-optimization/results/${encodeURIComponent(String(savedVersion.process_id))}`);
                  }}
                >
                  Open new version (v{savedVersion.process_version})
                </button>
              )}
              <button type="button" className="secondary-btn" onClick={() => setSaveModal({ open: false, title: "", message: "" })}>
                Stay here
              </button>
            </>
          }
        >
          <div style={{ fontSize: 14, color: "#334155" }}>
            {savedVersion?.process_version != null && (
              <span
                style={{ display: "inline-block", marginRight: 8, fontSize: 12, fontWeight: 800, color: "#0f766e", background: "#ccfbf1", border: "1px solid #99f6e4", borderRadius: 999, padding: "2px 10px" }}
              >
                v{savedVersion.process_version}
              </span>
            )}
            {saveModal.message || "Optimized process version saved."}
          </div>
        </FormModal>

        <FormModal
          open={errorModal.open}
          title={errorModal.title || "Error"}
          onCancel={handleErrorClose}
          footer={
            <button type="button" className="primary-btn" onClick={handleErrorClose}>
              OK
            </button>
          }
        >
          <div style={{ fontSize: 14, color: "#334155", whiteSpace: "pre-line" }}>
            {errorModal.message || "An error occurred."}
          </div>
        </FormModal>
      </div>
    </div>
  );
};

export default ProcessOptimizationPage;
